/**
 * KARATE DRILLS DATA STRUCTURE - CONSOLIDATED USED GEMINI 2.5 PRO
 *
 * This file contains consolidated karate drills organized into skill levels:
 * 1. Beginner Stances (30 minutes) - Basic karate stances and footwork
 * 2. Intermediate Stances (45 minutes) - Advanced stances and movement
 * 3. Advanced Stances (45 minutes) - Complex stance transitions
 * 4. Beginner Strikes (30 minutes) - Basic punching and striking techniques
 * 5. Intermediate Strikes (45 minutes) - Advanced striking combinations
 * 6. Advanced Strikes (45 minutes) - Complex striking sequences
 * 7. Beginner Blocks (30 minutes) - Basic defensive blocking techniques
 * 8. Intermediate Blocks (45 minutes) - Advanced blocking combinations
 * 9. Advanced Blocks (45 minutes) - Complex defensive sequences
 * 10. Complete Individual Karate (60 minutes) - Comprehensive karate workout
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 8001 (karate drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'karate'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Stances', 'Strikes', 'Blocks')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'None' or 'Karate mat')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * KARATE DRILL REQUIREMENTS:
 * - All drills doable at home/indoors with minimal space
 * - Focus on individual skills (no partner drills for safety)
 * - Equipment limited to optional karate mat
 * - Appropriate for 6-10 year olds
 * - Focus on fundamental karate techniques and coordination
 * - Progressive skill development within each drill
 * - Emphasis on proper form and technique
 *
 * CREATED: [Date] - Karate drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const karateDrills = [
  // BEGINNER STANCES (30 minutes)
  {
    id: 2031,
    name: 'Beginner Stances',
    categories: ['Stances', 'beginner'],
    sports: ['karate'],
    difficulty: 'beginner',
    skill: 'Stances',
    points: 30,
    instructions:
      'Hello Karate Legend! In this session, we will start to work on your karate stances and footwork.',
    timedInstructions: [
      {
        time: 0,
        text: "You will be light on your feet and ready to move like a Karate Master! Get ready to build a super strong karate foundation! Let's begin with our first series of stances!",
      },
      {
        time: 7,
        text: 'First up is the Natural Stance. Stand tall and relaxed with your heels together and toes pointing out, making a little V shape with your feet.',
      },
      {
        time: 15,
        text: 'Keep your back superhero-straight and let your arms hang by your sides. Hold it there. Feel strong and balanced.',
      },
      {
        time: 22,
        text: 'Great! Now, the Ready Stance. From where you are, slide your left foot out so your feet are shoulder-width apart.',
      },
      {
        time: 29,
        text: "As you step out, bring your hands up making two fists, then pull them right down to your belt. You're ready for action!",
      },
      {
        time: 36,
        text: "Let's practice that. Go back to Natural Stance... And... step out to Ready Stance! Fists up and down!",
      },
      {
        time: 43,
        text: 'Awesome! In your Ready Stance, keep your knees a little bent and bouncy. Your feet should point straight ahead like arrows.',
      },
      {
        time: 50,
        text: "Time for reps! We'll do five transitions. Back to Natural... and Ready Stance! One!",
      },
      {
        time: 57,
        text: 'Natural Stance... and Ready Stance! Two! Getting smoother!',
      },
      {
        time: 64,
        text: 'Natural Stance... and Ready Stance! Three! Keep that back straight!',
      },
      {
        time: 71,
        text: 'Natural Stance... and Ready Stance! Four! Great focus!',
      },
      {
        time: 78,
        text: 'Natural Stance... and Ready Stance! Five! You got it!',
      },
      {
        time: 84,
        text: 'Nice work! Now for a power stance: the Front Stance. From Ready Stance, take a big step forward with your left foot.',
      },
      {
        time: 91,
        text: "Now, bend your front knee so you can't see your toes. Your back leg should be super straight and strong. Feel the power!",
      },
      {
        time: 99,
        text: 'Hold this strong Front Stance for three... two... one... Now step back to Ready Stance.',
      },
      {
        time: 106,
        text: "Let's do the right foot. Step forward with your right! Bend that front knee, and keep the back leg straight. You look powerful!",
      },
      {
        time: 114,
        text: 'Hold it for three... two... one... And back to Ready Stance.',
      },
      {
        time: 120,
        text: "Let's practice switching sides. Ready... and Left Front Stance!",
      },
      {
        time: 126,
        text: 'Back to Ready. And... Right Front Stance!',
      },
      {
        time: 132,
        text: "Back to Ready. Remember, you're a strong, unmovable mountain! Left Front Stance!",
      },
      {
        time: 139,
        text: 'Back to Ready. Right Front Stance! Awesome!',
      },
      {
        time: 145,
        text: "Back to Ready. Now, let's hold each side for a ten-second countdown. Step into Left Front Stance... now!",
      },
      {
        time: 153,
        text: 'Hold it strong! Ten, nine, eight, seven, six...',
      },
      {
        time: 159,
        text: 'five, four, three, two, one! Back to Ready Stance!',
      },
      {
        time: 165,
        text: 'Awesome! Now, step into Right Front Stance and hold! Ten, nine, eight, seven, six...',
      },
      {
        time: 173,
        text: 'five, four, three, two, one! Back to Ready Stance!',
      },
      {
        time: 179,
        text: "Fantastic work! Let's keep moving! Time for our next drill: 'Stance Transitions'!",
      },
      {
        time: 186,
        text: "We're going to practice moving smoothly. Start in a strong Ready Stance. Feet shoulder-width, fists at your side.",
      },
      {
        time: 194,
        text: "Let's go from Ready Stance to Front Stance. On my call, step forward with your left foot. Ready... GO!",
      },
      {
        time: 200,
        text: 'Good! Back to Ready. Again with the left foot... GO!',
      },
      {
        time: 206,
        text: 'Back to Ready. Now with the right foot... GO!',
      },
      {
        time: 212,
        text: "Back to Ready. Let's get a rhythm. Left Front Stance... and back. Right Front Stance... and back. Keep it going!",
      },
      {
        time: 222,
        text: 'Try to make your steps smooth, not stompy. Glide like a ninja. Five more on each side. Left... and back. Right... and back.',
      },
      {
        time: 234,
        text: "That's two! Keep your back straight, don't lean forward. Three! Nice. Four! Good. Five! Excellent.",
      },
      {
        time: 245,
        text: 'Shake it out! Now for a new, tricky stance: the Back Stance. This is our defensive stance.',
      },
      {
        time: 252,
        text: 'From Ready Stance, step back with your right foot and turn your body to the side. Most of your weight should be on your back leg.',
      },
      {
        time: 260,
        text: "Your feet should make an 'L' shape. Your back foot points to the side, and your front foot points forward. Hold it and see.",
      },
      {
        time: 268,
        text: "Good! Back to Ready Stance. Let's try the other side. Step back with your left foot into a Left Back Stance.",
      },
      {
        time: 276,
        text: 'Remember, almost all your weight is on that back leg. Feel strong and stable, ready to block.',
      },
      {
        time: 284,
        text: "Let's practice that. Ready... and Right Back Stance! Hold it.",
      },
      {
        time: 290,
        text: 'Back to Ready. And... Left Back Stance! Hold it.',
      },
      {
        time: 296,
        text: "This is a tricky one, but you're getting it. Let's do five more reps. Ready... and Right Back Stance! Back to Ready.",
      },
      {
        time: 305,
        text: 'Ready... and Left Back Stance! Back to Ready. Keep that back leg strong!',
      },
      {
        time: 313,
        text: 'Three more! Right! And back. Left! And back. Last one, make it perfect... Right! And back.',
      },
      {
        time: 324,
        text: "Now, let's flow between all three stances. Start in Ready Stance. Go to Left Front Stance... hold.",
      },
      {
        time: 331,
        text: 'From Front Stance, without going back to ready, shift your weight back into a Left Back Stance. Be smooth.',
      },
      {
        time: 339,
        text: "Good! Now back to Ready Stance. Let's try the other side. Right Front Stance...",
      },
      {
        time: 346,
        text: 'And shift your weight back into a Right Back Stance. Awesome!',
      },
      {
        time: 353,
        text: "Let's do that flow four more times. Left side flow: Ready, Front, Back, Ready. Go!",
      },
      {
        time: 361,
        text: 'Right side flow: Ready, Front, Back, Ready. Go!',
      },
      {
        time: 369,
        text: 'Again, left side! Keep it smooth and controlled.',
      },
      {
        time: 376,
        text: "Again, right side! You're a true stance master.",
      },
      {
        time: 383,
        text: "Excellent transitions! Now let's test our balance and become steady as a mountain in our 'Balance Work' drill!",
      },
      {
        time: 391,
        text: "We're going to hold each stance to build super strong karate muscles! Start in Ready Stance, and hold for ten seconds. Be a statue!",
      },
      {
        time: 402,
        text: "five... four... three... two... one... Awesome! You didn't even wobble.",
      },
      {
        time: 408,
        text: "Now, into our powerful Left Front Stance. Hold it! We're holding for fifteen seconds. Bend that front knee!",
      },
      {
        time: 418,
        text: 'Keep that back leg straight! Tummy tight! Five, four, three, two, one... Back to Ready Stance!',
      },
      {
        time: 428,
        text: 'Great job! Other side. Step into a strong Right Front Stance. Hold it! Fifteen seconds on the clock!',
      },
      {
        time: 438,
        text: 'Are you looking forward? Back straight? Five, four, three, two, one... Back to Ready Stance!',
      },
      {
        time: 448,
        text: "You're building a powerful foundation. Now for our tricky Back Stance. Step into Right Back Stance. Most of your weight on that back leg.",
      },
      {
        time: 458,
        text: "Let's hold this for fifteen seconds. Go! Keep your balance, find a spot to focus on.",
      },
      {
        time: 468,
        text: 'Five... four... three... two... one... Back to Ready Stance! Awesome balance.',
      },
      {
        time: 475,
        text: 'Now the other side. Left Back Stance. Hold it strong! Fifteen seconds! Keep that back leg strong like a tree trunk.',
      },
      {
        time: 488,
        text: "Five... four... three... two... one... Back to Ready Stance! You're a balance master.",
      },
      {
        time: 495,
        text: "Now for a 'Stance Flow'. We'll move through the stances, holding each for just five seconds. Follow my lead. Start in Ready Stance.",
      },
      {
        time: 505,
        text: 'Go! Left Front Stance! Hold for five... four... three... two... one...',
      },
      {
        time: 512,
        text: 'Now, shift to Left Back Stance! Hold for five... four... three... two... one...',
      },
      {
        time: 519,
        text: "And back to Ready Stance! Great! Let's do the other side.",
      },
      {
        time: 525,
        text: 'Right Front Stance! Hold for five... four... three... two... one...',
      },
      {
        time: 532,
        text: 'Shift to Right Back Stance! Hold for five... four... three... two... one...',
      },
      {
        time: 539,
        text: "And back to Ready Stance! You're flowing like a karate river!",
      },
      {
        time: 545,
        text: "Let's do that flow one more time, a little quicker. Ready... Left Front... Left Back... Ready!",
      },
      {
        time: 553,
        text: 'Now the other side! Ready... Right Front... Right Back... Ready! Excellent!',
      },
      {
        time: 561,
        text: "Ultimate balance challenge. We're holding Left Front Stance for 20 seconds. Can you do it? Go!",
      },
      {
        time: 571,
        text: "Ten seconds down! Stay strong! Don't wobble. You're a mountain. Keep breathing.",
      },
      {
        time: 580,
        text: 'Five, four, three, two, one... Back to Ready! Awesome!',
      },
      {
        time: 586,
        text: 'Now Right Front Stance for 20 seconds! Go! This is the last one for this drill, make it your best hold yet!',
      },
      {
        time: 596,
        text: 'Halfway there! Keep that back leg straight! Core tight!',
      },
      {
        time: 604,
        text: 'Five, four, three, two, one... and TIME! Excellent balance.',
      },
      {
        time: 610,
        text: "That was amazing focus! Now, let's move like a wise old turtle in our next drill: 'Slow Movements'!",
      },
      {
        time: 618,
        text: "We're going to move between our stances very, very slowly. This perfects your form. Start in Ready Stance.",
      },
      {
        time: 626,
        text: 'Okay, as slow as you can, begin to step forward with your left foot. Feel your weight shift forward... slooowly...',
      },
      {
        time: 634,
        text: 'Slowly bend your front knee... straighten your back leg... until you are in a perfect Left Front Stance. Hold it.',
      },
      {
        time: 642,
        text: 'Awesome! Now, just as slowly, bring that left foot back to your Ready Stance. Control every inch.',
      },
      {
        time: 650,
        text: "Let's do the other side. Slowly step forward with your right foot... feel your balance... and sink into that Right Front Stance.",
      },
      {
        time: 660,
        text: "Perfect form. Hold it. And slowly back to Ready Stance. You're moving with great control!",
      },
      {
        time: 668,
        text: "Now let's try moving to Back Stance slowly. From Ready, slowly step back with your right foot, turning your body.",
      },
      {
        time: 677,
        text: 'Shift your weight onto that back leg... feel the power in your stance. Hold it.',
      },
      {
        time: 684,
        text: 'Good. Now slowly bring that foot back to Ready Stance. Every move is strong and deliberate.',
      },
      {
        time: 692,
        text: "Now the other side. Slowly step back with your left foot into a Left Back Stance. Remember the 'L' shape. Hold it strong.",
      },
      {
        time: 702,
        text: 'And slowly back to Ready Stance. Excellent control.',
      },
      {
        time: 708,
        text: "Now for a slow flow. We'll go from Ready, to Front, to Back, all on one side. Ready Stance... slowly step into Left Front Stance.",
      },
      {
        time: 719,
        text: 'Now, slowly shift your weight back, turning your body into a Left Back Stance. Feel that smooth change.',
      },
      {
        time: 728,
        text: "Good. And slowly back to Ready Stance. You're a master in training.",
      },
      {
        time: 735,
        text: "Let's do the other side. Slowly into a Right Front Stance... hold.",
      },
      {
        time: 743,
        text: 'Now, slowly shift into a Right Back Stance. Perfect.',
      },
      {
        time: 750,
        text: 'And slowly back to Ready Stance. Awesome!',
      },
      {
        time: 756,
        text: "Let's do that slow flow again. The slower you go, the stronger you get. Left side... Ready... to Front... to Back... to Ready.",
      },
      {
        time: 769,
        text: 'Now the right side... Ready... to Front... to Back... to Ready. Keep it super smooth.',
      },
      {
        time: 782,
        text: 'This is building amazing muscle memory. Your body is learning how to move perfectly. One more time on each side. Left side flow, go.',
      },
      {
        time: 794,
        text: 'And the right side flow. Excellent control.',
      },
      {
        time: 801,
        text: "The ultimate slow-mo challenge. We'll do the flow with eyes closed! Start in Ready Stance, close your eyes. Now, slowly move into Left Front Stance.",
      },
      {
        time: 813,
        text: 'Feel your balance. Slowly shift to Left Back Stance. Trust your body.',
      },
      {
        time: 820,
        text: 'And slowly back to Ready. Open your eyes! You did it!',
      },
      {
        time: 826,
        text: "Other side, eyes closed! Slowly into Right Front Stance. You're a rock!",
      },
      {
        time: 834,
        text: 'Slowly shift to Right Back Stance. Stay steady.',
      },
      {
        time: 841,
        text: 'And slowly back to Ready. Open your eyes! Amazing balance!',
      },
      {
        time: 848,
        text: "Incredible control! We've moved like a turtle, now let's move like a cheetah in 'Faster Movements'!",
      },
      {
        time: 856,
        text: "Let's move between stances much faster, but keep your perfect form! Start in Ready Stance.",
      },
      {
        time: 863,
        text: 'On my call, snap into a Left Front Stance as fast as you can. Ready... GO!',
      },
      {
        time: 869,
        text: "Quick and strong! Now, snap back to Ready Stance. Let's do it again. Ready... Left Front Stance!",
      },
      {
        time: 877,
        text: 'Back to Ready. Now with the right foot. Ready... Right Front Stance!',
      },
      {
        time: 884,
        text: "Back to Ready. Let's get a rhythm. Left... back. Right... back. Left... back. Right... back. Quick and sharp!",
      },
      {
        time: 895,
        text: "Your transitions are fast! Keep your balance, even when moving quickly. Let's do five more super fast on each side. Left! Right! That's one. Two! Three! Four! Five!",
      },
      {
        time: 909,
        text: 'Awesome! Now for Back Stance. From Ready, quickly step back into Right Back Stance! Go!',
      },
      {
        time: 916,
        text: 'Good! Snap back to Ready. Other side. Quickly into Left Back Stance! Go!',
      },
      {
        time: 923,
        text: "Back to Ready! Let's get a rhythm. Right Back! And Ready. Left Back! And Ready. Keep it snappy!",
      },
      {
        time: 932,
        text: "Let's do five more fast reps. Ready... Right! And back. Left! And back. Three more! Four! Last one, super fast!",
      },
      {
        time: 945,
        text: "Now let's flow quickly between all three. Start in Ready. On my call, go to Left Front Stance... now quickly shift to Left Back Stance!",
      },
      {
        time: 954,
        text: 'Good! Back to Ready. Now the other side. Right Front Stance... and quick shift to Right Back Stance!',
      },
      {
        time: 962,
        text: "Awesome! Let's do that fast flow again. Left side, GO! Ready, Front, Back, Ready!",
      },
      {
        time: 970,
        text: 'Right side, GO! Ready, Front, Back, Ready!',
      },
      {
        time: 977,
        text: "Final challenge! I'm going to call out stances randomly. Snap into them as fast as you can from ready stance. Ready... Left Front!",
      },
      {
        time: 985,
        text: 'Right Back! ... Right Front! ... Left Back! ... Ready Stance!',
      },
      {
        time: 994,
        text: 'Awesome reactions! Five, four, three, two, one... and TIME! Excellent speed.',
      },
      {
        time: 1001,
        text: "Amazing, fast movement! Now, let's put it all together and work on our awesome 'Karate Coordination'!",
      },
      {
        time: 1009,
        text: 'This is about flowing like a ninja from one move to the next. Start in Ready Stance. Fists up, eyes forward.',
      },
      {
        time: 1016,
        text: 'Step into a Left Front Stance, and at the same time, do a strong punch with your right hand. Ready? Go!',
      },
      {
        time: 1024,
        text: 'Awesome! Your feet and hand moved together. Back to Ready. Now, Right Front Stance with a left punch. Go!',
      },
      {
        time: 1032,
        text: "Excellent! That's coordination! Let's practice. Left Front, right punch... and back. Right Front, left punch... and back.",
      },
      {
        time: 1043,
        text: 'Your coordination looks great. Now for a new combo. From Ready, step back into a Left Back Stance and bring your hands up to block.',
      },
      {
        time: 1052,
        text: 'Good! Back to Ready. Now the other side. Right Back Stance, and bring those hands up to block. Go!',
      },
      {
        time: 1060,
        text: "You're moving like a real karate master. Let's practice that. Left Back Stance and block... and back. Right Back Stance and block... and back.",
      },
      {
        time: 1072,
        text: 'Time to link everything together. Start in Ready Stance. Step to Left Front Stance with a punch... GO!',
      },
      {
        time: 1079,
        text: 'Now, from that Front Stance, shift back into a Left Back Stance and do a block. Good!',
      },
      {
        time: 1086,
        text: "And now back to Ready Stance. That was a full karate combo! Let's do the other side.",
      },
      {
        time: 1093,
        text: 'Ready... Right Front Stance with a punch! ... Shift to Right Back Stance with a block! ... And back to Ready!',
      },
      {
        time: 1103,
        text: "Awesome! You're not just doing stances; you're doing karate! Let's make the movements faster, but still sharp.",
      },
      {
        time: 1111,
        text: 'Left side combo, but quicker! Ready... Front Punch... Back Block... Ready! Awesome speed.',
      },
      {
        time: 1120,
        text: "Now the right side, just as fast! Ready... Front Punch... Back Block... Ready! You're a blur!",
      },
      {
        time: 1129,
        text: "Let's do four more sets. Left side combo... GO! Right side combo... GO!",
      },
      {
        time: 1139,
        text: "Keep your stances strong even when you're fast! Left side! Right side!",
      },
      {
        time: 1148,
        text: 'Last minute of this drill! Your choice! You can do the slow, controlled combo, or the fast, sharp combo. Show me your best stuff for the next 30 seconds!',
      },
      {
        time: 1160,
        text: 'Ten seconds left! Faster, stronger, sharper! Go, go, go!',
      },
      {
        time: 1168,
        text: 'Five, four, three, two, one... and TIME! Excellent coordination.',
      },
      {
        time: 1175,
        text: "Incredible karate! For our final drill, let's put everything to the test in the 'Mastery Challenge'!",
      },
      {
        time: 1183,
        text: "This is your time to shine. I'll call out moves, you do them as quickly and sharply as you can. Start in a strong Ready Stance.",
      },
      {
        time: 1191,
        text: 'Ready... Left Front Stance!',
      },
      {
        time: 1195,
        text: 'Good! Now... Right Back Stance!',
      },
      {
        time: 1200,
        text: 'Excellent! ... Left Back Stance!',
      },
      {
        time: 1204,
        text: "And... Right Front Stance! You're moving like a master.",
      },
      {
        time: 1210,
        text: "Back to Ready Stance. Now let's add the punches and blocks. Ready... Left Front Stance with a right punch!",
      },
      {
        time: 1218,
        text: 'From there, shift to Left Back Stance with a block!',
      },
      {
        time: 1224,
        text: 'Back to Ready. Other side. Right Front Stance with a left punch!',
      },
      {
        time: 1231,
        text: "And shift to Right Back Stance with a block! You're a warrior.",
      },
      {
        time: 1238,
        text: "Let's do that whole combo again, but faster. Left side combo, GO!",
      },
      {
        time: 1245,
        text: 'And the right side combo, GO! Awesome speed.',
      },
      {
        time: 1252,
        text: 'Time to test your balance. Hold a Left Front Stance for ten seconds. Go! Be a statue.',
      },
      {
        time: 1263,
        text: 'Now a Right Back Stance for ten seconds. Stay strong.',
      },
      {
        time: 1273,
        text: "Back to fast transitions. I'm going to call them out randomly. Be ready! Start in Ready Stance.",
      },
      {
        time: 1281,
        text: 'Right Front Stance! ... Left Back Stance! ... Left Front Stance! ... Right Back Stance! ... Ready Stance! Awesome reactions!',
      },
      {
        time: 1294,
        text: "Again, even faster! Ready... Right Back! Left Front! Right Front! Left Back! Ready! You're a blur!",
      },
      {
        time: 1306,
        text: "Final minute! This is your time to shine. Freestyle! Move between any of the stances you've learned. Show me your best moves.",
      },
      {
        time: 1315,
        text: 'Show me a strong Front Stance. Now a sneaky Back Stance. Now a powerful punch!',
      },
      {
        time: 1323,
        text: 'Thirty seconds left! Empty the tank. Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 1333,
        text: "Final fifteen seconds. Give me everything you've got. One more perfect combo!",
      },
      {
        time: 1341,
        text: 'Five, four, three, two, one... and TIME! You are a stance master!',
      },
      {
        time: 1348,
        text: 'That was an incredible workout! You learned and mastered the basic stances. You should be so proud of your hard work.',
      },
      {
        time: 1356,
        text: "Let's do a final cool down. Stand in your Natural Stance. Take a big, deep breath in... and out.",
      },
      {
        time: 1364,
        text: 'Another deep breath in... and let it all out. You trained like a true karate champion today. Amazing job. Skill complete!',
      },
    ],
    timeTotal: 1368,
    equipment: 'None',
  },

  // INTERMEDIATE STANCES (45 minutes)
  {
    id: 2032,
    name: 'Intermediate Stances',
    categories: ['Stances', 'intermediate'],
    sports: ['karate'],
    difficulty: 'intermediate',
    skill: 'Stances',
    points: 45,
    instructions:
      "Ready to become a stance master? We're going to learn some super cool new stances, move like a ninja, and challenge our balance. Get ready to power up your karate skills!",
    timedInstructions: [
      {
        time: 0,
        text: "Hey, Karate Star! Ready to level up your stances? First, let's warm up with a lightning-fast review! Start in your natural stance.",
      },
      {
        time: 9,
        text: "First up: Ready Stance! Feet apart, fists tight... pull 'em down... YOI! Go! Hold it strong! Back straight? Knees bent? Awesome!",
      },
      {
        time: 18,
        text: "Now let's do that five times, super fast! Ready... Yoi! ONE! ...and back. Yoi! TWO! ...and back.",
      },
      {
        time: 25,
        text: 'Yoi! THREE! Quicker! ...and back. Yoi! FOUR! ...and back. Yoi! FIVE! Awesome! Hold that Ready Stance.',
      },
      {
        time: 35,
        text: "Now, Front Stance! From Ready, step forward LEFT! Bend that front knee deep so you can't see your toes! Go! ... Back leg straight and strong? Perfect! Slide back to Ready.",
      },
      {
        time: 46,
        text: 'Other side! Step forward RIGHT! Go! Check that form! Awesome! Slide back to Ready.',
      },
      {
        time: 54,
        text: "Let's practice that switch, quick-snap! Left Front Stance! ...And back. Right Front Stance! ...And back. One more each side... Left!... and Right! Awesome power!",
      },
      {
        time: 66,
        text: "Time for Back Stance! From Ready, step back with your right foot, and turn your body. Put most of your weight on that back leg... and make your feet look like an 'L'. Let's see it!",
      },
      {
        time: 78,
        text: "Hold it strong! Feel that power! ...Back to Ready. Other side! Step back with your left foot! Go! ...'L' shape? Weight back? Perfect! Back to Ready.",
      },
      {
        time: 89,
        text: "Let's practice that switch! Right Back Stance! ...and back. Left Back Stance! ...and back. You're making this look easy!",
      },
      {
        time: 99,
        text: "Now for a 'Stance Flow'! Follow me! Start in Ready... Now, Left Front Stance... HOLD!",
      },
      {
        time: 107,
        text: 'Smoothly shift your weight back into a Left Back Stance! Tricky move! ...Good! Step forward again to Left Front Stance...',
      },
      {
        time: 115,
        text: 'And all the way back to Ready! Awesome! Right side now! Ready... Right Front Stance... HOLD!',
      },
      {
        time: 123,
        text: "Shift back to Right Back Stance! Keep your balance! ...Good! Step forward to Right Front Stance... and all the way back to Ready! You're a rockstar!",
      },
      {
        time: 133,
        text: "Let's do that whole flow again, but faster! Left side first... Ready... Front... Back... Front... Ready! GO!",
      },
      {
        time: 142,
        text: "Now the right side, just as fast! Ready... Front... Back... Front... Ready! You're a blur!",
      },
      {
        time: 150,
        text: 'Lightning round! I call it, you do it! Start in Ready! ...Left Front Stance! Step forward with your left foot, bend that knee!',
      },
      {
        time: 155,
        text: 'Right Back Stance! Step back with your right foot, weight on your back leg!',
      },
      {
        time: 158,
        text: 'Right Front Stance! Step forward with your right foot, bend that knee!',
      },
      {
        time: 161,
        text: 'Left Back Stance! Step back with your left foot, weight on your back leg!',
      },
      {
        time: 164,
        text: 'Ready Stance! Feet shoulder-width apart! Awesome reactions! ...Fantastic work! Now get ready for some brand new moves!',
      },
      {
        time: 172,
        text: "First new move: The super strong Horse Stance! From Ready, take a giant step to the side... and bend your knees like you're riding a horse! Sink down low!",
      },
      {
        time: 183,
        text: 'Keep your back straight and feet forward! Feel the power! Hold for ten... nine... eight... seven... six... five... four... three... two... one! And up! Awesome!',
      },
      {
        time: 198,
        text: "Let's do it again! Ready... sink into Horse Stance! And back up!",
      },
      {
        time: 204,
        text: 'Next up: the sneaky Cat Stance! Ready to pounce! From Ready, take a small step forward with your left foot, ball of the foot down, heel up! Put all your weight on your back leg... like a cat!',
      },
      {
        time: 218,
        text: 'Hold it... stay light! ...and back to Ready. Other side! Right foot forward into Cat Stance! All the weight on your back leg! ...and back.',
      },
      {
        time: 228,
        text: "Let's practice! Left Cat Stance! ...And back. Right Cat Stance! ...Perfect!",
      },
      {
        time: 236,
        text: 'Now for the ultimate balance test: Crane Stance! From Ready, slowly lift your right knee up high. Touch the bottom of your right foot to the side of your standing left knee. Use your arms to balance!',
      },
      {
        time: 249,
        text: 'Find your balance! Stare at one spot to help! Hold for five... four... three... two... one... and down. Wow! Other side! Lift your left knee. Hold it steady... five... four... three... two... one... and down! Amazing balance!',
      },
      {
        time: 265,
        text: 'Time to flow with our new stances! Start in Ready... And... Horse Stance! HOLD!',
      },
      {
        time: 272,
        text: 'From there, shift into a Left Cat Stance! ...Now, lift that front leg into a Crane Stance! Woah! ...and back to Ready! Super combo!',
      },
      {
        time: 282,
        text: 'Other side! Ready... Horse Stance! ...Right Cat Stance! ...Lift into Right Crane Stance! Awesome! ...and back to Ready!',
      },
      {
        time: 292,
        text: "Let's do that flow again, faster! Left side! Ready... Horse... Cat... Crane... Ready! GO!",
      },
      {
        time: 300,
        text: "Right side, go! Horse... Cat... Crane... Ready! You're a lightning bolt!",
      },
      {
        time: 307,
        text: 'Lightning round! Random callouts! Be ready! ...Horse Stance! Step out wide and sink down low!',
      },
      {
        time: 311,
        text: 'Left Crane Stance! Lift your left knee up high!',
      },
      {
        time: 314,
        text: 'Right Cat Stance! Right foot forward, be light on your toes!',
      },
      {
        time: 317,
        text: "Ready Stance! Feet apart, fists ready! Awesome! Great job! No breaks, keep moving! Next up is the 'Dynamic Stance Flow'!",
      },
      {
        time: 326,
        text: 'This is all about moving smoothly, like a karate river. Start in a strong Ready Stance.',
      },
      {
        time: 332,
        text: "First, flow into a Left Front Stance! ...Good! Now shift straight back into a Left Back Stance! Feet in an 'L', weight back!",
      },
      {
        time: 341,
        text: 'Excellent! From Back Stance, step your feet wide into a powerful Horse Stance. Sink down low!',
      },
      {
        time: 349,
        text: 'Awesome! From Horse Stance, bring your left foot in for a sneaky Left Cat Stance. Be light!',
      },
      {
        time: 357,
        text: "And from Cat Stance, lift that knee high into a Left Crane Stance! Find your balance! ...And back down to Ready Stance. You're a true karateka.",
      },
      {
        time: 368,
        text: "Let's do that whole flow on the right side now. Ready... Right Front Stance! ...Shift back to Right Back Stance!",
      },
      {
        time: 377,
        text: 'Step out to Horse Stance! ...Bring it in to Right Cat Stance! ...And lift up to Right Crane Stance! ...And back down to Ready. Awesome work!',
      },
      {
        time: 388,
        text: "Let's try that flow again, smoother. Think about connecting the moves. Left side, go! Ready... to Front Stance... shift to Back Stance... out to Horse Stance... into Cat Stance... up to Crane Stance... and back to Ready. Beautiful!",
      },
      {
        time: 405,
        text: "Now the right side, just as smooth! Ready... Front... Back... Horse... Cat... Crane... and back to Ready. You're telling a story with your movements!",
      },
      {
        time: 419,
        text: "Let's make it faster now, but keep it clean. Left side combo, quicker! Ready... Front, Back, Horse, Cat, Crane, Ready! Go!",
      },
      {
        time: 429,
        text: "Awesome speed! Now the right side, just as fast! Ready... Front, Back, Horse, Cat, Crane, Ready! You're a blur!",
      },
      {
        time: 439,
        text: "Even when you're fast, your stances must be strong and balanced. One more time each side... make it your best one yet. Left side... GO!",
      },
      {
        time: 448,
        text: 'And the right side... GO! Excellent control.',
      },
      {
        time: 454,
        text: 'Last minute! Your choice. Slow and controlled, or fast and sharp. Show me your best stuff!',
      },
      {
        time: 462,
        text: 'Thirty seconds left! Empty the tank. Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 472,
        text: "Final fifteen seconds. Give me everything you've got. One more perfect combo!",
      },
      {
        time: 480,
        text: 'Five, four, three, two, one... and TIME! Excellent flow. You are looking strong!',
      },
      {
        time: 488,
        text: "Now, let's add our hands! Time for 'Stance & Strike Coordination'! Let's flow like a ninja. Start in Ready Stance.",
      },
      {
        time: 497,
        text: 'Step into a Left Front Stance AND throw a right punch! Make a fist and punch straight out with your right hand! Ready... GO! ...Awesome! Back to Ready.',
      },
      {
        time: 506,
        text: 'Now, step into a Right Front Stance with a left punch! Make a fist and punch straight out! Go! ...Excellent! Your whole body is a team. Back to Ready.',
      },
      {
        time: 516,
        text: "Let's do that again, faster. Left Front Stance, right punch! ...And back. Right Front Stance, left punch! ...And back.",
      },
      {
        time: 526,
        text: "New combo! From Ready, step back into a Left Back Stance AND bring your hands up to block! Like you're protecting your face! GO! ...Good! Back to Ready.",
      },
      {
        time: 536,
        text: "Other side! Step into a Right Back Stance with a block! Bring those hands up! GO! ...You're moving like a real karate master. Back to Ready.",
      },
      {
        time: 546,
        text: "Let's link everything together now. Start in Ready. Get set... Left Front Stance with a punch! Punch straight out! GO!",
      },
      {
        time: 553,
        text: 'From there, shift straight to a Left Back Stance with a block! Bring your arms up to guard! Good! ...And back to Ready. That was a full karate combo!',
      },
      {
        time: 562,
        text: 'Other side! Ready... Right Front Stance with a punch! ...Shift to Right Back Stance with a block! ...And back to Ready! Awesome!',
      },
      {
        time: 573,
        text: "Let's make the movements quicker, but still sharp. Left side combo, go! Front Stance Punch... Back Stance Block... Ready! Awesome speed.",
      },
      {
        time: 583,
        text: "Now the right side, just as fast! Go! Front Stance Punch... Back Stance Block... Ready! You're a blur!",
      },
      {
        time: 593,
        text: "Let's see that again. Make it your best one yet. Sharp and powerful. Left side... go!",
      },
      {
        time: 600,
        text: 'And the right side... go! Stay steady like a rock.',
      },
      {
        time: 606,
        text: 'Prepare for the final minute. Your choice. Slow and controlled, or fast and sharp. Show me your best stuff!',
      },
      {
        time: 616,
        text: 'Thirty seconds left! Empty the tank. Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 625,
        text: "Final fifteen seconds. Give me everything you've got. One more perfect combo!",
      },
      {
        time: 632,
        text: 'Five, four, three, two, one... and TIME! Excellent coordination.',
      },
      {
        time: 639,
        text: "That was amazing! Now for a super fun brain game to test your speed. It's the 'Reactive Stance Challenge'!",
      },
      {
        time: 647,
        text: 'Listen and react as fast as you can. I call it, you snap to it! Start in Ready Stance. Eyes and ears open!',
      },
      {
        time: 655,
        text: 'Get ready! ...Left Front Stance! Step forward with your left foot! ...Back to Ready. Right Back Stance! Step back with your right foot! ...Back to Ready.',
      },
      {
        time: 663,
        text: 'Horse Stance! Jump your feet out wide! ...Back to Ready. Left Cat Stance! Step forward with your left foot, stay on your toes! ...Back to Ready.',
      },
      {
        time: 670,
        text: "Right Crane Stance! Lift your right knee high! ...And back to Ready! You're a true karate machine!",
      },
      {
        time: 677,
        text: "Let's get faster! Ready... Right Front Stance! ...Left Back Stance! ...Horse Stance!",
      },
      {
        time: 685,
        text: 'Ready... Right Cat Stance! ...Left Crane Stance! And back to Ready!',
      },
      {
        time: 692,
        text: "I'm going to try to trick you now. The calls will be faster. Stay on your toes. Ready... Horse Stance!",
      },
      {
        time: 698,
        text: 'Left Cat Stance! ...Right Front Stance! ...Left Back Stance!',
      },
      {
        time: 705,
        text: "Right Crane Stance! Don't lose your balance! ...Horse Stance again! Sink low!",
      },
      {
        time: 712,
        text: 'Now for combos! Move from the first stance to the second. Ready... Left Front Stance... to Left Back Stance! Smooth shift!',
      },
      {
        time: 720,
        text: 'Back to Ready. Now... Right Front Stance... to Right Back Stance! Excellent.',
      },
      {
        time: 727,
        text: "Back to Ready. Let's try... Horse Stance... to Left Cat Stance! A tricky one!",
      },
      {
        time: 734,
        text: 'Awesome! Back to Ready. Now... Horse Stance... to Right Cat Stance!',
      },
      {
        time: 741,
        text: 'Incredible balance test! Left Back Stance... to Left Crane Stance! ...And back to Ready!',
      },
      {
        time: 749,
        text: "Now the other side. Right Back Stance... to Right Crane Stance! You're a master! ...And back to Ready!",
      },
      {
        time: 757,
        text: "Final minute! This is the lightning round! Calls are super fast. Don't think, just react! Ready...",
      },
      {
        time: 764,
        text: 'Left Front! Right Front! Left Back! Right Back!',
      },
      {
        time: 770,
        text: 'Horse! Left Cat! Right Cat! Left Crane! Right Crane!',
      },
      {
        time: 777,
        text: 'Thirty seconds left! Go, go, go! Ready! Horse! Left Front! Right Back! Right Crane!',
      },
      {
        time: 785,
        text: "Final fifteen seconds. Everything you've got. Left Cat! Right Front! Horse! Ready!",
      },
      {
        time: 792,
        text: 'Five, four, three, two, one... and TIME! Awesome reactions.',
      },
      {
        time: 799,
        text: "Incredible speed! For our final drill, YOU are the master. It's time for 'Kata Creator Freestyle'!",
      },
      {
        time: 808,
        text: 'This is your chance to shine. Create your own mini-kata by combining all the stances. Be creative! Start in Ready Stance... and... GO!',
      },
      {
        time: 819,
        text: 'Start with a powerful Front Stance... what comes next? Try a sneaky Cat Stance! ...Excellent! From there, can you lift into a balanced Crane Stance? Show me that control.',
      },
      {
        time: 831,
        text: "You're creating a story! How about a strong Horse Stance? Sink low. Good. From there, maybe a sharp turn into a Back Stance? Keep that flow going.",
      },
      {
        time: 843,
        text: "Let's add some strikes! As you move into a Front Stance, add a sharp punch! As you move into a Back Stance, add a strong block! Stance and strike together! You're a warrior!",
      },
      {
        time: 856,
        text: "Let's focus on speed. Can you do a fast combo? A quick Front Stance with a punch, then quickly to Back Stance with a block. Awesome speed!",
      },
      {
        time: 867,
        text: 'Now slow it down. Show me a graceful, slow-motion combo. This is how you master control. Keep that focus.',
      },
      {
        time: 878,
        text: "Let's try a balance challenge. From a Crane Stance, can you land softly into a Cat Stance? Incredible! Now from that Cat Stance, explode into a Front Stance with a punch!",
      },
      {
        time: 892,
        text: "You're linking moves with power and purpose. Let's see that combo again. Crane to Cat to Front. A superstar combination.",
      },
      {
        time: 902,
        text: 'Last minute! Empty the tank! Show me your most creative, most powerful kata. This is where it counts.',
      },
      {
        time: 912,
        text: "Thirty seconds! Let's see a masterpiece! Keep that intensity! Link the moves. Make it flow!",
      },
      {
        time: 921,
        text: 'Final fifteen seconds! Show me your best combination! Ten, nine, eight, seven, six...',
      },
      {
        time: 929,
        text: 'five, four, three, two, one... AND FINISH! Excellent work.',
      },
      {
        time: 936,
        text: 'WOW! That was an amazing workout! You crushed all those new stances. You should be super proud of your awesome focus and hard work.',
      },
      {
        time: 946,
        text: "Let's cool down. Stand in your Natural Stance. Take a big, deep breath in... and bow. You trained like a true karate champion today. Amazing job. Skill complete!",
      },
    ],
    timeTotal: 1776,
    equipment: 'None',
  },

  // ADVANCED STANCES (45 minutes)
  {
    id: 2033,
    name: 'Advanced Stances',
    categories: ['Stances', 'advanced'],
    sports: ['karate'],
    difficulty: 'advanced',
    skill: 'Stances',
    points: 45,
    instructions:
      "Ready for a black belt challenge? Let's master some super-secret, powerful stances! We'll move like a storm, stand strong like a mountain, and test your balance like a true karate master. Let's begin!",
    timedInstructions: [
      {
        time: 0,
        text: "Hey, Black Belt in training! Ready for a real challenge? Let's warm up with a super fast review! Start in a Ready Stance! Feet apart, fists ready!",
      },
      {
        time: 9,
        text: 'Okay, left side first! Flow with me! Step out to a wide Horse Stance, sink low! ... Good! Now shift into a Left Cat Stance, get light on your front toes!',
      },
      {
        time: 19,
        text: 'Now lift that knee high into a Left Crane Stance, find your balance! ... And back to Ready! Awesome!',
      },
      {
        time: 27,
        text: "Right side, just as smooth! Ready... sink into a Horse Stance! ... Shift to a Right Cat Stance! ... Lift into a Right Crane Stance! Balance like a champ! ... And back down to Ready! You're a warrior!",
      },
      {
        time: 40,
        text: "Let's do that flow again, even smoother! Left side! Ready... Horse... Cat... Crane... Ready! Go!",
      },
      {
        time: 49,
        text: 'Right side, go! Ready... Horse... Cat... Crane... Ready! Beautiful flow!',
      },
      {
        time: 58,
        text: "Now let's mix EVERYTHING together! Follow the flow! Ready... step to Left Front Stance, knee bent!",
      },
      {
        time: 66,
        text: "From there, step out to a wide Horse Stance! ... Now, step back into a Right Back Stance, feet in an 'L' shape!",
      },
      {
        time: 76,
        text: 'Shift to a sneaky Right Cat Stance! ... And finish by lifting that knee to a Right Crane Stance! ... And back to Ready! WOW! What a combo!',
      },
      {
        time: 88,
        text: "Let's try that whole combination again, but faster! Ready... Left Front... Horse... Right Back... Right Cat... Right Crane... and Ready! GO!",
      },
      {
        time: 100,
        text: "Awesome speed! Keep that focus. Even when you're fast, your stances must be strong. One more time, even faster! Go!",
      },
      {
        time: 110,
        text: "Excellent! You're looking like a true martial artist. Prepare for the final minute of our warm-up!",
      },
      {
        time: 118,
        text: 'Last minute! Your choice! Slow and controlled, or fast and sharp! Show me your best flow!',
      },
      {
        time: 126,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 136,
        text: "Final fifteen seconds! Give me everything you've got! One more perfect combo!",
      },
      {
        time: 143,
        text: 'Five, four, three, two, one... and TIME! Excellent flow. Now get ready to learn!',
      },
      {
        time: 150,
        text: "Time to learn 'Elemental Stances: Earth & Sky'! First up, the unmovable Mountain Stance!",
      },
      {
        time: 158,
        text: 'From Ready, step your feet a little wider than your shoulders, turn your toes in slightly, and bend your knees. Sink your weight down! Feel strong, like a giant mountain!',
      },
      {
        time: 169,
        text: "Hold it... feel that power! Five... four... three... two... one! And up! Awesome! Let's do it again! Sink into Mountain Stance! SOLID! And back to Ready.",
      },
      {
        time: 182,
        text: 'Now for the graceful Phoenix Stance! From Ready, cross your right leg behind your left, so your knees are crossed. Stay on the ball of that back foot, ready to spring! Most of your weight is on your front leg. Find that tricky balance!',
      },
      {
        time: 196,
        text: "Good! Back to Ready. Other side! Cross your left leg behind for a Left Phoenix Stance! Stay tall! ... And back. Let's practice that switch, quick! Right Phoenix! ... and Left Phoenix! Perfect!",
      },
      {
        time: 209,
        text: "Let's combine them! Earth and Sky! Ready... sink into a strong Mountain Stance! ... Now spring up and cross into a Left Phoenix Stance! Light and flowing! ... And back to Ready! Superstar combo!",
      },
      {
        time: 222,
        text: "Other side! Ready... sink into Mountain Stance! ... Now spring into a Right Phoenix Stance! ... And back to Ready! You're an elemental master!",
      },
      {
        time: 233,
        text: "Let's do that flow again, but smoother. Left side first. Ready... Mountain... Phoenix... Ready! Go!",
      },
      {
        time: 242,
        text: 'Now the right side, just as smooth! Ready... Mountain... Phoenix... and back to Ready. Beautiful!',
      },
      {
        time: 251,
        text: 'Time for a challenge! Hold the Mountain Stance for 15 seconds! Sink low, be unmovable! Go!',
      },
      {
        time: 267,
        text: "Good! Now let's hold the Left Phoenix Stance for 10 seconds. Find that balance.",
      },
      {
        time: 278,
        text: 'And the Right Phoenix Stance for 10 seconds. Stay steady.',
      },
      {
        time: 289,
        text: 'Last minute! Lightning round! I call it, you do it! Start in Ready Stance.',
      },
      {
        time: 295,
        text: 'Mountain Stance! Get wide and sink low!',
      },
      {
        time: 299,
        text: 'Left Phoenix Stance! Cross your left leg behind you!',
      },
      {
        time: 303,
        text: 'Right Phoenix Stance! Cross your right leg behind!',
      },
      {
        time: 307,
        text: 'Mountain Stance! Be solid as a rock!',
      },
      {
        time: 311,
        text: 'Ready Stance! Feet apart, fists ready! Awesome reactions! Great work!',
      },
      {
        time: 318,
        text: "Amazing! Now for two more elemental stances: 'Storm & Stillness'!",
      },
      {
        time: 324,
        text: 'First, the explosive Thunder Stance! From Ready, take a big step forward with your left foot, but keep BOTH knees bent. Balance your weight, ready to spring forward like a lightning strike!',
      },
      {
        time: 337,
        text: "Hold it! Feel that power! Five... four... three... two... one... And back to Ready! Awesome! Let's do the other side. Explode into a Right Thunder Stance! And back to Ready.",
      },
      {
        time: 351,
        text: 'Now, the calm Water Stance. From Ready, take a short step back with your right foot and turn your body slightly. Keep both knees bent and stay light on your feet, like a flowing river.',
      },
      {
        time: 364,
        text: "Good! Back to Ready. Other side! Step back with your left foot into a Left Water Stance. Relaxed but ready! ... And back. Let's practice that switch! Right Water! ... and Left Water! Perfect.",
      },
      {
        time: 378,
        text: 'Time to combine them! Storm and Stillness! Ready... explode into a Left Thunder Stance! Be powerful! ... Good! Now flow back into a Left Water Stance. Be calm and fluid.',
      },
      {
        time: 391,
        text: "Excellent! And back to Ready! Let's do the other side. Ready... explode into a Right Thunder Stance! ... Now, flow back into a Right Water Stance! ... And back to Ready! You're a master!",
      },
      {
        time: 405,
        text: "Let's do that flow again, but smoother. Left side first. Ready... Thunder... Water... Ready! Go!",
      },
      {
        time: 414,
        text: 'Now the right side, just as smooth! Ready... Thunder... Water... and back to Ready. Beautiful!',
      },
      {
        time: 423,
        text: 'Time for a power challenge! Hold the Left Thunder Stance for 15 seconds. Get low, feel the energy!',
      },
      {
        time: 439,
        text: "Good! Now let's hold the Left Water Stance for 10 seconds. Find that flow.",
      },
      {
        time: 450,
        text: 'And the Right Water Stance for 10 seconds. Stay relaxed but ready.',
      },
      {
        time: 461,
        text: 'Last minute! Lightning round! Get ready! Start in Ready Stance.',
      },
      {
        time: 467,
        text: 'Thunder Stance! Explode forward, bend both knees!',
      },
      {
        time: 471,
        text: 'Left Water Stance! Step back with your left foot, stay light!',
      },
      {
        time: 475,
        text: 'Right Water Stance! Step back with your right foot!',
      },
      {
        time: 479,
        text: 'Thunder Stance! Be powerful!',
      },
      {
        time: 483,
        text: 'Ready Stance! Awesome speed! Fantastic work.',
      },
      {
        time: 488,
        text: "You are crushing it! Now let's link ALL our new stances together in the 'Advanced Combinations' drill!",
      },
      {
        time: 496,
        text: 'First, we combine Earth and Storm! Ready... sink into Mountain Stance! Be unmovable! ... Good! From that strong base, explode forward into a Left Thunder Stance! Feel the power!',
      },
      {
        time: 509,
        text: 'Excellent! Back to Ready. Other side! Sink into Mountain Stance... and explode into a Right Thunder Stance! Go!',
      },
      {
        time: 519,
        text: 'Awesome! Now for Stillness and Sky! Flow into a Left Water Stance. Be calm. ... Good! Now, spring up into a Left Phoenix Stance! Cross that leg behind, be graceful!',
      },
      {
        time: 532,
        text: 'And back to Ready! Other side! Go! Right Water Stance... and spring into a Right Phoenix Stance! Excellent balance!',
      },
      {
        time: 543,
        text: 'Time for a longer flow! Three stances! Ready... sink into a Mountain Stance! Be strong. Now, from there, flow into a Left Water Stance.',
      },
      {
        time: 554,
        text: 'Good! And from Water Stance, explode into a Left Thunder Stance! ... And back to Ready! That was Earth, Stillness, and Storm!',
      },
      {
        time: 564,
        text: "Right side! Ready... Mountain... Right Water... Right Thunder... and back to Ready! You're a kata master!",
      },
      {
        time: 575,
        text: "Let's make it faster! Left side combo, quicker! Ready... Mountain, Water, Thunder, Ready! Go!",
      },
      {
        time: 584,
        text: "Awesome speed! Now the right side, just as fast! Ready... Mountain, Water, Thunder, Ready! You're a blur!",
      },
      {
        time: 593,
        text: 'Keep that focus! One more time each side, your fastest yet! GO!',
      },
      {
        time: 602,
        text: 'Final minute! Your choice! Slow and controlled, or fast and sharp! Show me your best combination!',
      },
      {
        time: 611,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 621,
        text: "Final fifteen seconds! Give me everything you've got! One more perfect combo!",
      },
      {
        time: 628,
        text: 'Five, four, three, two, one... and TIME! Excellent combinations.',
      },
      {
        time: 634,
        text: "Amazing combos! Now for the ultimate test of your speed and brain power... the 'Reactive Stance Gauntlet'!",
      },
      {
        time: 642,
        text: "I'm going to call out ANY stance we've learned. Snap into it as fast as you can! Start in Ready Stance. Eyes and ears open!",
      },
      {
        time: 651,
        text: 'Get ready! ... Mountain Stance! Go! Get wide, sink low!',
      },
      {
        time: 656,
        text: 'Back to Ready! Now... Left Phoenix Stance! Cross your left leg behind!',
      },
      {
        time: 662,
        text: 'Back to Ready! And... Right Thunder Stance! Explode forward!',
      },
      {
        time: 668,
        text: 'Back to Ready! Left Water Stance! Step back left, be fluid!',
      },
      {
        time: 674,
        text: 'Back to Ready! Right Crane Stance! Lift that right knee high!',
      },
      {
        time: 680,
        text: "Back to Ready! Horse Stance! Sink down low! ... You're a karate machine!",
      },
      {
        time: 688,
        text: 'The calls are getting faster! Stay on your toes! Ready... Horse Stance! Good. Ready... Left Cat Stance!',
      },
      {
        time: 695,
        text: 'Ready... Right Thunder Stance! Ready... Left Water Stance!',
      },
      {
        time: 701,
        text: "Ready... Right Crane Stance! Don't lose your balance!",
      },
      {
        time: 707,
        text: 'Ready... Mountain Stance again! Be solid as a rock!',
      },
      {
        time: 712,
        text: 'Now for combos! Move from the first stance to the second! Ready... Mountain Stance... to Left Thunder Stance! Earth to Storm!',
      },
      {
        time: 721,
        text: 'Back to Ready! Now... Right Water Stance... to Right Phoenix Stance! Stillness to Sky!',
      },
      {
        time: 729,
        text: 'Back to Ready! Horse Stance... to Left Cat Stance! A tricky one!',
      },
      {
        time: 736,
        text: 'Back to Ready! Right Back Stance... to Right Crane Stance! Awesome flow!',
      },
      {
        time: 744,
        text: 'Back to single calls! Be ready! Right Cat Stance, toes down! ...Left Front Stance, step forward! ...Horse Stance, get low! ...Ready!',
      },
      {
        time: 753,
        text: "Final minute! This is the lightning round! Super fast! Don't think, just react! Ready...",
      },
      {
        time: 760,
        text: 'Left Front! Right Phoenix! Horse! Left Water!',
      },
      {
        time: 766,
        text: 'Mountain! Left Cat! Right Cat! Left Crane! Right Crane!',
      },
      {
        time: 773,
        text: 'Thirty seconds left! Go, go, go! Ready! Horse! Left Front! Right Back! Right Thunder!',
      },
      {
        time: 781,
        text: "Final fifteen seconds! Everything you've got! Left Cat! Right Front! Mountain! Ready!",
      },
      {
        time: 788,
        text: 'Five, four, three, two, one... and TIME! Awesome reactions.',
      },
      {
        time: 794,
        text: "Incredible speed! For our final drill, YOU are the master. It's time for the 'Kata Creator Freestyle'!",
      },
      {
        time: 802,
        text: "This is your chance to shine. You will create your own mini-kata by combining ALL the stances we've learned. Be creative! Start in Ready Stance... Begin!",
      },
      {
        time: 812,
        text: 'Start with a powerful Mountain Stance... Now, what comes next? Try flowing into a graceful Phoenix Stance!',
      },
      {
        time: 821,
        text: 'Excellent! From that Phoenix Stance, can you explode into a Thunder Stance? Show me that power.',
      },
      {
        time: 829,
        text: "Now, let's add some strikes! As you step into a Front Stance, add a sharp punch! Make a fist and punch straight out!",
      },
      {
        time: 838,
        text: "Good! When you move into a Back Stance, add a strong block! Bring your arm up to guard your head! You're a warrior!",
      },
      {
        time: 847,
        text: 'Keep that creativity flowing. What other combos can you create? Maybe a Horse Stance with a double block? Be powerful!',
      },
      {
        time: 857,
        text: "Let's focus on your speed. Show me a fast combo! Quick transition from a Thunder Stance to a Water Stance!",
      },
      {
        time: 866,
        text: 'Awesome speed! Now slow it down. Show me a graceful, slow-motion combo. This is how you master control.',
      },
      {
        time: 876,
        text: "Let's see a balance challenge! From a Crane Stance, can you land softly into a Phoenix Stance? Incredible balance!",
      },
      {
        time: 886,
        text: 'Now from that Phoenix Stance, can you explode into a Thunder Stance with a punch? A superstar combination!',
      },
      {
        time: 895,
        text: 'Keep that quality high. Every stance should be perfect. This is very advanced work.',
      },
      {
        time: 903,
        text: 'Final minute! Empty the tank! Show me your most creative, most powerful kata!',
      },
      {
        time: 911,
        text: "Thirty seconds! Let's see a masterpiece! Keep that intensity! Link the moves. Make it flow!",
      },
      {
        time: 920,
        text: 'Final fifteen seconds! Show me your best combination! Ten, nine, eight, seven, six...',
      },
      {
        time: 928,
        text: 'five, four, three, two, one... AND FINISH! Excellent work.',
      },
      {
        time: 935,
        text: 'WOW! That was an incredible session! You mastered so many advanced stances today. Be proud of your amazing hard work and focus.',
      },
      {
        time: 944,
        text: "Let's do a final cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You have trained like a true karate champion today. Amazing job. Skill complete!",
      },
    ],
    timeTotal: 950,
    equipment: 'None',
  },

  // BEGINNER STRIKES (30 minutes)
  {
    id: 2034,
    name: 'Beginner Strikes',
    categories: ['Strikes', 'beginner'],
    sports: ['karate'],
    difficulty: 'beginner',
    skill: 'Strikes',
    points: 30,
    instructions:
      'Beginner Strikes is a comprehensive session to develop your basic karate striking techniques. This 30-minute session combines fundamental punching techniques, basic striking form, and simple striking patterns.',
    timedInstructions: [
      {
        time: 0,
        text: "Hello, karateka! Ready to build some powerful strikes? Our first drill is the most important one: 'Fist of Fury Foundation'! Let's begin in a ready stance.",
      },
      {
        time: 10,
        text: "Before we punch, we have to learn how to make a perfect, safe fist. Hold your right hand out flat, like you're about to give a high-five.",
      },
      {
        time: 19,
        text: 'Now, curl your four fingers down tight into your palm. Really tight!',
      },
      {
        time: 25,
        text: "Good. Now, take your thumb and wrap it over the top of your first two fingers. Your thumb should NOT be inside your fist. That's the secret to a safe fist!",
      },
      {
        time: 36,
        text: "Let's try that again. Open your hand... curl your fingers... and wrap your thumb. Perfect! Now do the same with your left hand.",
      },
      {
        time: 46,
        text: 'Open... curl... and wrap. You now have two perfect karate fists! Awesome.',
      },
      {
        time: 53,
        text: "One minute in! Now, let's learn the straight punch. Start in your ready stance, with both fists at your hips, palms facing up.",
      },
      {
        time: 63,
        text: "We'll start slowly. Push your right fist straight out in front of you. As it gets to the end, I want you to twist it so your palm is facing down. It's a little twist at the very end.",
      },
      {
        time: 76,
        text: "As you punch with your right hand, pull your left hand back to your hip, twisting it palm up. It's like they are working together as a team! Let's try that slow motion again. Punch and pull.",
      },
      {
        time: 89,
        text: 'Excellent! Now the other side. Punch slowly with your left fist, twisting it at the end, and pull your right fist back to your hip. Punch and pull.',
      },
      {
        time: 101,
        text: "You're doing great. This is the most important part of a strong punch.",
      },
      {
        time: 108,
        text: "Let's do five slow and perfect punches with your right hand. Remember the twist and the pull. One...",
      },
      {
        time: 116,
        text: 'Two... good twist. Three... pull that other hand back!',
      },
      {
        time: 122,
        text: 'Four... looking strong. Five! Awesome.',
      },
      {
        time: 128,
        text: 'Now five with your left hand. Slow and perfect. One...',
      },
      {
        time: 135,
        text: 'Two... straight like an arrow. Three... remember to twist.',
      },
      {
        time: 142,
        text: "Four... great form. Five! You're a natural.",
      },
      {
        time: 148,
        text: "Halfway! Now let's try alternating. A slow right punch, then a slow left punch. Find a rhythm.",
      },
      {
        time: 157,
        text: 'Right... then left. Right... then left. As one hand goes out, the other comes back.',
      },
      {
        time: 165,
        text: "This is looking great. You're building that muscle memory.",
      },
      {
        time: 171,
        text: "Now, let's pick up the speed just a little bit. Not too fast, just a little sharper. Right, left, right, left.",
      },
      {
        time: 180,
        text: "Good! Remember to breathe out with every punch. It makes you stronger. A little 'kiai' shout!",
      },
      {
        time: 189,
        text: "Let's try it. A sharp punch with a little 'ha!' sound. Go!",
      },
      {
        time: 196,
        text: 'Awesome! You sound like a true warrior. Keep that going.',
      },
      {
        time: 202,
        text: "Let's see ten more sharp punches, alternating hands. Count them out!",
      },
      {
        time: 209,
        text: 'Keep that form perfect. A straight punch is a strong punch.',
      },
      {
        time: 215,
        text: "You're doing great. Keep that energy high.",
      },
      {
        time: 220,
        text: "Let's go back to slow motion for a second. Let's check our fists. Are they still perfect?",
      },
      {
        time: 228,
        text: 'Fingers curled, thumb on top. Good. Now back to sharp punches.',
      },
      {
        time: 234,
        text: "Prepare for the final minute. We're going to do a speed challenge.",
      },
      {
        time: 240,
        text: "Last minute! Let's see how many good, sharp punches you can do in 30 seconds. I'll count. Ready? Go!",
      },
      {
        time: 270,
        text: 'Awesome job! Now for the final 30 seconds, I want to see your most powerful punches. Put your whole body into it, but keep that form perfect. Go!',
      },
      {
        time: 280,
        text: "Strong punches! You're a powerhouse!",
      },
      {
        time: 285,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 290,
        text: 'Five, four, three, two, one... and TIME! Excellent work.',
      },
      {
        time: 297,
        text: "That was a fantastic first exercise! Now it's time to work on our accuracy with 'Laser Punch Practice'!",
      },
      {
        time: 305,
        text: 'I want you to imagine a tiny target right in front of you, at the same height as your nose. This is our bullseye!',
      },
      {
        time: 313,
        text: "Start in your Ready Stance. We're going to practice hitting that bullseye with a perfect, straight punch. Let's start with your right hand. Aim for the target... and... PUNCH!",
      },
      {
        time: 324,
        text: "Good! Did it go straight like a laser beam? Remember to twist your fist at the end. Let's try again. Right hand... PUNCH!",
      },
      {
        time: 333,
        text: 'Awesome! Now the left hand. Aim for that same bullseye. Ready... PUNCH!',
      },
      {
        time: 340,
        text: "Remember to pull your other hand back to your hip super fast. Let's see another left-hand laser. PUNCH!",
      },
      {
        time: 348,
        text: "One minute in! You're a true karate sniper. Let's try alternating now. Right punch, then left punch. Hit that target every time.",
      },
      {
        time: 358,
        text: 'Right... left... right... left. Keep that laser beam straight and fast.',
      },
      {
        time: 366,
        text: "Let's see ten in a row. Count them with me! One! Two! Three! Four! Five!",
      },
      {
        time: 375,
        text: 'Six! Seven! Eight! Nine! Ten! Perfect accuracy.',
      },
      {
        time: 382,
        text: "Let's check our fists. Are they still tight and safe? Fingers curled, thumb on top. Good.",
      },
      {
        time: 390,
        text: "Now back to the laser punches. Let's focus on speed. A little faster now. Right, left, right, left!",
      },
      {
        time: 399,
        text: "Don't forget your kiai! A little 'ha!' with every punch makes you even more powerful.",
      },
      {
        time: 407,
        text: "Let's hear it! Ten fast punches with a kiai! Go!",
      },
      {
        time: 417,
        text: 'Awesome! You sound like a true warrior. Keep that energy.',
      },
      {
        time: 423,
        text: "Halfway! Let's slow it down again. Focus on perfect form. A slow, beautiful right punch.",
      },
      {
        time: 431,
        text: 'And a slow, perfect left punch. Remember that twist at the end.',
      },
      {
        time: 438,
        text: 'This is how you build strong habits. Slow and perfect is better than fast and sloppy.',
      },
      {
        time: 446,
        text: "Let's do another slow one. Feel every muscle working together.",
      },
      {
        time: 453,
        text: 'Excellent. Now, back to game speed. Sharp and fast!',
      },
      {
        time: 459,
        text: 'Imagine the target is moving. You have to be quick to hit it. Right, left, right, left!',
      },
      {
        time: 467,
        text: "Keep your eyes on that target. Don't look away.",
      },
      {
        time: 473,
        text: "You're doing great. Your punches are looking so much stronger and straighter.",
      },
      {
        time: 480,
        text: "Let's see another ten-punch combo. Make it your best one yet.",
      },
      {
        time: 488,
        text: "Keep that energy high. You're a striking machine!",
      },
      {
        time: 494,
        text: 'Prepare for the final minute. This is the lightning round!',
      },
      {
        time: 500,
        text: "Last minute! I'm going to call out a hand. You have to punch with it as fast as you can. Ready... RIGHT!",
      },
      {
        time: 508,
        text: 'LEFT! LEFT! RIGHT! RIGHT!',
      },
      {
        time: 515,
        text: 'LEFT! RIGHT! LEFT! RIGHT!',
      },
      {
        time: 522,
        text: 'Thirty seconds left! Alternating punches, as fast as you can! Go, go, go!',
      },
      {
        time: 530,
        text: "Faster, faster! Don't slow down!",
      },
      {
        time: 536,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 542,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 548,
        text: 'Five, four, three, two, one... and TIME! Excellent punches.',
      },
      {
        time: 555,
        text: "That was some amazing accuracy! Now it's time to use our powerful legs in our next drill: 'Karate Kick-off'!",
      },
      {
        time: 564,
        text: "We're going to learn the beginner front kick, or 'Mae-geri'. This is a super important kick. Let's start in our Ready Stance.",
      },
      {
        time: 573,
        text: "Let's break it down into four easy parts. The first part is the 'chamber'. Lift your right knee up as high as your belt. Try to balance on your left leg. Hold it!",
      },
      {
        time: 585,
        text: "Good! And back down. Let's try that again. Lift that right knee high! This is where all your power comes from. And down.",
      },
      {
        time: 595,
        text: 'Now the left leg. Lift your left knee up to your belt. Find your balance. Use your arms to help you. And down.',
      },
      {
        time: 605,
        text: 'One more time with the left. A high knee chamber! Excellent.',
      },
      {
        time: 611,
        text: "One minute in! Now for part two: the 'kick'. After you lift your knee, I want you to snap your foot straight out in front of you, like you're kicking a door open.",
      },
      {
        time: 623,
        text: "Let's try it slowly. Right knee up... and kick! Then bring it back down. Awesome!",
      },
      {
        time: 631,
        text: "Now the left leg. Left knee up... and kick! You're a powerhouse.",
      },
      {
        time: 638,
        text: "The third part is the 're-chamber'. After you kick, you have to bring your knee right back to that high chamber position before you put it down.",
      },
      {
        time: 649,
        text: "Let's try the whole thing slowly. Right knee up... kick... knee back... and down. That's a perfect front kick!",
      },
      {
        time: 660,
        text: "Now the left leg. Knee up... kick... knee back... and down. You're a natural!",
      },
      {
        time: 670,
        text: "Two minutes in! Let's practice that full motion. We'll do five slow and perfect kicks with each leg. Right leg first. Go!",
      },
      {
        time: 680,
        text: "Remember all four parts. Chamber, kick, re-chamber, down. That's two.",
      },
      {
        time: 688,
        text: "Keep your hands up in a guard position to protect your face. That's three.",
      },
      {
        time: 695,
        text: "Don't lean back too much. Try to keep your back straight. That's four.",
      },
      {
        time: 702,
        text: 'And five! Awesome! Now the left leg. Five slow and perfect kicks.',
      },
      {
        time: 710,
        text: 'Remember to kick with the ball of your foot, right under your toes.',
      },
      {
        time: 717,
        text: "You're doing great. This is a tricky kick to learn, but you're crushing it.",
      },
      {
        time: 724,
        text: "Halfway! Let's pick up the speed just a little bit. A little sharper, a little faster.",
      },
      {
        time: 732,
        text: "Let's see a sharp front kick with your right leg. Good! Now one with your left.",
      },
      {
        time: 740,
        text: "Let's try alternating now. A right kick, then a left kick. Find a good rhythm.",
      },
      {
        time: 748,
        text: 'Right... left... right... left. Keep those hands up!',
      },
      {
        time: 755,
        text: "Don't forget to bring that knee back after you kick. It's super important for your balance.",
      },
      {
        time: 763,
        text: "Let's see ten in a row, alternating. Count them out!",
      },
      {
        time: 769,
        text: "You're a kicking machine! Keep that energy.",
      },
      {
        time: 775,
        text: "Let's go back to slow motion for a second. Let's check our form. High knee chamber.",
      },
      {
        time: 783,
        text: 'Good. A sharp kick. Good. Knee back to chamber. Perfect. And down.',
      },
      {
        time: 791,
        text: 'Now back to game speed. Sharp and fast!',
      },
      {
        time: 797,
        text: 'Prepare for the final minute. This is the power round!',
      },
      {
        time: 802,
        text: "Last minute! I want to see your most powerful kicks. Imagine you're breaking a board! Ready? Right leg, go!",
      },
      {
        time: 811,
        text: 'Awesome power! Now left leg! Just as strong.',
      },
      {
        time: 817,
        text: "Don't forget your kiai! A big 'ha!' with every kick!",
      },
      {
        time: 824,
        text: "Thirty seconds left! Let's see how many power kicks you can do. Go, go, go!",
      },
      {
        time: 832,
        text: 'Faster, stronger! Come on!',
      },
      {
        time: 838,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 844,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 850,
        text: 'Five, four, three, two, one... and TIME! Excellent kicks.',
      },
      {
        time: 857,
        text: "That was some amazing power! Now that you're a kicking machine, let's work on our aim with 'Bullseye Kicks'!",
      },
      {
        time: 866,
        text: 'I want you to imagine a small target, a bullseye, right in front of you at belly-button height. Our mission is to hit that bullseye with every single kick.',
      },
      {
        time: 877,
        text: "Let's start in our Ready Stance. We'll use our perfect front kick form. Let's start with your right leg. Aim for the bullseye... and... KICK!",
      },
      {
        time: 888,
        text: "Awesome! Did you hit it? Remember to kick with the ball of your foot, right under your toes. Let's try again. Right leg... KICK!",
      },
      {
        time: 898,
        text: 'Bullseye! Now the left leg. Aim for that same target. Ready... KICK!',
      },
      {
        time: 906,
        text: "Remember all four parts: high knee chamber, snap the kick, bring the knee back, and then down. Let's see another left-leg bullseye. KICK!",
      },
      {
        time: 917,
        text: "One minute in! You're a true karate sniper. Let's try alternating now. A right kick, then a left kick. Hit that target every time.",
      },
      {
        time: 927,
        text: 'Right... left... right... left. Keep those hands up in your guard position!',
      },
      {
        time: 935,
        text: "Let's see ten in a row. Count them with me! One! Two! Three! Four! Five!",
      },
      {
        time: 944,
        text: 'Six! Seven! Eight! Nine! Ten! Perfect accuracy.',
      },
      {
        time: 951,
        text: "Let's check our balance. Your standing foot should be strong like a tree trunk. Don't wobble!",
      },
      {
        time: 959,
        text: "Now back to the bullseye kicks. Let's focus on speed. A little faster now. Right, left, right, left!",
      },
      {
        time: 968,
        text: "Don't forget your kiai! A little 'ha!' with every kick makes you even more powerful.",
      },
      {
        time: 976,
        text: "Let's hear it! Ten fast kicks with a kiai! Go!",
      },
      {
        time: 986,
        text: 'Awesome! You sound like a true warrior. Keep that energy.',
      },
      {
        time: 992,
        text: "Halfway! Let's slow it down again. Focus on perfect form. A slow, beautiful right kick.",
      },
      {
        time: 1000,
        text: 'And a slow, perfect left kick. Remember that high knee chamber.',
      },
      {
        time: 1007,
        text: 'This is how you build strong habits. Slow and perfect is better than fast and sloppy.',
      },
      {
        time: 1015,
        text: "Let's do another slow one. Feel every muscle working together.",
      },
      {
        time: 1022,
        text: 'Excellent. Now, back to game speed. Sharp and fast!',
      },
      {
        time: 1028,
        text: 'Imagine the target is moving. You have to be quick to hit it. Right, left, right, left!',
      },
      {
        time: 1036,
        text: "Keep your eyes on that target. Don't look away.",
      },
      {
        time: 1042,
        text: "You're doing great. Your kicks are looking so much stronger and more accurate.",
      },
      {
        time: 1049,
        text: "Let's see another ten-kick combo. Make it your best one yet.",
      },
      {
        time: 1057,
        text: "Keep that energy high. You're a kicking machine!",
      },
      {
        time: 1063,
        text: 'Prepare for the final minute. This is the lightning round!',
      },
      {
        time: 1068,
        text: "Last minute! I'm going to call out a leg. You have to kick with it as fast as you can. Ready... RIGHT!",
      },
      {
        time: 1076,
        text: 'LEFT! LEFT! RIGHT! RIGHT!',
      },
      {
        time: 1083,
        text: 'LEFT! RIGHT! LEFT! RIGHT!',
      },
      {
        time: 1090,
        text: 'Thirty seconds left! Alternating kicks, as fast as you can! Go, go, go!',
      },
      {
        time: 1098,
        text: "Faster, faster! Don't slow down!",
      },
      {
        time: 1104,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1110,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1116,
        text: 'Five, four, three, two, one... and TIME! Excellent kicks.',
      },
      {
        time: 1123,
        text: "That was some amazing accuracy! Now it's time to put our skills together in the 'Punch-Kick Power Combo'!",
      },
      {
        time: 1131,
        text: "This is all about linking our punches and kicks into an awesome combination. Let's start in our Ready Stance. Hands up, ready to go!",
      },
      {
        time: 1140,
        text: "First, let's practice a 'one-two' punch. That's a quick straight punch with your left hand, followed immediately by a straight punch with your right hand. Let's try it slowly. Left, right.",
      },
      {
        time: 1152,
        text: "Awesome! Now, right after that second punch, you're going to throw a powerful front kick with your right leg. So it's: Left punch, right punch, right kick! Let's see it slowly.",
      },
      {
        time: 1165,
        text: "Excellent! After the kick, bring your foot back down and reset in your Ready Stance. Let's try the combo again. Punch, punch, kick!",
      },
      {
        time: 1175,
        text: "Now let's try it on the other side. A right punch, then a left punch, followed by a big left front kick. Go!",
      },
      {
        time: 1185,
        text: "You're a combo machine! One minute in! Let's focus on making it smooth. The kick should flow right after the last punch.",
      },
      {
        time: 1195,
        text: "Let's see that flow. Punch-punch-kick! It should feel like one single, powerful move.",
      },
      {
        time: 1203,
        text: "Remember to keep your hands up in a guard position the whole time, even when you're kicking.",
      },
      {
        time: 1211,
        text: "Let's see five good combos on each side. Keep switching after each one.",
      },
      {
        time: 1218,
        text: "This is how you build amazing coordination. You're doing great.",
      },
      {
        time: 1224,
        text: "Let's see another one. Make it your best one yet. Sharp and strong!",
      },
      {
        time: 1231,
        text: "Excellent! You're looking like a real karateka.",
      },
      {
        time: 1236,
        text: "Two minutes in! Let's pick up the speed a little bit. A little sharper, a little faster.",
      },
      {
        time: 1244,
        text: "Let's see a fast combo. Punch-punch-kick! Good. Now the other side, just as fast.",
      },
      {
        time: 1253,
        text: "Don't forget your kiai on that last kick! A big 'ha!' shows your power.",
      },
      {
        time: 1261,
        text: "Let's hear it! A sharp combo with a loud kiai! Go!",
      },
      {
        time: 1268,
        text: 'Awesome! You sound like a true warrior. Keep that energy.',
      },
      {
        time: 1274,
        text: "Let's see another fast combo. Make it a blur!",
      },
      {
        time: 1280,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1285,
        text: "Halfway! Let's slow it down again. Focus on perfect form. A slow, beautiful punch-punch-kick.",
      },
      {
        time: 1294,
        text: "Remember that high knee chamber on your kick. It's the secret to power.",
      },
      {
        time: 1301,
        text: 'This is how you build strong habits. Slow and perfect is better than fast and sloppy.',
      },
      {
        time: 1309,
        text: "Let's do another slow one. Feel every muscle working together.",
      },
      {
        time: 1316,
        text: 'Excellent. Now, back to game speed. Sharp and fast!',
      },
      {
        time: 1322,
        text: 'Imagine a target in front of you. Your punches and your kick should all hit that same bullseye.',
      },
      {
        time: 1331,
        text: "Let's see that accuracy. Punch, punch, kick, all on target.",
      },
      {
        time: 1338,
        text: "You're a striking machine! Keep that energy.",
      },
      {
        time: 1344,
        text: "Let's see another ten-combo series. Make it your best one yet.",
      },
      {
        time: 1352,
        text: "Keep that focus. You're almost there.",
      },
      {
        time: 1357,
        text: 'Prepare for the final minute. This is the power round!',
      },
      {
        time: 1362,
        text: "Last minute! I want to see your most powerful combos. Imagine you're breaking a board! Ready? Go!",
      },
      {
        time: 1371,
        text: 'Awesome power! Now the other side! Just as strong.',
      },
      {
        time: 1377,
        text: "Don't forget your kiai! A big 'ha!' with every combo!",
      },
      {
        time: 1384,
        text: "Thirty seconds left! Let's see how many power combos you can do. Go, go, go!",
      },
      {
        time: 1392,
        text: 'Faster, stronger! Come on!',
      },
      {
        time: 1398,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1404,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1410,
        text: 'Five, four, three, two, one... and TIME! Excellent combos.',
      },
      {
        time: 1417,
        text: "That was some amazing power! For our final and most fun drill, it's time for the 'Ninja Shadow Battle'! Are you ready?",
      },
      {
        time: 1426,
        text: "This is your chance to be a hero! Imagine you're a ninja, and you're surrounded by imaginary enemy ninjas. You have to use all the strikes you've learned to defend yourself. Start in your Ready Stance, eyes open!",
      },
      {
        time: 1439,
        text: 'Ready? Ninja in front of you! Give me a fast one-two punch! Left, right! Good.',
      },
      {
        time: 1447,
        text: 'Another ninja in front! A powerful front kick! Remember that high knee chamber. Awesome!',
      },
      {
        time: 1455,
        text: 'Oh no, a ninja to your left! Turn and give them a sharp left punch! Perfect.',
      },
      {
        time: 1462,
        text: "Now one to your right! Turn and a right punch! You're a true defender.",
      },
      {
        time: 1469,
        text: "One minute in! The ninjas are getting faster! Let's see a Punch-Kick Power Combo! Left punch, right punch, right kick! Go!",
      },
      {
        time: 1479,
        text: 'Excellent! Now the other side. A ninja is coming! Right punch, left punch, left kick! Take them down!',
      },
      {
        time: 1489,
        text: 'Keep moving your feet. A good ninja is always light and bouncy, ready for anything.',
      },
      {
        time: 1497,
        text: 'Another ninja! Your choice! A punch or a kick? Good choice! Very powerful.',
      },
      {
        time: 1505,
        text: "Let's see that one-two punch again. Make it a blur! Fast and sharp.",
      },
      {
        time: 1512,
        text: 'And a big front kick! Aim for that bullseye.',
      },
      {
        time: 1518,
        text: 'Two minutes in! The ninjas are coming from all sides now! Be ready.',
      },
      {
        time: 1525,
        text: 'Ninja on your right! A quick kick! Good.',
      },
      {
        time: 1530,
        text: 'Ninja on your left! A one-two punch!',
      },
      {
        time: 1535,
        text: 'Ninja right in front! A big power combo! Punch, punch, kick!',
      },
      {
        time: 1542,
        text: "You're doing an amazing job. You're a true karate hero.",
      },
      {
        time: 1548,
        text: 'Keep your hands up in your guard position. Protect your face!',
      },
      {
        time: 1554,
        text: "Let's see another powerful front kick. Remember to bring that knee back after you kick.",
      },
      {
        time: 1562,
        text: "Halfway! It's freestyle time! For the next minute, you decide how to fight the ninjas. Use any of the moves we've learned. Go!",
      },
      {
        time: 1572,
        text: 'Show me your best punches and kicks. Be creative!',
      },
      {
        time: 1578,
        text: "Maybe a double kick? Or a triple punch? It's your kata!",
      },
      {
        time: 1585,
        text: 'Keep moving around your space. A good ninja never stands still.',
      },
      {
        time: 1592,
        text: "Let's see a big kiai with your next move! Show me your power!",
      },
      {
        time: 1599,
        text: 'Awesome! The ninjas are scared of you now.',
      },
      {
        time: 1605,
        text: "Keep that energy high. You're doing great.",
      },
      {
        time: 1610,
        text: "Let's see another powerful combo. Make it your own.",
      },
      {
        time: 1616,
        text: "Excellent! You're a true martial artist.",
      },
      {
        time: 1622,
        text: "Last minute! Oh no, the Big Boss Ninja has appeared! He's right in front of you. We need your most powerful moves!",
      },
      {
        time: 1632,
        text: 'Give me a one-two punch! Fast and strong!',
      },
      {
        time: 1637,
        text: 'Now a big front kick! Push him back!',
      },
      {
        time: 1642,
        text: "He's still coming! Give me a full power combo! Punch, punch, kick!",
      },
      {
        time: 1649,
        text: "Thirty seconds left! It's the final battle! Go, go, go!",
      },
      {
        time: 1655,
        text: "Faster, stronger! Use everything you've got!",
      },
      {
        time: 1661,
        text: 'Final fifteen seconds. Give me your best combination of the day!',
      },
      {
        time: 1667,
        text: 'One more big combo! You can do it!',
      },
      {
        time: 1672,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1678,
        text: 'Five, four, three, two, one... YOU DID IT! The Boss Ninja is defeated!',
      },
      {
        time: 1686,
        text: 'That was an incredible workout! You have learned and practiced so many amazing strikes today. You should be so proud of your hard work and focus.',
      },
      {
        time: 1697,
        text: "Let's do a final cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You have trained like a true karate champion today. Amazing job. Skill complete!",
      },
    ],
    timeTotal: 1704,
    equipment: 'None',
  },

  // INTERMEDIATE STRIKES (45 minutes)
  {
    id: 2035,
    name: 'Intermediate Strikes',
    categories: ['Strikes', 'intermediate'],
    sports: ['karate'],
    difficulty: 'intermediate',
    skill: 'Strikes',
    points: 45,
    instructions:
      "Awesome job mastering your basic strikes! Are you ready to level up? This session is where we turn up the power and learn some super cool new moves. We'll practice new punches, learn some powerful kicks, and even start linking them together into awesome combinations, just like a real black belt! Let's get ready to train hard and have fun!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello, karateka! Ready to build some powerful strikes? Our first drill is the most important one: 'Fist of Fury Foundation'! Let's begin in a ready stance.",
      },
      {
        time: 10,
        text: "Before we punch, we have to learn how to make a perfect, safe fist. Hold your right hand out flat, like you're about to give a high-five.",
      },
      {
        time: 19,
        text: 'Now, curl your four fingers down tight into your palm. Really tight!',
      },
      {
        time: 25,
        text: "Good. Now, take your thumb and wrap it over the top of your first two fingers. Your thumb should NOT be inside your fist. That's the secret to a safe fist!",
      },
      {
        time: 36,
        text: "Let's try that again. Open your hand... curl your fingers... and wrap your thumb. Perfect! Now do the same with your left hand.",
      },
      {
        time: 46,
        text: 'Open... curl... and wrap. You now have two perfect karate fists! Awesome.',
      },
      {
        time: 53,
        text: "One minute in! Now, let's learn the straight punch. Start in your ready stance, with both fists at your hips, palms facing up.",
      },
      {
        time: 63,
        text: "We'll start slowly. Push your right fist straight out in front of you. As it gets to the end, I want you to twist it so your palm is facing down. It's a little twist at the very end.",
      },
      {
        time: 76,
        text: "As you punch with your right hand, pull your left hand back to your hip, twisting it palm up. It's like they are working together as a team! Let's try that slow motion again. Punch and pull.",
      },
      {
        time: 89,
        text: 'Excellent! Now the other side. Punch slowly with your left fist, twisting it at the end, and pull your right fist back to your hip. Punch and pull.',
      },
      {
        time: 101,
        text: "You're doing great. This is the most important part of a strong punch.",
      },
      {
        time: 108,
        text: "Let's do five slow and perfect punches with your right hand. Remember the twist and the pull. One...",
      },
      {
        time: 116,
        text: 'Two... good twist. Three... pull that other hand back!',
      },
      {
        time: 122,
        text: 'Four... looking strong. Five! Awesome.',
      },
      {
        time: 128,
        text: 'Now five with your left hand. Slow and perfect. One...',
      },
      {
        time: 135,
        text: 'Two... straight like an arrow. Three... remember to twist.',
      },
      {
        time: 142,
        text: "Four... great form. Five! You're a natural.",
      },
      {
        time: 148,
        text: "Halfway! Now let's try alternating. A slow right punch, then a slow left punch. Find a rhythm.",
      },
      {
        time: 157,
        text: 'Right... then left. Right... then left. As one hand goes out, the other comes back.',
      },
      {
        time: 165,
        text: "This is looking great. You're building that muscle memory.",
      },
      {
        time: 171,
        text: "Now, let's pick up the speed just a little bit. Not too fast, just a little sharper. Right, left, right, left.",
      },
      {
        time: 180,
        text: "Good! Remember to breathe out with every punch. It makes you stronger. A little 'kiai' shout!",
      },
      {
        time: 189,
        text: "Let's try it. A sharp punch with a little 'ha!' sound. Go!",
      },
      {
        time: 196,
        text: 'Awesome! You sound like a true warrior. Keep that going.',
      },
      {
        time: 202,
        text: "Let's see ten more sharp punches, alternating hands. Count them out!",
      },
      {
        time: 209,
        text: 'Keep that form perfect. A straight punch is a strong punch.',
      },
      {
        time: 215,
        text: "You're doing great. Keep that energy high.",
      },
      {
        time: 220,
        text: "Let's go back to slow motion for a second. Let's check our fists. Are they still perfect?",
      },
      {
        time: 228,
        text: 'Fingers curled, thumb on top. Good. Now back to sharp punches.',
      },
      {
        time: 234,
        text: "Prepare for the final minute. We're going to do a speed challenge.",
      },
      {
        time: 240,
        text: "Last minute! Let's see how many good, sharp punches you can do in 30 seconds. I'll count. Ready? Go!",
      },
      {
        time: 270,
        text: 'Awesome job! Now for the final 30 seconds, I want to see your most powerful punches. Put your whole body into it, but keep that form perfect. Go!',
      },
      {
        time: 280,
        text: "Strong punches! You're a powerhouse!",
      },
      {
        time: 285,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 290,
        text: 'Five, four, three, two, one... and TIME! Excellent work.',
      },
      {
        time: 297,
        text: "That was a fantastic first exercise! Now it's time to level up our kicks with 'The Triple Kick Challenge'!",
      },
      {
        time: 305,
        text: "First, we need to learn a new kick: the roundhouse kick, or 'Mawashi-geri'. It's a super powerful circular kick. Let's start in our Ready Stance.",
      },
      {
        time: 315,
        text: 'Just like our front kick, the first step is the chamber. Bring your right knee up, but this time, point it to the side, across your body.',
      },
      {
        time: 325,
        text: "Now, pivot on your standing foot and snap your kicking leg out in a circle, like you're kicking around a corner. Then bring it right back. Let's try that slowly. Chamber, pivot, kick!",
      },
      {
        time: 337,
        text: "Awesome! Let's see five slow and perfect roundhouse kicks with your right leg. Remember to pivot on your other foot!",
      },
      {
        time: 345,
        text: "Now five with your left leg. Chamber across your body, pivot, and snap that kick! You're a powerhouse.",
      },
      {
        time: 353,
        text: "One minute in! Now for our second new kick: the side kick, or 'Yoko-geri'. This is a strong, pushing kick.",
      },
      {
        time: 362,
        text: 'From Ready Stance, bring your right knee up and across your body, but keep your foot tucked in. This is the chamber.',
      },
      {
        time: 371,
        text: "Now, pivot on your standing foot and push your kicking foot straight out to the side, leading with your heel. Then bring it right back. Let's see it. Chamber, pivot, push!",
      },
      {
        time: 383,
        text: "Excellent! Let's see five strong side kicks with your right leg. Push that imaginary door down!",
      },
      {
        time: 391,
        text: "Now five with your left leg. Remember to lead with your heel. That's where the power is.",
      },
      {
        time: 399,
        text: "Two minutes in! Now for the Triple Kick Challenge! We're going to do all three kicks without putting our foot down. It's a big balance test!",
      },
      {
        time: 410,
        text: "Let's try it slowly with your right leg. Lift your knee... do a front kick... bring it back to chamber...",
      },
      {
        time: 418,
        text: 'Now, from that chamber, pivot and do a roundhouse kick... bring it back to chamber...',
      },
      {
        time: 425,
        text: 'And from that chamber, pivot and do a side kick... and then back down! Awesome!',
      },
      {
        time: 433,
        text: "That is a superstar combo! Let's try it on the left leg now. Slowly. Front kick... roundhouse kick... side kick... and down.",
      },
      {
        time: 444,
        text: "You're doing great. This is one of the toughest challenges in karate. Keep your balance.",
      },
      {
        time: 451,
        text: "Halfway! Let's focus on your standing leg. It has to be strong and slightly bent, like a tree trunk.",
      },
      {
        time: 460,
        text: 'Use your arms to help you balance. Hold them out like airplane wings.',
      },
      {
        time: 467,
        text: "Let's try the triple kick combo again on your right leg. Find a spot on the wall to stare at to help your balance.",
      },
      {
        time: 476,
        text: 'Front... roundhouse... side... and down. Excellent!',
      },
      {
        time: 483,
        text: "Now the left leg. Remember to bring your knee back to the chamber position after each kick. That's the secret.",
      },
      {
        time: 492,
        text: "You're a balance master! Keep that focus.",
      },
      {
        time: 498,
        text: "Let's see one more perfect combo on each side. Make it your best one yet.",
      },
      {
        time: 505,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 509,
        text: "Last minute! Let's see how many good, clean kicks you can do in 30 seconds. Just front kicks, alternating legs. Go!",
      },
      {
        time: 540,
        text: 'Awesome! Now, for the final 30 seconds, just roundhouse kicks, alternating legs. Go!',
      },
      {
        time: 550,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 555,
        text: 'Five, four, three, two, one... and TIME! Excellent kicks.',
      },
      {
        time: 562,
        text: "That was an amazing challenge! Now it's time to add some footwork to our strikes with 'Sensei's Strike & Move'!",
      },
      {
        time: 570,
        text: "A true karate master is always moving. We're going to practice stepping forward and backward while we strike. Start in a strong Ready Stance...feet shoulder width apart, knees slightly bent, hands at your hips.",
      },
      {
        time: 579,
        text: "First, the forward strike. On my call, you're going to step forward with your left foot into a Front Stance and land a powerful right punch at the exact same time. Ready? Go!",
      },
      {
        time: 591,
        text: "Excellent! Your foot and hand moved as a team. Now, step back to Ready. Let's do the other side. Step forward with your right foot and punch with your left. Go!",
      },
      {
        time: 602,
        text: 'Awesome! Now for the backward movement. From Ready Stance, slide your left foot back into a Left Back Stance and do a strong block with your right arm. Go!',
      },
      {
        time: 613,
        text: "Perfect! You're defending as you move back. Now back to Ready. Let's do the other side. Slide your right foot back into a Right Back Stance and block with your left. Go!",
      },
      {
        time: 624,
        text: "One minute in! Your movements are looking sharp. Let's get a rhythm going. Forward punch, back to ready. Then backward block, back to ready. Let's start with the left side.",
      },
      {
        time: 636,
        text: 'Step forward with your left foot and right punch! Good. Back to Ready. Now, step back with your left foot and right block! Good. Back to Ready.',
      },
      {
        time: 648,
        text: 'Now the right side! Step forward with your right foot and left punch! Good. Back to Ready. Now, step back with your right foot and left block! Good. Back to Ready.',
      },
      {
        time: 662,
        text: "You're learning to attack and defend while moving. This is a huge step!",
      },
      {
        time: 669,
        text: "Let's see that left side combo again. Forward punch... and backward block. Excellent.",
      },
      {
        time: 677,
        text: "And the right side combo. Forward punch... and backward block. You're a true warrior.",
      },
      {
        time: 685,
        text: "Two minutes in! Let's link the movements together without stopping in Ready Stance.",
      },
      {
        time: 693,
        text: 'Start in a Left Front Stance with a punch. Now, from there, slide straight back into a Left Back Stance with a block. A continuous flow.',
      },
      {
        time: 704,
        text: 'Good! Now slide forward again into your Left Front Stance with a punch. Forward and back, forward and back. Keep it going.',
      },
      {
        time: 714,
        text: "Awesome! Let's switch to the right side. Start in a Right Front Stance with a punch. Now, slide back and forth between your Right Front and Right Back stance.",
      },
      {
        time: 726,
        text: "This is how you control the distance in a real match. You're doing great.",
      },
      {
        time: 733,
        text: "Halfway! Let's pick up the pace. A little faster on your slides, a little sharper on your strikes.",
      },
      {
        time: 741,
        text: "Let's see that left side flow again, but with more speed. Forward punch, backward block. Go, go, go!",
      },
      {
        time: 750,
        text: "Now the right side, just as fast! Don't lose your form, even when you're moving quickly.",
      },
      {
        time: 759,
        text: 'Remember to keep your stances strong. A bent front knee in Front Stance, and your weight on your back leg in Back Stance.',
      },
      {
        time: 769,
        text: "Let's see another fast one. Make it your best one yet. Sharp and powerful.",
      },
      {
        time: 776,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 782,
        text: "Let's do the left side combo again. Focus on your balance.",
      },
      {
        time: 788,
        text: 'And the right side combo. Stay steady like a rock.',
      },
      {
        time: 794,
        text: "You're doing great. This is a very advanced drill.",
      },
      {
        time: 800,
        text: 'Prepare for the final minute.',
      },
      {
        time: 804,
        text: "Last minute! Freestyle! Move around your space. I'll call out a move, and you do it. Ready? Move around... FORWARD PUNCH!",
      },
      {
        time: 814,
        text: 'Good! Keep moving... BACKWARD BLOCK!',
      },
      {
        time: 820,
        text: 'Excellent! Keep moving... FORWARD PUNCH, OTHER SIDE!',
      },
      {
        time: 827,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 833,
        text: 'Keep moving... BACKWARD BLOCK, OTHER SIDE!',
      },
      {
        time: 839,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 845,
        text: 'One more perfect combo!',
      },
      {
        time: 849,
        text: 'Five, four, three, two, one... and TIME! Excellent movement.',
      },
      {
        time: 856,
        text: "That was some amazing footwork! Now it's time to unleash your power with a close-range move in our next drill: 'The Elbow Powerhouse'!",
      },
      {
        time: 865,
        text: "This is a super strong strike for when you're up close. Let's learn the elbow strike first. Start in your Ready Stance. Bring your right hand up to your chest, and keep your other hand guarding your face.",
      },
      {
        time: 876,
        text: "Now, drive that right elbow straight across in front of you, like you're a powerful train. And bring it right back. Let's see it again. A sharp elbow strike!",
      },
      {
        time: 887,
        text: 'Awesome! Now the other side. Left hand to your chest, and drive that left elbow across. Keep it flat and strong.',
      },
      {
        time: 896,
        text: "Now, let's add our power stance: the Horse Stance. From Ready, step out wide and sink down low. At the same time you land, I want you to do that right elbow strike. Go!",
      },
      {
        time: 908,
        text: 'Excellent! Your whole body moved as one. Back to Ready. Now the other side. Step into Horse Stance and a left elbow strike! Go!',
      },
      {
        time: 918,
        text: 'One minute in! Your strikes are looking powerful. The secret to a strong elbow strike is using your whole body. Twist your hips and shoulders as you strike.',
      },
      {
        time: 929,
        text: "Let's try that. As you step into Horse Stance, twist your body into the elbow strike. Feel that power!",
      },
      {
        time: 938,
        text: "Let's see five powerful elbow strikes on each side. Remember to twist! Right side first.",
      },
      {
        time: 946,
        text: 'Now the left side. Just as strong, just as sharp.',
      },
      {
        time: 952,
        text: "You're doing great. This is a very powerful move.",
      },
      {
        time: 958,
        text: "Let's see another one. Make it your best one yet. Strong and fast!",
      },
      {
        time: 964,
        text: "Excellent! You're looking like a true karateka.",
      },
      {
        time: 969,
        text: "Two minutes in! Let's make it a combo. We're going to do two quick elbow strikes in our Horse Stance.",
      },
      {
        time: 978,
        text: 'Step into your Horse Stance and give me a right elbow, then a left elbow. A quick one-two combo!',
      },
      {
        time: 987,
        text: "Good! Back to Ready. Let's do it again. Horse Stance, right elbow, left elbow! Awesome speed.",
      },
      {
        time: 996,
        text: 'Remember to keep your other hand up to guard your face. Always protect yourself.',
      },
      {
        time: 1003,
        text: "Let's see that combo again. Make it sharp and powerful.",
      },
      {
        time: 1009,
        text: "You're a true warrior. Keep it up.",
      },
      {
        time: 1014,
        text: "Halfway! Let's focus on your breathing. Remember to breathe out with a sharp 'kiai' on every strike.",
      },
      {
        time: 1023,
        text: "It makes your moves even stronger. Let's hear that power! Horse Stance, right elbow, left elbow... KIAI!",
      },
      {
        time: 1033,
        text: "Awesome! That was so powerful. Let's do it again.",
      },
      {
        time: 1039,
        text: 'Keep that focus. Every strike should be your best one.',
      },
      {
        time: 1045,
        text: "Let's see another powerful combo. Show me that energy.",
      },
      {
        time: 1051,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 1057,
        text: "Let's do the combo again. Focus on your strong Horse Stance. Sink low.",
      },
      {
        time: 1064,
        text: 'The lower you are, the more power you have. Stay strong.',
      },
      {
        time: 1070,
        text: "You're doing great. This is a very advanced drill.",
      },
      {
        time: 1076,
        text: 'Prepare for the final minute.',
      },
      {
        time: 1080,
        text: "Last minute! Let's see your most powerful elbow strikes. I'm going to call them out. Be ready! HORSE STANCE, RIGHT ELBOW!",
      },
      {
        time: 1089,
        text: 'Good! Back to Ready. HORSE STANCE, LEFT ELBOW!',
      },
      {
        time: 1095,
        text: 'Excellent! Back to Ready. HORSE STANCE, DOUBLE ELBOW COMBO!',
      },
      {
        time: 1102,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1108,
        text: 'Faster, stronger, sharper! Come on!',
      },
      {
        time: 1113,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1119,
        text: 'One more perfect combo!',
      },
      {
        time: 1123,
        text: 'Five, four, three, two, one... and TIME! Excellent power.',
      },
      {
        time: 1130,
        text: "That was some amazing strength! Now it's time to link everything together in a super cool, snake-like combination I call 'The Cobra Combo'!",
      },
      {
        time: 1140,
        text: "This is a four-part combo that is super fast and powerful. We'll learn it step-by-step. Start in your Ready Stance.",
      },
      {
        time: 1148,
        text: "First is a quick jab with your left hand. Just a fast, straight punch. Jab! Good. Now, a powerful reverse punch with your right hand. Jab, reverse punch! Let's see it.",
      },
      {
        time: 1159,
        text: "Awesome! Right after that reverse punch, you're going to throw a big right roundhouse kick. Remember, that's the one where you pivot and snap your leg in a circle.",
      },
      {
        time: 1170,
        text: "And the final move is a sharp right elbow strike as you land from your kick. It's a big, powerful finish!",
      },
      {
        time: 1179,
        text: "So the full combo is: Left jab, right reverse punch, right roundhouse kick, and a right elbow strike! Let's try it very slowly. Go!",
      },
      {
        time: 1190,
        text: "Excellent! That's a lot of moves, so let's break it down again. Just the punches first. Left jab, right reverse punch. Ten times, nice and sharp.",
      },
      {
        time: 1200,
        text: 'One minute in! Now, just the kicks. A big right roundhouse kick. Remember to pivot on your standing foot.',
      },
      {
        time: 1209,
        text: "Good. Now let's practice the end of the combo. A right roundhouse kick that flows right into a right elbow strike. Kick and strike!",
      },
      {
        time: 1219,
        text: "Awesome! Now let's put the whole Cobra Combo together again, slowly. Left jab... right reverse punch... right roundhouse kick... right elbow strike.",
      },
      {
        time: 1232,
        text: "You're doing great. This is a very advanced combination.",
      },
      {
        time: 1238,
        text: "Let's try the whole combo on the other side now! A right jab, a left reverse punch, a left roundhouse kick, and a left elbow strike. Go!",
      },
      {
        time: 1250,
        text: "You're a two-sided warrior! Keep practicing that.",
      },
      {
        time: 1256,
        text: "Two minutes in! Let's pick up the speed a little bit. A little sharper, a little faster on the right side.",
      },
      {
        time: 1264,
        text: 'Jab, punch, kick, elbow! Good. Now the left side, just as fast.',
      },
      {
        time: 1272,
        text: 'Remember your kiai on that final elbow strike! Show me your power.',
      },
      {
        time: 1279,
        text: "Let's hear it! A sharp combo with a loud kiai! Go!",
      },
      {
        time: 1286,
        text: 'Awesome! You sound like a true champion. Keep that energy.',
      },
      {
        time: 1292,
        text: "Let's see another fast combo. Make it a blur!",
      },
      {
        time: 1298,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1303,
        text: "Halfway! Let's slow it down again. Focus on perfect form. A slow, beautiful combo.",
      },
      {
        time: 1311,
        text: "Remember that high knee chamber on your roundhouse kick. It's the secret to power.",
      },
      {
        time: 1318,
        text: 'And remember to twist your hips on your punches and your elbow strike.',
      },
      {
        time: 1325,
        text: "Let's do another slow one. Feel every muscle working together.",
      },
      {
        time: 1332,
        text: 'Excellent. Now, back to game speed. Sharp and fast!',
      },
      {
        time: 1338,
        text: 'Imagine a target in front of you. Every strike in your combo should hit that same bullseye.',
      },
      {
        time: 1346,
        text: "Let's see that accuracy. Jab, punch, kick, elbow, all on target.",
      },
      {
        time: 1353,
        text: "You're a striking machine! Keep that energy.",
      },
      {
        time: 1359,
        text: "Let's see another combo. Make it your best one yet.",
      },
      {
        time: 1365,
        text: "Keep that focus. You're almost there.",
      },
      {
        time: 1370,
        text: 'Prepare for the final minute. This is the power round!',
      },
      {
        time: 1375,
        text: "Last minute! I want to see your most powerful Cobra Combo. Imagine you're breaking a board! Ready? Go!",
      },
      {
        time: 1384,
        text: 'Awesome power! Now the other side! Just as strong.',
      },
      {
        time: 1390,
        text: "Don't forget your kiai! A big 'ha!' with every combo!",
      },
      {
        time: 1397,
        text: "Thirty seconds left! Let's see how many power combos you can do. Go, go, go!",
      },
      {
        time: 1405,
        text: 'Faster, stronger! Come on!',
      },
      {
        time: 1411,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1417,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1423,
        text: 'Five, four, three, two, one... and TIME! Excellent combos. Take a deep breath and get ready for the next drill.',
      },
      {
        time: 1430,
        text: "For our final and most exciting drill, it's time for the 'Advanced Ninja Shadow Battle'!",
      },
      {
        time: 1439,
        text: "This is your chance to be a hero! Imagine you're a ninja, and you're surrounded by imaginary enemy ninjas. You have to use all the strikes you've learned to defend yourself. Start in your Ready Stance, eyes open!",
      },
      {
        time: 1452,
        text: "Ready? Two ninjas in front! Give me a fast 'Fist of Fury Combo'! That's a left jab, a right reverse punch, and a left hook! Go!",
      },
      {
        time: 1463,
        text: "Awesome! Another ninja is rushing you! Give me a 'Triple Kick Challenge'! That's a right front kick, a right roundhouse kick, and a right side kick, all without putting your foot down!",
      },
      {
        time: 1476,
        text: 'Incredible balance! Now, a ninja to your left! Step and give them a sharp elbow strike! Remember to use your hips for power.',
      },
      {
        time: 1485,
        text: "Now one to your right! Step into a horse stance and another powerful elbow strike! You're a true defender.",
      },
      {
        time: 1494,
        text: "One minute in! The ninjas are getting faster! Let's see a full 'Cobra Combo'! Remember, that's jab, reverse punch, roundhouse kick, and elbow strike! Go!",
      },
      {
        time: 1506,
        text: "Excellent! Now the other side. A ninja is coming! A 'Cobra Combo' with your left leg kicking! Take them down!",
      },
      {
        time: 1516,
        text: 'Keep moving your feet. A good ninja is always light and bouncy, ready for anything.',
      },
      {
        time: 1524,
        text: 'Another ninja! Your choice! Show me a powerful kick. A roundhouse or a side kick?',
      },
      {
        time: 1532,
        text: 'Good choice! Very powerful.',
      },
      {
        time: 1537,
        text: "Let's see that 'Fist of Fury Combo' again. Make it a blur! Fast and sharp.",
      },
      {
        time: 1545,
        text: "And a big 'Triple Kick Challenge'! Front kick, roundhouse, side kick! Balance is key.",
      },
      {
        time: 1554,
        text: 'Two minutes in! The ninjas are coming from all sides now! Be ready.',
      },
      {
        time: 1561,
        text: 'Ninja on your right! A quick roundhouse kick! Remember to pivot.',
      },
      {
        time: 1567,
        text: "Ninja on your left! A 'Fist of Fury Combo'!",
      },
      {
        time: 1573,
        text: "Ninja right in front! A big 'Cobra Combo'! Punch, punch, kick, elbow!",
      },
      {
        time: 1581,
        text: "You're doing an amazing job. You're a true karate hero.",
      },
      {
        time: 1587,
        text: 'Keep your hands up in your guard position. Protect your face!',
      },
      {
        time: 1593,
        text: "Let's see another powerful kick. A big side kick! Push them away.",
      },
      {
        time: 1600,
        text: "Halfway! It's freestyle time! For the next minute, you decide how to fight the ninjas. Use any of the moves we've learned. Go!",
      },
      {
        time: 1610,
        text: 'Show me your best punches and kicks. Be creative!',
      },
      {
        time: 1616,
        text: "Maybe a double kick? Or a triple punch combo? It's your kata!",
      },
      {
        time: 1623,
        text: 'Keep moving around your space. A good ninja never stands still.',
      },
      {
        time: 1630,
        text: "Let's see a big kiai with your next move! Show me your power!",
      },
      {
        time: 1637,
        text: 'Awesome! The ninjas are scared of you now.',
      },
      {
        time: 1643,
        text: "Keep that energy high. You're doing great.",
      },
      {
        time: 1648,
        text: "Let's see another powerful combo. Make it your own.",
      },
      {
        time: 1654,
        text: "Excellent! You're a true martial artist.",
      },
      {
        time: 1660,
        text: "Last minute! Oh no, the Big Boss Ninja has appeared! He's right in front of you. We need your most powerful moves!",
      },
      {
        time: 1670,
        text: "Give me a 'Fist of Fury Combo'! Fast and strong!",
      },
      {
        time: 1676,
        text: "He's still coming! A 'Triple Kick Challenge'! Don't let your foot touch the ground!",
      },
      {
        time: 1684,
        text: "He's still coming! Give me a full 'Cobra Combo'! Punch, punch, kick, elbow!",
      },
      {
        time: 1692,
        text: "Thirty seconds left! It's the final battle! Go, go, go!",
      },
      {
        time: 1698,
        text: "Faster, stronger! Use everything you've got!",
      },
      {
        time: 1704,
        text: 'Final fifteen seconds. Give me your best combination of the day!',
      },
      {
        time: 1710,
        text: 'One more big combo! You can do it!',
      },
      {
        time: 1715,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1721,
        text: 'Five, four, three, two, one... YOU DID IT! The Boss Ninja is defeated!',
      },
      {
        time: 1729,
        text: 'That was an incredible workout! You have learned and practiced so many amazing strikes today. You should be so proud of your hard work and focus.',
      },
      {
        time: 1740,
        text: "Let's do a final cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You have trained like a true karate champion today. Amazing job. Skill complete!",
      },
    ],
    timeTotal: 1746,
    equipment: 'None',
  },

  // ADVANCED STRIKES (45 minutes)
  {
    id: 2036,
    name: 'Advanced Strikes',
    categories: ['Strikes', 'advanced'],
    sports: ['karate'],
    difficulty: 'advanced',
    skill: 'Strikes',
    points: 45,
    instructions:
      'Advanced Strikes teaches you fancy punching and kicking moves. This 45-minute session helps you practice cool striking moves, fun combinations, and tricky coordination exercises.',
    timedInstructions: [
      {
        time: 0,
        text: "Hello, warrior! Get ready to unleash your power in this advanced striking drill. Let's start with a high-energy warm-up I call 'Lightning Chain Combos'! Start in your ready stance. For five minutes, we're going to be a blur of motion. Let's go!",
      },
      {
        time: 12,
        text: "Let's start with our Fist of Fury combo! First is the left jab. That's a super fast, straight punch with your front hand. Snap it out and back! JAB!",
      },
      {
        time: 25,
        text: 'Awesome! Next is the right reverse punch. This is your power shot! Twist your body and punch straight with your back hand. REVERSE PUNCH!',
      },
      {
        time: 37,
        text: "Great! The last move is the left hook. Bring your front elbow up and swing your fist in a circle, like you're going around a corner. HOOK!",
      },
      {
        time: 49,
        text: "Now let's chain them all together! Ready? Jab... reverse punch... hook! Let's see the full Fist of Fury combo!",
      },
      {
        time: 59,
        text: "Awesome! Now the other side. A right jab which is a super fast front punch, a left reverse punch where you twist your body and punch straight with your back hand, and a right hook where you bring your front elbow up and swing your fist in a circle, like you're going around a corner! Remember to twist your hips for power.",
      },
      {
        time: 69,
        text: "Now, let's chain it. Do the left combo, then immediately the right combo. It's a non-stop chain of punches! Go!",
      },
      {
        time: 80,
        text: "Keep those hands up! Protect your face, even when you're attacking.",
      },
      {
        time: 87,
        text: "One minute in! Your punches are looking sharp. Now let's add a kick. After your left-side Fist of Fury combo, add a powerful right roundhouse kick.",
      },
      {
        time: 99,
        text: "So it's: Jab which is a super fast, straight punch with your front hand, reverse punch where you twist your body and punch straight with your back hand, hook where you bring your front elbow up and swing your fist in a circle, like you're going around a corner, and a big roundhouse kick! Remember to pivot on your standing foot for that kick. Let's see it.",
      },
      {
        time: 110,
        text: 'Excellent! Now the other side. Right-side Fist of Fury combo, jab, reverse, hook, and finish with a powerful left roundhouse kick. A roundhouse kick is a powerful kick where you swing your leg in a circular motion with your foot. Go!',
      },
      {
        time: 121,
        text: "You're a striking machine! Keep that flow going. The last punch should flow right into the chamber for your kick.",
      },
      {
        time: 131,
        text: "Let's see that smooth transition now. Punch, punch, punch, KICK! Awesome.",
      },
      {
        time: 140,
        text: 'Keep alternating sides. This is a full-body workout.',
      },
      {
        time: 146,
        text: "Two minutes in! Let's make the combo even longer. We're going to add the Triple Kick Challenge at the end.",
      },
      {
        time: 155,
        text: "So, it's a left-side Fist of Fury combo... then, without putting your foot down, a right front kick, a right roundhouse kick, and a right side kick! It's a huge challenge!",
      },
      {
        time: 170,
        text: "Let's try it slowly. Jab, reverse, hook... now balance... front kick, roundhouse kick, side kick! You did it!",
      },
      {
        time: 182,
        text: 'Now the other side! Right-side Fist of Fury combo, followed by the left leg Triple Kick Challenge. Go for it!',
      },
      {
        time: 193,
        text: "This is a true test of your balance and coordination. You're doing great.",
      },
      {
        time: 200,
        text: "Halfway! Let's simplify and go for speed. Just the Fist of Fury combo into a single roundhouse kick. But make it lightning fast!",
      },
      {
        time: 211,
        text: 'Left combo and right kick! GO! Right combo and left kick! GO!',
      },
      {
        time: 219,
        text: 'Faster, faster! The punches should be a blur, and the kick should be a thunderclap.',
      },
      {
        time: 227,
        text: 'Remember your kiai! Let me hear that power on the kick!',
      },
      {
        time: 233,
        text: "Let's see another one. Make it your fastest one yet. Sharp and powerful.",
      },
      {
        time: 240,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 246,
        text: "Let's go back to the Triple Kick Challenge combo. Left side punches, right side kicks. Front, roundhouse, side. Go!",
      },
      {
        time: 256,
        text: 'Awesome! Now the other side. Right side punches, left side kicks.',
      },
      {
        time: 263,
        text: 'Prepare for the final minute.',
      },
      {
        time: 266,
        text: "Last minute! Freestyle combos! Mix any of the punches and kicks we've learned. Create your own lightning chain!",
      },
      {
        time: 275,
        text: 'Maybe a double jab into a side kick? Or a hook into a roundhouse? Be creative!',
      },
      {
        time: 283,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 289,
        text: 'Faster, stronger, sharper! Come on!',
      },
      {
        time: 294,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 300,
        text: 'One more perfect combo!',
      },
      {
        time: 304,
        text: 'Five, four, three, two, one... and TIME! Excellent combinations.',
      },
      {
        time: 311,
        text: "That was an amazing warm-up! Now it's time to learn a true superstar move, the spinning back kick! I call this drill 'Tornado Kick Training'!",
      },
      {
        time: 321,
        text: "This is a very powerful and tricky kick, so we'll learn it step-by-step. Let's start in our Ready Stance. Hands up.",
      },
      {
        time: 329,
        text: "First is the turn. Without kicking, I want you to turn your head and body backwards over your right shoulder, like you're looking for someone behind you. Try to spot an imaginary target.",
      },
      {
        time: 340,
        text: "Good! Now the other way. Turn your head and body backwards over your left shoulder. This look is the secret to a good spinning kick. Let's do that again. Look right... and look left.",
      },
      {
        time: 353,
        text: "Awesome! Now, let's add the chamber. As you look over your right shoulder, lift your kicking knee, your right knee, up towards your chest. Keep it tucked in tight.",
      },
      {
        time: 365,
        text: "Let's try that again. Look over your right shoulder and bring that right knee up. Good! Now the other side. Look over your left shoulder and bring your left knee up.",
      },
      {
        time: 377,
        text: "One minute in! Now for the 'tornado' part! After you look and lift your knee, you're going to unleash the kick straight behind you, leading with your heel.",
      },
      {
        time: 388,
        text: "Let's try it slowly with the right leg. Look over your right shoulder, lift your right knee, and then push that kick straight back. Then bring it back and down. Awesome!",
      },
      {
        time: 400,
        text: "Now the left leg. Look over your left shoulder, lift your left knee, and kick straight back with your heel. You're a powerhouse!",
      },
      {
        time: 411,
        text: "Let's see five slow and perfect Tornado Kicks with your right leg. Remember: Look, chamber, kick!",
      },
      {
        time: 420,
        text: "Now five with your left leg. This is a tricky kick, but you're doing great. Keep your balance.",
      },
      {
        time: 428,
        text: 'Use your arms to help you balance during the spin. Keep them in tight, then let them flow.',
      },
      {
        time: 435,
        text: "Two minutes in! Your kicks are looking strong. Let's focus on the target. Imagine that bullseye is right behind you.",
      },
      {
        time: 444,
        text: "You have to spot the target as you turn. Look, then kick the target. Let's see it.",
      },
      {
        time: 452,
        text: 'Excellent accuracy! The faster you spot the target, the faster your kick will be.',
      },
      {
        time: 460,
        text: "Let's see another one. A quick look, and a powerful kick right on the bullseye.",
      },
      {
        time: 467,
        text: "You're doing great. This is a very advanced skill.",
      },
      {
        time: 473,
        text: "Halfway! Let's pick up the speed a little bit. A little sharper, a little faster.",
      },
      {
        time: 481,
        text: "Let's see a sharp Tornado Kick with your right leg. Good! Now one with your left.",
      },
      {
        time: 489,
        text: "Let's try alternating now. A right kick, then a left kick. Find a good rhythm.",
      },
      {
        time: 497,
        text: 'Right... left... right... left. Keep those hands up in your guard position!',
      },
      {
        time: 504,
        text: "Don't forget to bring that knee back to the chamber after you kick. It's super important for your balance.",
      },
      {
        time: 512,
        text: "Let's see ten in a row, alternating. Count them out!",
      },
      {
        time: 518,
        text: "You're a kicking machine! Keep that energy.",
      },
      {
        time: 524,
        text: "Let's go back to slow motion for a second. Let's check our form. Look, high knee chamber, powerful kick with the heel, re-chamber, and down. Perfect.",
      },
      {
        time: 536,
        text: 'Now back to game speed. Sharp and fast!',
      },
      {
        time: 542,
        text: 'Prepare for the final minute. This is the power round!',
      },
      {
        time: 547,
        text: "Last minute! I want to see your most powerful kicks. Imagine you're a tornado! Ready? Right leg, go!",
      },
      {
        time: 556,
        text: 'Awesome power! Now left leg! Just as strong.',
      },
      {
        time: 562,
        text: "Don't forget your kiai! A big 'ha!' with every kick!",
      },
      {
        time: 569,
        text: "Thirty seconds left! Let's see how many power kicks you can do. Go, go, go!",
      },
      {
        time: 577,
        text: 'Faster, stronger! Come on!',
      },
      {
        time: 583,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 589,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 595,
        text: 'Five, four, three, two, one... and TIME! Excellent kicks. Take a breather.',
      },
      {
        time: 608,
        text: "Now it's time to sharpen our hands into weapons with a super cool drill called 'Viper Hand Strikes!",
      },
      {
        time: 611,
        text: "We're moving beyond punches to more precise strikes. First, the spear-hand! Hold your right hand out, but keep all your fingers perfectly straight and squeezed together, like a spear. Tuck your thumb in tight.",
      },
      {
        time: 623,
        text: "From your Ready Stance, I want you to do a straight, stabbing strike with your spear-hand, aiming for a tiny imaginary target. It's a fast, snappy move. Let's see it!",
      },
      {
        time: 634,
        text: 'Awesome! Now the left hand. Make that perfect spear-hand, fingers together, and a sharp strike forward. Remember to pull your other hand back to your hip.',
      },
      {
        time: 645,
        text: "Let's see five sharp spear-hand strikes with your right hand. One, two, three, four, five! Good.",
      },
      {
        time: 653,
        text: "Now five with your left. Keep those fingers tight! That's what makes the spear strong.",
      },
      {
        time: 660,
        text: "One minute in! Now for a new strike: the ridge-hand. It's like a hook punch, but with an open hand. Make a fist, then open your four fingers, keeping them straight and together. Tuck your thumb underneath.",
      },
      {
        time: 674,
        text: "You'll be striking with the side of your hand, by your index finger. From your Ready Stance, swing your right arm in a circle, like a hook. Let's see that ridge-hand strike.",
      },
      {
        time: 686,
        text: 'Excellent! Now the left hand. Remember to tuck that thumb! A sharp, circular ridge-hand strike.',
      },
      {
        time: 695,
        text: "Let's see five ridge-hand strikes with your right hand. Keep that circular motion.",
      },
      {
        time: 702,
        text: "Now five with your left. You're a true viper!",
      },
      {
        time: 708,
        text: "Two minutes in! Let's combine them. We'll do a spear-hand, then a ridge-hand with the same hand.",
      },
      {
        time: 716,
        text: "With your right hand: a straight spear-hand strike... bring it back... and a circular ridge-hand strike. It's a one-two combo!",
      },
      {
        time: 726,
        text: 'Awesome! Now the left hand. A sharp spear-hand... and a powerful ridge-hand.',
      },
      {
        time: 734,
        text: 'This is a fantastic combination of a straight strike and a circular strike. Keep practicing that.',
      },
      {
        time: 742,
        text: "Let's see three good combos on each side. Right hand first. Spear, ridge!",
      },
      {
        time: 749,
        text: 'Now the left hand. Spear, ridge! Excellent.',
      },
      {
        time: 755,
        text: "Halfway! Let's add our stances to make these strikes even more powerful.",
      },
      {
        time: 762,
        text: 'Step forward into a Left Front Stance and do a right spear-hand strike. Use your whole body!',
      },
      {
        time: 771,
        text: 'Good! Now, from that same stance, do a left ridge-hand strike. A powerful combo.',
      },
      {
        time: 779,
        text: "Let's try the other side. Step into a Right Front Stance and do a left spear-hand.",
      },
      {
        time: 787,
        text: "And from there, a right ridge-hand. You're a true powerhouse.",
      },
      {
        time: 794,
        text: "Let's practice that again. Left Front Stance, right spear, left ridge. Back to ready.",
      },
      {
        time: 803,
        text: 'Now the other side. Right Front Stance, left spear, right ridge. Back to ready.',
      },
      {
        time: 812,
        text: "You're not just using your arms; you're using your strong stances to generate power.",
      },
      {
        time: 820,
        text: "Let's see another one. Make it your best one yet. Sharp and powerful.",
      },
      {
        time: 827,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 833,
        text: 'Prepare for the final minute.',
      },
      {
        time: 837,
        text: 'Last minute! Freestyle! Mix your spear-hands and ridge-hands. Use any stance you want. Be a creative viper!',
      },
      {
        time: 847,
        text: 'Show me a combo! Maybe a spear-hand, another spear-hand, and then a big ridge-hand!',
      },
      {
        time: 855,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 861,
        text: 'Faster, stronger, sharper! Come on!',
      },
      {
        time: 866,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 872,
        text: 'One more perfect combo!',
      },
      {
        time: 876,
        text: 'Five, four, three, two, one... and TIME! Excellent strikes.',
      },
      {
        time: 883,
        text: "That was some amazing hand speed! Now it's time to learn how to defend and attack at the same time with a super smart drill called 'The Cobra's Counter'!",
      },
      {
        time: 893,
        text: "A true master doesn't just attack; they react! Imagine an enemy ninja is throwing a slow punch right at your face. First, we evade! Lean your head back just a little to dodge the punch.",
      },
      {
        time: 904,
        text: "As soon as you dodge, you're going to explode back with a counter-attack! Let's try a one-two punch. So it's: Dodge, then a fast left jab and a right reverse punch! Go!",
      },
      {
        time: 916,
        text: "Awesome! Let's try that again. Imagine the punch is coming... DODGE... and COUNTER! Jab, reverse punch! You're a cobra striking back.",
      },
      {
        time: 926,
        text: "Now, let's try a different counter. Imagine the punch is coming... dodge back... and this time, counter with a powerful right roundhouse kick! Remember to pivot!",
      },
      {
        time: 938,
        text: "Excellent! You dodged their attack and landed your own. This is a very advanced skill. Let's practice that again. Dodge, and a big roundhouse kick!",
      },
      {
        time: 948,
        text: "One minute in! Let's try a different defense. This time, instead of dodging, we'll block. The punch is coming... use a strong rising block to protect your head.",
      },
      {
        time: 959,
        text: "Immediately after you block, counter with a 'Fist of Fury' combo! That's a jab, a reverse punch, and a hook. So it's: Block, then jab, reverse, hook! Go!",
      },
      {
        time: 972,
        text: "You're not just defending; you're turning their attack into your opportunity! Let's see that again. A strong block, and a fast combo!",
      },
      {
        time: 982,
        text: "Now, let's try a block and a kick. Block the punch... and counter with a fast side kick! Remember to lead with your heel for power.",
      },
      {
        time: 993,
        text: "You're doing great. You're learning to think like a true martial artist.",
      },
      {
        time: 999,
        text: "Two minutes in! Let's make it a game. I'll call out the attack, and you decide the counter. Ready? A high punch is coming! Dodge or block!",
      },
      {
        time: 1010,
        text: 'Good! Now counter! What will it be? A punch combo? A kick? Show me!',
      },
      {
        time: 1018,
        text: 'Excellent choice! Now, another high punch is coming! Defend!',
      },
      {
        time: 1025,
        text: 'And counter! A different one this time. Be creative!',
      },
      {
        time: 1032,
        text: 'Now, imagine a low kick is coming. Do a low block to defend.',
      },
      {
        time: 1039,
        text: 'And counter with a fast punch combination! Good!',
      },
      {
        time: 1045,
        text: "You're reacting so quickly. This is what makes a great fighter.",
      },
      {
        time: 1052,
        text: "Halfway! Let's use our new Viper Hand Strikes as counters. An enemy ninja throws a punch... dodge it!",
      },
      {
        time: 1061,
        text: 'Now, counter with a sharp spear-hand strike! Aim for that tiny target.',
      },
      {
        time: 1069,
        text: "Awesome! Let's try that again. Dodge, and a lightning-fast spear-hand.",
      },
      {
        time: 1077,
        text: "Now, let's try blocking. Block the punch... and counter with a powerful ridge-hand strike! Remember that circular motion.",
      },
      {
        time: 1087,
        text: "Excellent! You're using your most advanced strikes to counter.",
      },
      {
        time: 1094,
        text: "Let's see a combo. Block, then a spear-hand, then a ridge-hand. A full viper counter!",
      },
      {
        time: 1103,
        text: "You're looking so sharp and dangerous. Keep that focus.",
      },
      {
        time: 1109,
        text: "Let's see another one. Make it your best one yet. Sharp and powerful.",
      },
      {
        time: 1116,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 1122,
        text: 'Prepare for the final minute.',
      },
      {
        time: 1126,
        text: "Last minute! Freestyle! I'm going to call out attacks, and you counter with any move you want. Be a creative cobra! Ready? High punch!",
      },
      {
        time: 1136,
        text: 'Good counter! Now a low kick!',
      },
      {
        time: 1141,
        text: 'Excellent! Another high punch!',
      },
      {
        time: 1146,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1152,
        text: 'A punch from the side! How do you block and counter?',
      },
      {
        time: 1158,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1164,
        text: 'A super fast punch is coming! React!',
      },
      {
        time: 1169,
        text: 'Five, four, three, two, one... and TIME! Excellent counters.',
      },
      {
        time: 1176,
        text: "That was some amazing defensive work! Now it's time to unleash the dragon with our next drill: 'Dragon's Fury Combos'!",
      },
      {
        time: 1185,
        text: "This is where we combine our most powerful moves. We're going to link our Tornado Kick with our Viper Hand Strikes. Start in your Ready Stance.",
      },
      {
        time: 1195,
        text: "First, I want you to do a powerful right-leg Tornado Kick. Remember, that's the spinning back kick where you look over your shoulder, chamber your knee, and strike with your heel.",
      },
      {
        time: 1207,
        text: "As you land from that kick, you're going to immediately follow up with a sharp left-hand spear-hand strike! So it's: Spin kick, then a spear-hand! Let's try it slowly.",
      },
      {
        time: 1219,
        text: "Awesome! Now the other side. A left-leg Tornado Kick... and as you land, a right-hand spear-hand strike! You're a true dragon warrior.",
      },
      {
        time: 1230,
        text: "Let's see five good combos on each side. Remember to keep your balance during the spin.",
      },
      {
        time: 1238,
        text: 'One minute in! Your combos are looking fierce. Now for a new combination.',
      },
      {
        time: 1245,
        text: "This time, after your right-leg Tornado Kick, you're going to counter with a powerful left-hand ridge-hand strike. Remember, that's the circular strike with the side of your hand.",
      },
      {
        time: 1258,
        text: "Let's see it. A spinning kick, and a big circular ridge-hand. Excellent!",
      },
      {
        time: 1266,
        text: 'Now the other side. A left-leg Tornado Kick, and a right-hand ridge-hand. Keep that power.',
      },
      {
        time: 1275,
        text: "You're linking your most advanced skills together. This is amazing to see.",
      },
      {
        time: 1282,
        text: "Let's see another one. Make it your best one yet. Sharp and powerful.",
      },
      {
        time: 1289,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 1295,
        text: "Two minutes in! Let's focus on the transition. The moment your foot lands from the kick, the hand strike should be exploding forward.",
      },
      {
        time: 1305,
        text: "There should be no pause in between. It's all one fluid, powerful motion. Let's see that smooth transition.",
      },
      {
        time: 1314,
        text: 'Kick and STRIKE! Awesome speed.',
      },
      {
        time: 1320,
        text: "Let's go back to the first combo. Tornado Kick and spear-hand. Make that transition seamless.",
      },
      {
        time: 1328,
        text: 'Now the second combo. Tornado Kick and ridge-hand. Just as smooth.',
      },
      {
        time: 1336,
        text: "You're doing great. Keep that focus.",
      },
      {
        time: 1341,
        text: 'Halfway! Your choice now. You can do the spear-hand combo or the ridge-hand combo. Surprise me!',
      },
      {
        time: 1350,
        text: "Let's see five perfect combos in a row. Show me that dragon's fury.",
      },
      {
        time: 1357,
        text: 'Remember to use your kiai on the hand strike. Let me hear that power!',
      },
      {
        time: 1364,
        text: 'Keep that quality high. Every kick, every strike.',
      },
      {
        time: 1370,
        text: "You're building incredible power and coordination. Keep it up.",
      },
      {
        time: 1376,
        text: "Let's see another one. Make it your fastest one yet.",
      },
      {
        time: 1382,
        text: "Excellent! You're looking like a true warrior.",
      },
      {
        time: 1388,
        text: "Keep that focus. We're in the final stretch of this exercise.",
      },
      {
        time: 1394,
        text: "Let's see one more perfect rep. A powerful kick and a sharp strike.",
      },
      {
        time: 1401,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1405,
        text: "Last minute! Let's see your most powerful combos. Imagine you're in a movie, performing the ultimate finishing move!",
      },
      {
        time: 1415,
        text: 'Give me a right-leg Tornado Kick into any hand strike you want. Be creative!',
      },
      {
        time: 1422,
        text: 'Now the left leg! Just as powerful, just as creative.',
      },
      {
        time: 1428,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1434,
        text: 'Faster, stronger, sharper! Come on!',
      },
      {
        time: 1439,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1445,
        text: 'One more perfect combo!',
      },
      {
        time: 1449,
        text: 'Five, four, three, two, one... and TIME! Excellent combos.',
      },
      {
        time: 1456,
        text: "That was some amazing power! For our final and most exciting drill, it's time for the 'Elite Ninja Shadow Battle'!",
      },
      {
        time: 1465,
        text: "This is your chance to be a hero! Imagine you're a ninja, and you're surrounded by imaginary enemy ninjas. You have to use all the strikes you've learned to defend yourself. Start in your Ready Stance, eyes open!",
      },
      {
        time: 1478,
        text: "Ready? Two ninjas in front! Give me a fast 'Fist of Fury Combo'! Remember, that's a left jab, a right reverse punch, and a left hook! Go!",
      },
      {
        time: 1489,
        text: "Awesome! Another ninja is rushing you! Give me a 'Triple Kick Challenge'! That's a right front kick, a right roundhouse kick, and a right side kick, all without putting your foot down!",
      },
      {
        time: 1502,
        text: 'Incredible balance! Now, a ninja to your left! Step and give them a sharp elbow strike! Remember to use your hips for power.',
      },
      {
        time: 1511,
        text: "Now one to your right! Step into a horse stance and another powerful elbow strike! You're a true defender.",
      },
      {
        time: 1520,
        text: "One minute in! The ninjas are getting faster! Let's see a full 'Cobra Combo'! Remember, that's jab, reverse punch, roundhouse kick, and elbow strike! Go!",
      },
      {
        time: 1532,
        text: "Excellent! Now the other side. A ninja is coming! A 'Cobra Combo' with your left leg kicking! Take them down!",
      },
      {
        time: 1542,
        text: 'Keep moving your feet. A good ninja is always light and bouncy, ready for anything.',
      },
      {
        time: 1550,
        text: 'Another ninja! Your choice! Show me a powerful kick. A roundhouse or a side kick?',
      },
      {
        time: 1558,
        text: 'Good choice! Very powerful.',
      },
      {
        time: 1563,
        text: "Let's see that 'Fist of Fury Combo' again. Make it a blur! Fast and sharp.",
      },
      {
        time: 1571,
        text: "And a big 'Triple Kick Challenge'! Front kick, roundhouse, side kick! Balance is key.",
      },
      {
        time: 1580,
        text: 'Two minutes in! The ninjas are coming from all sides now! Be ready.',
      },
      {
        time: 1587,
        text: 'Ninja on your right! A quick roundhouse kick! Remember to pivot.',
      },
      {
        time: 1593,
        text: "Ninja on your left! A 'Fist of Fury Combo'!",
      },
      {
        time: 1599,
        text: "Ninja right in front! A big 'Cobra Combo'! Punch, punch, kick, elbow!",
      },
      {
        time: 1607,
        text: "You're doing an amazing job. You're a true karate hero.",
      },
      {
        time: 1613,
        text: 'Keep your hands up in your guard position. Protect your face!',
      },
      {
        time: 1619,
        text: "Let's see another powerful kick. A big side kick! Push them away.",
      },
      {
        time: 1626,
        text: "Halfway! It's freestyle time! For the next minute, you decide how to fight the ninjas. Use any of the moves we've learned. Go!",
      },
      {
        time: 1636,
        text: 'Show me your best punches and kicks. Be creative!',
      },
      {
        time: 1642,
        text: "Maybe a double kick? Or a triple punch combo? It's your kata!",
      },
      {
        time: 1649,
        text: 'Keep moving around your space. A good ninja never stands still.',
      },
      {
        time: 1656,
        text: "Let's see a big kiai with your next move! Show me your power!",
      },
      {
        time: 1663,
        text: 'Awesome! The ninjas are scared of you now.',
      },
      {
        time: 1669,
        text: "Keep that energy high. You're doing great.",
      },
      {
        time: 1674,
        text: "Let's see another powerful combo. Make it your own.",
      },
      {
        time: 1680,
        text: "Excellent! You're a true martial artist.",
      },
      {
        time: 1686,
        text: "Last minute! Oh no, the Big Boss Ninja has appeared! He's right in front of you. We need your most powerful moves!",
      },
      {
        time: 1696,
        text: "Give me a 'Fist of Fury Combo'! That's a jab, reverse, and hook! Fast and strong!",
      },
      {
        time: 1703,
        text: "He's still coming! A 'Triple Kick Challenge'! Remember, front kick, roundhouse, and side kick without putting your foot down!",
      },
      {
        time: 1713,
        text: "He's still coming! Give me a full 'Cobra Combo'! That's jab, reverse punch, roundhouse kick, and elbow strike!",
      },
      {
        time: 1723,
        text: "Thirty seconds left! It's the final battle! Go, go, go!",
      },
      {
        time: 1729,
        text: "Faster, stronger! Use everything you've got!",
      },
      {
        time: 1735,
        text: 'Final fifteen seconds. Give me your best combination of the day!',
      },
      {
        time: 1741,
        text: 'One more big combo! You can do it!',
      },
      {
        time: 1746,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1752,
        text: 'Five, four, three, two, one... YOU DID IT! The Boss Ninja is defeated!',
      },
      {
        time: 1760,
        text: 'That was an incredible workout! You have learned and practiced so many amazing strikes today. You should be so proud of your hard work and focus.',
      },
      {
        time: 1771,
        text: "Let's do a final cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You have trained like a true karate champion today. Amazing job. Drill complete!",
      },
    ],
    timeTotal: 1780,
    equipment: 'None',
  },

  // BEGINNER BLOCKS (30 minutes) DONE
  {
    id: 2037,
    name: 'Beginner Blocks',
    categories: ['Blocks', 'beginner'],
    sports: ['karate'],
    difficulty: 'beginner',
    skill: 'Blocks',
    points: 30,
    instructions:
      "Ready to become a defensive master? This session will teach you how to build a super-strong karate shield! You'll learn the coolest blocks to protect yourself and move like a true karate hero. Let's get started!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello, Karate Hero! Ready to build an unbreakable shield? Let's get started with 'Block Foundations'! Begin in a Ready Stance! Feet apart, fists by your side, and stand tall!",
      },
      {
        time: 10,
        text: 'First up, the High Block, to protect your head! With your right arm, bring your right fist to your left ear, palm facing you. Now, sweep it up and across to make a roof over your head! As you do, pull your left fist back to your hip. Go!',
      },
      {
        time: 22,
        text: "Awesome! Your arm should be angled, not flat. Let's do the other side! Bring your left fist to your right ear... and SWEEP up into a Left High Block! Pull that right fist back to your hip!",
      },
      {
        time: 33,
        text: "You're a natural defender! Let's get a rhythm going. Ready... Right High Block! ...and Left High Block! Keep switching! Remember to set it up at your ear each time for extra power!",
      },
      {
        time: 45,
        text: "Let's see four more on each side! One! ...Two! ...Three! ...Four! Perfect form!",
      },
      {
        time: 56,
        text: "Next up, the Middle Block, to protect your body! With your right arm, bring your right fist way over to your left shoulder. Now, swing it across your body in a big circle, like you're closing a giant door! And... BLOCK!",
      },
      {
        time: 69,
        text: 'Awesome! Now the left arm. Bring your left fist to your right shoulder... and swing it across in a big Left Middle Block! Protect that body!',
      },
      {
        time: 79,
        text: "Let's practice that! Right Middle Block! ...and Left Middle Block! Keep switching! Make your blocking arm strong like a shield!",
      },
      {
        time: 90,
        text: 'Four more on each side! One! ...Two! ...Three! ...Four! Great power!',
      },
      {
        time: 101,
        text: "Last one, the Low Block, to protect your legs! Bring your right fist up to your left ear, just like a high block. But this time, sweep your arm DOWN and across your body, like you're sweeping away a kick! Ready... SWEEP!",
      },
      {
        time: 114,
        text: 'Excellent! Other side! Bring your left fist to your right ear... and sweep it down into a Left Low Block! Powerful!',
      },
      {
        time: 124,
        text: "Let's get a rhythm! Right Low Block! ...and Left Low Block! Keep switching! Put your whole body into that sweep!",
      },
      {
        time: 135,
        text: "You're a true defender! High, middle, and low! Final minute! Lightning round! I call it, you do it! Ready? RIGHT HIGH BLOCK! Make that roof!",
      },
      {
        time: 145,
        text: 'LEFT LOW BLOCK! Sweep it down!',
      },
      {
        time: 149,
        text: 'RIGHT MIDDLE BLOCK! Close that door!',
      },
      {
        time: 153,
        text: 'Thirty seconds left! Faster! HIGH! LOW! MIDDLE!',
      },
      {
        time: 160,
        text: 'LEFT HIGH! RIGHT LOW! LEFT MIDDLE! Go, go, go!',
      },
      {
        time: 167,
        text: "Final ten seconds! Give me everything you've got! Five, four, three, two, one... TIME! Excellent blocks!",
      },
      {
        time: 178,
        text: "Amazing start! No break, let's go! Time to make our blocks super strong in 'The Shield Wall' drill!",
      },
      {
        time: 186,
        text: "We're going to hold each block to build strong karate muscles! Let's start in a Ready Stance.",
      },
      {
        time: 193,
        text: 'First, get into a Right High Block! Make a roof over your head... and HOLD IT! Make it super strong for ten... nine... eight... seven... six... five... four... three... two... one... And relax! Awesome!',
      },
      {
        time: 210,
        text: 'Now the Left High Block! Get in position and HOLD! Be a rock! Ten seconds! ...five, four, three, two, one... And relax!',
      },
      {
        time: 224,
        text: 'Now the Right Middle Block! Swing it across like a shield and HOLD! Ten seconds! ...five, four, three, two, one... And relax! Great power!',
      },
      {
        time: 238,
        text: 'Now the Left Middle Block! Make that shield strong! HOLD! Ten seconds! ...five, four, three, two, one... And relax! Unbreakable!',
      },
      {
        time: 252,
        text: 'And the Right Low Block! Sweep it down and across and HOLD! Ten seconds! ...five, four, three, two, one... And relax! Awesome!',
      },
      {
        time: 266,
        text: "Last one! Left Low Block! Sweep it down, hold it strong! Ten seconds! ...five, four, three, two, one... And relax! You're a true defender!",
      },
      {
        time: 280,
        text: "Let's make it harder! We're holding for fifteen seconds now! Start with the Right High Block. Go! Make it your strongest hold yet!",
      },
      {
        time: 296,
        text: 'five... four... three... two... one... And relax! Great focus. Now the Left High Block for fifteen seconds! You can do it!',
      },
      {
        time: 312,
        text: "five... four... three... two... one... And relax! You're a powerhouse. Now the Right Middle Block for fifteen seconds! Be a shield!",
      },
      {
        time: 328,
        text: 'five... four... three... two... one... And relax! Final two! Right Low Block for fifteen seconds! Sweep and hold!',
      },
      {
        time: 344,
        text: 'five... four... three... two... one... And relax! Last one, best one! Left Low Block for fifteen seconds! Go!',
      },
      {
        time: 360,
        text: "five... four... three... two... one... And relax! You're a true warrior. Final minute is a lightning round! I call it, you hold it for five seconds! Ready? RIGHT HIGH BLOCK!",
      },
      {
        time: 372,
        text: 'LEFT LOW BLOCK! Hold!',
      },
      {
        time: 378,
        text: 'RIGHT MIDDLE BLOCK! Hold!',
      },
      {
        time: 384,
        text: 'Thirty seconds! Go, go, go! LEFT HIGH BLOCK!',
      },
      {
        time: 390,
        text: 'RIGHT LOW BLOCK!',
      },
      {
        time: 395,
        text: 'LEFT MIDDLE BLOCK!',
      },
      {
        time: 400,
        text: 'Final ten seconds! Five, four, three, two, one... TIME! Excellent strength!',
      },
      {
        time: 410,
        text: "Awesome strength! Now let's link our moves together in 'Karate Combo Blocks'!",
      },
      {
        time: 417,
        text: 'This is all about flowing from one block to the next without stopping. Start in your Ready Stance.',
      },
      {
        time: 424,
        text: "First, a Right High Block, then immediately flow into a Right Middle Block. Roof to shield! Ready? GO! ...Awesome! Let's try it again. High block... right into middle block. Perfect!",
      },
      {
        time: 436,
        text: "Other side! Left High Block... into a Left Middle Block. Flow like a river! Go! ...You're a combo machine!",
      },
      {
        time: 446,
        text: "Let's get a rhythm. Right high-middle combo! ...and Left high-middle combo! Keep switching! Make it look like one smooth move!",
      },
      {
        time: 458,
        text: "Now for the ultimate combo: The Block Shield! We're going from High Block, to Middle Block, to Low Block, all with the same arm. Let's try it slowly with your right arm.",
      },
      {
        time: 470,
        text: 'Right High Block, make the roof... into a Right Middle Block, make the shield... and finish with a Right Low Block, the big sweep down. Awesome!',
      },
      {
        time: 481,
        text: "Now the left arm! Left High Block... Left Middle Block... and Left Low Block! You're an unbreakable wall!",
      },
      {
        time: 491,
        text: "Let's practice! Right side Block Shield! Go! ...and Left side Block Shield! Go! Keep alternating!",
      },
      {
        time: 501,
        text: "Let's pick up the speed! A little sharper, a little faster! Let's see a fast Block Shield with your right arm. High, middle, low! ...Good! Now the left arm, just as fast!",
      },
      {
        time: 513,
        text: "Don't forget your kiai on that final low block! A big 'ha!' shows your power. Let's hear it! A sharp combo with a loud kiai! Go!",
      },
      {
        time: 523,
        text: "Awesome! You sound like a true warrior. Final minute is a lightning round! I'll call out a combo. Ready? RIGHT HIGH-MIDDLE COMBO!",
      },
      {
        time: 532,
        text: 'Good! LEFT BLOCK SHIELD! High, middle, low!',
      },
      {
        time: 537,
        text: 'Awesome! RIGHT BLOCK SHIELD!',
      },
      {
        time: 542,
        text: 'Thirty seconds left! Keep it going! LEFT HIGH-MIDDLE COMBO!',
      },
      {
        time: 548,
        text: 'RIGHT LOW BLOCK! Just one sweep!',
      },
      {
        time: 552,
        text: 'LEFT BLOCK SHIELD!',
      },
      {
        time: 556,
        text: 'Final ten seconds! Five, four, three, two, one... TIME! Excellent combos.',
      },
      {
        time: 565,
        text: "Amazing karate! Now for a super fun game... 'Sensei Says: Block Challenge'!",
      },
      {
        time: 572,
        text: "When I say 'Sensei says,' do the block fast! If I DON'T say 'Sensei says,' you have to stay frozen in your Ready Stance! Let's go!",
      },
      {
        time: 582,
        text: 'Ready Stance, eyes on me. Sensei says... do a Right High Block! Make that roof!',
      },
      {
        time: 589,
        text: 'Good! Back to Ready. Sensei says... do a Left Low Block! Sweep down low!',
      },
      {
        time: 596,
        text: "Excellent! Back to Ready. Right Middle Block! ... Ah, did I get you? I didn't say Sensei says! Let's try again.",
      },
      {
        time: 606,
        text: "Let's pick up the pace! Ready Stance. Sensei says... Left Middle Block!",
      },
      {
        time: 612,
        text: 'Good! Back to Ready. Right High Block! ... Ooh, so close! Remember to listen!',
      },
      {
        time: 620,
        text: "Let's go again! Sensei says... Left High Block! ...Sensei says... Right Low Block! ...Sensei says... Left Middle Block!",
      },
      {
        time: 630,
        text: 'The calls are going to get faster. Be ready! Sensei says... Right Middle Block! Good. Sensei says... Left Low Block!',
      },
      {
        time: 639,
        text: 'Right High Block! ... Gotcha! Stay sharp, warrior.',
      },
      {
        time: 645,
        text: "Let's try a combo! Sensei says... Right High Block! ...Sensei says... Right Middle Block!",
      },
      {
        time: 653,
        text: 'Good! Back to Ready. Sensei says... Left High Block! ...Sensei says... Left Low Block!',
      },
      {
        time: 661,
        text: "Let's speed up the calls! Ready Stance! Sensei says... RIGHT LOW BLOCK!",
      },
      {
        time: 666,
        text: 'Sensei says... LEFT HIGH BLOCK!',
      },
      {
        time: 670,
        text: 'Sensei says... RIGHT MIDDLE BLOCK!',
      },
      {
        time: 674,
        text: "LEFT LOW BLOCK! ... Ah, you're too good! You didn't move.",
      },
      {
        time: 680,
        text: 'Final minute! The calls are rapid-fire! Ready? Sensei says... RIGHT HIGH BLOCK!',
      },
      {
        time: 687,
        text: 'Sensei says... LEFT LOW BLOCK! Sensei says... RIGHT MIDDLE BLOCK!',
      },
      {
        time: 693,
        text: 'LEFT HIGH BLOCK! ... Good freeze! Sensei says... LEFT MIDDLE BLOCK!',
      },
      {
        time: 700,
        text: 'Thirty seconds left! Go, go, go! Sensei says... RIGHT LOW! Sensei says... LEFT HIGH! Sensei says... RIGHT MIDDLE!',
      },
      {
        time: 709,
        text: 'Final ten seconds! One more perfect block! Sensei says... LOW BLOCK!',
      },
      {
        time: 716,
        text: 'Five, four, three, two, one... TIME! Excellent reactions.',
      },
      {
        time: 722,
        text: "Amazing listening! Now let's become a 'Moving Fortress'! We're adding stances to our blocks.",
      },
      {
        time: 730,
        text: "Let's move and defend at the same time! Start in Ready Stance. Fists up!",
      },
      {
        time: 736,
        text: 'Step forward with your left foot into a Left Front Stance and do a Right High Block at the exact same time. Ready? Go!',
      },
      {
        time: 744,
        text: 'Awesome! Back to Ready. Other side! Step forward with your right foot into a Right Front Stance and a Left High Block. Go!',
      },
      {
        time: 753,
        text: 'Excellent! Now, with a Middle Block! Step forward left and do a Right Middle Block! ...Good! Back to Ready. Now step forward right and a Left Middle Block!',
      },
      {
        time: 764,
        text: 'And the Low Block! Step forward left and a Right Low Block! ...Good! Back to Ready. Step forward right and a Left Low Block! Awesome!',
      },
      {
        time: 775,
        text: "Let's get a rhythm! Step forward with a high block, then back. Then a middle block, then back. Then a low block! Left side first! Go! High... middle... low! Excellent combo!",
      },
      {
        time: 788,
        text: "Now the right side! Same thing! High... middle... low! You're an unbreakable fortress!",
      },
      {
        time: 799,
        text: "Let's try moving backwards! Step back into a Left Back Stance and do a Right Middle Block at the same time. Remember, weight on your back leg!",
      },
      {
        time: 810,
        text: 'Good! Back to Ready. Now other side! Step back into a Right Back Stance with a Left Middle Block! Excellent!',
      },
      {
        time: 820,
        text: "Let's combine forward and backward! Start in Ready. Step forward into a Left Front Stance with a punch! Make a fist and punch straight out! ...Now, step back into a Left Back Stance with a block! Bring your arm up to guard!",
      },
      {
        time: 834,
        text: 'Awesome! Now the right side! Step forward into a Right Front Stance with a punch! ...then back into a Right Back Stance with a block!',
      },
      {
        time: 845,
        text: "You're moving like a karate master! Final minute! Lightning round! I'll call a direction and a block. Ready? FORWARD, HIGH BLOCK!",
      },
      {
        time: 854,
        text: 'BACKWARD, MIDDLE BLOCK!',
      },
      {
        time: 858,
        text: 'FORWARD, LOW BLOCK!',
      },
      {
        time: 862,
        text: 'Thirty seconds! Go, go, go! BACKWARD, HIGH BLOCK!',
      },
      {
        time: 867,
        text: 'FORWARD, MIDDLE BLOCK!',
      },
      {
        time: 871,
        text: 'BACKWARD, LOW BLOCK!',
      },
      {
        time: 875,
        text: 'Final ten seconds! Five, four, three, two, one... TIME! Excellent movement.',
      },
      {
        time: 884,
        text: "Amazing footwork! For our final drill, it's time for 'Ninja Defense Mode'!",
      },
      {
        time: 891,
        text: "Imagine you're a ninja surrounded by imaginary enemies! Use all your blocks to defend yourself. Start in Ready Stance, stay bouncy!",
      },
      {
        time: 901,
        text: 'An enemy attacks high! Fast Right High Block! ...Good! Another attacks low! Powerful Left Low Block! ...Excellent! One attacks your body! Strong Right Middle Block!',
      },
      {
        time: 914,
        text: "The enemies are getting faster! Give me a 'Block Shield' combo! High, middle, and low block with your right arm! Go! ...Now the other side! Left arm Block Shield! Go!",
      },
      {
        time: 927,
        text: 'An enemy is in front! Step forward into a Front Stance with a High Block! ...Now one is behind you! Step back into a Back Stance with a Middle Block!',
      },
      {
        time: 938,
        text: "You're a true hero! It's freestyle time! For the next minute, you decide how to fight the enemies. Use any blocks, any stances! Go!",
      },
      {
        time: 949,
        text: "Show me your best moves! Be creative! Keep moving around! Don't stand still! Let's see a big kiai with your next block! Show your power!",
      },
      {
        time: 961,
        text: 'Awesome! The enemies are scared! Thirty seconds left of freestyle! Keep that energy high!',
      },
      {
        time: 970,
        text: "Final minute! Oh no, the Big Boss has appeared! We need your most powerful moves! He's attacking high! Strong High Block!",
      },
      {
        time: 981,
        text: "He's attacking low! Powerful Low Block! ...He's attacking the middle! Strong Middle Block!",
      },
      {
        time: 989,
        text: "Thirty seconds left! It's the final battle! Go, go, go! Faster, stronger! Use everything you've got!",
      },
      {
        time: 998,
        text: 'Final fifteen seconds! Give me your best combination of the day! One more big combo! You can do it!',
      },
      {
        time: 1006,
        text: 'Ten, nine, eight, seven, six... five, four, three, two, one... YOU DID IT! The Boss is defeated!',
      },
      {
        time: 1017,
        text: 'That was an incredible workout! You learned so many amazing blocks today. Be super proud of your hard work and focus.',
      },
      {
        time: 1026,
        text: "Let's cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You trained like a true karate champion today. Amazing job. Drill complete!",
      },
    ],
    timeTotal: 1035,
    equipment: 'None',
  },

  // INTERMEDIATE BLOCKS (45 minutes) Done
  {
    id: 2038,
    name: 'Intermediate Blocks',
    categories: ['Blocks', 'intermediate'],
    sports: ['karate'],
    difficulty: 'intermediate',
    skill: 'Blocks',
    points: 30,
    instructions:
      'Intermediate Blocks builds on your basic blocking foundation with more advanced techniques, complex defensive combinations, and dynamic blocking patterns. This 30-minute session includes advanced blocking techniques, complex combinations, and dynamic defensive sequences.',
    timedInstructions: [
      {
        time: 0,
        text: "Hello, karateka! Ready to take your blocking skills to the next level? Let's start with a high-energy warm-up I call the 'Block Master Warm-up'! Start in a strong Ready Stance.",
      },
      {
        time: 11,
        text: "Let's review our basic blocks at a faster speed. On my call, snap into the block as quickly as you can. Ready... Right High Block! Remember, that's the roof over your head.",
      },
      {
        time: 22,
        text: 'Good! Back to Ready. Now... Left Low Block! The big sweep down to protect your legs.',
      },
      {
        time: 29,
        text: 'Excellent! Back to Ready. And... Right Middle Block! The big circle across your body like a shield.',
      },
      {
        time: 38,
        text: "Awesome! Back to Ready. Let's pick up the pace. Left High Block! Right Low Block! Left Middle Block!",
      },
      {
        time: 48,
        text: "You're a true blocking master! Keep that energy.",
      },
      {
        time: 53,
        text: "One minute in! Now, let's add our stances. Step forward into a Left Front Stance with a Right High Block. Go!",
      },
      {
        time: 62,
        text: 'Good! Back to Ready. Now step forward into a Right Front Stance with a Left Middle Block. Be strong!',
      },
      {
        time: 71,
        text: 'Excellent! Back to Ready. Now step back into a Left Back Stance with a Right Middle Block. Remember, weight on that back leg.',
      },
      {
        time: 81,
        text: 'Awesome! Back to Ready. And step back into a Right Back Stance with a Left Low Block. Perfect form.',
      },
      {
        time: 90,
        text: "You're moving and blocking like a true warrior. Let's get a rhythm going. Forward with a punch, back with a block.",
      },
      {
        time: 99,
        text: "Let's see that again. Step forward into a Front Stance with a High Block.",
      },
      {
        time: 106,
        text: 'Now step back into a Back Stance with a Middle Block. Keep it flowing.',
      },
      {
        time: 113,
        text: "Excellent! You're looking sharp.",
      },
      {
        time: 118,
        text: "Two minutes in! Let's do a combo. From Ready, step forward into a Left Front Stance with a High Block, then immediately shift into a Left Back Stance with a Middle Block.",
      },
      {
        time: 130,
        text: "That's a pro-level combination! Let's do the other side. Right Front Stance with a High Block, then shift to a Right Back Stance with a Middle Block.",
      },
      {
        time: 142,
        text: "You're not just blocking; you're moving like a true martial artist. Keep it up.",
      },
      {
        time: 149,
        text: "Let's see that left side combo again. Make it smooth and fast.",
      },
      {
        time: 156,
        text: 'And the right side combo. Just as sharp.',
      },
      {
        time: 162,
        text: "Halfway! Let's go back to our random calls, but this time, you'll move into a stance with each one. Ready?",
      },
      {
        time: 171,
        text: 'Step forward into a Left Front Stance with a HIGH BLOCK!',
      },
      {
        time: 178,
        text: 'Good! Back to Ready. Step back into a Right Back Stance with a MIDDLE BLOCK!',
      },
      {
        time: 186,
        text: 'Excellent! Back to Ready. Step forward into a Right Front Stance with a LOW BLOCK!',
      },
      {
        time: 194,
        text: "Awesome reactions! You're thinking and moving at the same time.",
      },
      {
        time: 200,
        text: "Let's see another one. Step back into a Left Back Stance with a HIGH BLOCK!",
      },
      {
        time: 208,
        text: 'Keep that focus. Every block, every stance, perfect.',
      },
      {
        time: 214,
        text: "Let's see one more. Step forward into a Left Front Stance with a MIDDLE BLOCK!",
      },
      {
        time: 222,
        text: "Excellent! You're looking like a true warrior.",
      },
      {
        time: 228,
        text: 'Prepare for the final minute. This is the lightning round!',
      },
      {
        time: 232,
        text: "Last minute! I'm going to call them out fast. Move and block as quickly as you can. Ready? FORWARD, HIGH BLOCK!",
      },
      {
        time: 240,
        text: 'BACKWARD, LOW BLOCK!',
      },
      {
        time: 245,
        text: 'FORWARD, MIDDLE BLOCK!',
      },
      {
        time: 250,
        text: 'Thirty seconds left! Keep it going! BACKWARD, HIGH BLOCK!',
      },
      {
        time: 256,
        text: 'FORWARD, LOW BLOCK!',
      },
      {
        time: 261,
        text: 'BACKWARD, MIDDLE BLOCK!',
      },
      {
        time: 266,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 272,
        text: 'One more perfect move!',
      },
      {
        time: 276,
        text: 'Five, four, three, two, one... and TIME! Excellent warm-up.',
      },
      {
        time: 283,
        text: "That was an amazing start! Now it's time to add new tools to our dojo with a drill I call 'The Sword & Shield'!",
      },
      {
        time: 292,
        text: "First, we'll learn the Knife-Hand Block, or 'Shuto-uke'. This is your sword! Hold your right hand out, but keep all your fingers perfectly straight and squeezed together. Now, tuck your thumb in tight by your palm.",
      },
      {
        time: 305,
        text: "From your Ready Stance, bring your right 'sword hand' up to your left ear. Now, sweep it down and across your body in a sharp, chopping motion. At the same time, pull your other fist back to your hip. Let's see it!",
      },
      {
        time: 318,
        text: 'Awesome! Now the other side! Make a sword with your left hand, bring it to your right ear... and CHOP! A perfect Left Knife-Hand Block.',
      },
      {
        time: 329,
        text: "Let's see five sharp sword blocks with your right hand. One, two, three, four, five! Good.",
      },
      {
        time: 338,
        text: "Now five with your left. Keep those fingers tight! That's what makes the sword strong.",
      },
      {
        time: 345,
        text: "One minute in! Now for the shield! The Inside Block, or 'Uchi-uke'. This block is a powerful sweep from the outside in.",
      },
      {
        time: 356,
        text: "Start with your right arm. Bring your right fist down by your right hip. Now, swing it up and across your body in a big circle, ending in front of your left shoulder. It's a big shield protecting you.",
      },
      {
        time: 370,
        text: 'Excellent! Now the left arm. Bring your left fist to your left hip... and swing it up and across to your right. A powerful shield!',
      },
      {
        time: 381,
        text: "Let's practice that. Right Inside Block... then left Inside Block. Keep alternating.",
      },
      {
        time: 389,
        text: 'Your blocking arm should be strong, like a steel shield. Make a big muscle!',
      },
      {
        time: 396,
        text: "Let's see five powerful shield blocks on each side. Go for it.",
      },
      {
        time: 403,
        text: "You're a true defender, with a sword and a shield. Keep it up.",
      },
      {
        time: 409,
        text: "Two minutes in! Let's combine them. We'll do a sword block, then a shield block.",
      },
      {
        time: 417,
        text: "With your right arm: a sharp Knife-Hand Block... bring it back... and a powerful Inside Block. It's a one-two combo!",
      },
      {
        time: 427,
        text: 'Awesome! Now the left arm. A sharp Knife-Hand... and a powerful Inside Block.',
      },
      {
        time: 435,
        text: 'This is a fantastic combination of a chopping block and a sweeping block. Keep practicing that.',
      },
      {
        time: 443,
        text: "Let's see three good combos on each side. Right hand first. Sword, shield!",
      },
      {
        time: 450,
        text: 'Now the left hand. Sword, shield! Excellent.',
      },
      {
        time: 456,
        text: "Halfway! Let's add our stances to make these blocks even more powerful.",
      },
      {
        time: 463,
        text: 'Step back into a Right Back Stance and do a Left Knife-Hand block. Remember, your weight should be on your back leg.',
      },
      {
        time: 473,
        text: 'Good! Now, from that Back Stance, step forward into a Right Front Stance and do a Right Inside Block. A powerful combo.',
      },
      {
        time: 483,
        text: "Let's try the other side. Step back into a Left Back Stance and do a Right Knife-Hand block.",
      },
      {
        time: 491,
        text: "And from there, a Left Front Stance and a Left Inside Block. You're a true powerhouse.",
      },
      {
        time: 500,
        text: "Let's practice that again. Back Stance with a Knife-Hand, then Front Stance with an Inside Block. Keep switching sides.",
      },
      {
        time: 510,
        text: "You're not just using your arms; you're using your strong stances to generate power.",
      },
      {
        time: 518,
        text: "Let's see another one. Make it your best one yet. Sharp and powerful.",
      },
      {
        time: 525,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 531,
        text: 'Prepare for the final minute.',
      },
      {
        time: 535,
        text: 'Last minute! Freestyle! Mix your Knife-Hand and Inside blocks. Use any stance you want. Be a creative warrior!',
      },
      {
        time: 545,
        text: 'Show me a combo! Maybe a Knife-Hand, another Knife-Hand, and then a big Inside Block!',
      },
      {
        time: 553,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 559,
        text: 'Faster, stronger, sharper! Come on!',
      },
      {
        time: 564,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 570,
        text: 'One more perfect combo!',
      },
      {
        time: 574,
        text: 'Five, four, three, two, one... and TIME! Excellent blocks.',
      },
      {
        time: 581,
        text: "That was some amazing defense! Now it's time to learn how to defend and attack at the same time with a super smart drill called 'The Cobra's Counter'!",
      },
      {
        time: 591,
        text: "A true master doesn't just block; they strike back like a cobra! Imagine an enemy ninja is throwing a slow punch right at your face. First, we evade! Lean your head back just a little to dodge the punch.",
      },
      {
        time: 602,
        text: "As soon as you dodge, you're going to explode back with a counter-attack! Let's try a one-two punch. So it's: Dodge, then a fast left jab and a right reverse punch! Go!",
      },
      {
        time: 614,
        text: "Awesome! Let's try that again. Imagine the punch is coming... DODGE... and COUNTER! Jab, reverse punch! You're a cobra striking back.",
      },
      {
        time: 624,
        text: "Now, let's try a different counter. Imagine the punch is coming... dodge back... and this time, counter with a powerful right roundhouse kick! Remember to pivot!",
      },
      {
        time: 636,
        text: "Excellent! You dodged their attack and landed your own. This is a very advanced skill. Let's practice that again. Dodge, and a big roundhouse kick!",
      },
      {
        time: 646,
        text: "One minute in! Let's try a different defense. This time, instead of dodging, we'll block. The punch is coming... use a strong rising block to protect your head.",
      },
      {
        time: 657,
        text: "Immediately after you block, counter with a 'Fist of Fury' combo! That's a jab, a reverse punch, and a hook. So it's: Block, then jab, reverse, hook! Go!",
      },
      {
        time: 670,
        text: "You're not just defending; you're turning their attack into your opportunity! Let's see that again. A strong block, and a fast combo!",
      },
      {
        time: 680,
        text: "Now, let's try a block and a kick. Block the punch... and counter with a fast side kick! Remember to lead with your heel for power.",
      },
      {
        time: 691,
        text: "You're doing great. You're learning to think like a true martial artist.",
      },
      {
        time: 697,
        text: "Two minutes in! Let's make it a game. I'll call out the attack, and you decide the counter. Ready? A high punch is coming! Dodge or block!",
      },
      {
        time: 708,
        text: 'Good! Now counter! What will it be? A punch combo? A kick? Show me!',
      },
      {
        time: 716,
        text: 'Excellent choice! Now, another high punch is coming! Defend!',
      },
      {
        time: 723,
        text: 'And counter! A different one this time. Be creative!',
      },
      {
        time: 730,
        text: 'Now, imagine a low kick is coming. Do a low block to defend.',
      },
      {
        time: 737,
        text: 'And counter with a fast punch combination! Good!',
      },
      {
        time: 743,
        text: "You're reacting so quickly. This is what makes a great fighter.",
      },
      {
        time: 750,
        text: "Halfway! Let's use our new Viper Hand Strikes as counters. An enemy ninja throws a punch... dodge it!",
      },
      {
        time: 759,
        text: "Now, counter with a sharp spear-hand strike! Remember, that's the one with your fingers straight like a spear. Aim for that tiny target.",
      },
      {
        time: 769,
        text: "Awesome! Let's try that again. Dodge, and a lightning-fast spear-hand.",
      },
      {
        time: 777,
        text: "Now, let's try blocking. Block the punch... and counter with a powerful ridge-hand strike! Remember that circular strike with the side of your hand.",
      },
      {
        time: 787,
        text: "Excellent! You're using your most advanced strikes to counter.",
      },
      {
        time: 794,
        text: "Let's see a combo. Block, then a spear-hand, then a ridge-hand. A full viper counter!",
      },
      {
        time: 803,
        text: "You're looking so sharp and dangerous. Keep that focus.",
      },
      {
        time: 809,
        text: "Let's see another one. Make it your best one yet. Sharp and powerful.",
      },
      {
        time: 816,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 822,
        text: 'Prepare for the final minute.',
      },
      {
        time: 826,
        text: "Last minute! Freestyle! I'm going to call out attacks, and you counter with any move you want. Be a creative cobra! Ready? High punch!",
      },
      {
        time: 836,
        text: 'Good counter! Now a low kick!',
      },
      {
        time: 841,
        text: 'Excellent! Another high punch!',
      },
      {
        time: 846,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 852,
        text: 'A punch from the side! How do you block and counter?',
      },
      {
        time: 858,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 864,
        text: 'A super fast punch is coming! React!',
      },
      {
        time: 869,
        text: 'Five, four, three, two, one... and TIME! Excellent counters.',
      },
      {
        time: 876,
        text: "That was some amazing defensive work!  Now it's time to become a true ninja and learn how to move and block at the same time in our next drill: 'Ninja Evasion'!",
      },
      {
        time: 887,
        text: "A great ninja doesn't just stand there; they move their feet! We're going to practice blocking while stepping off the line of attack. Start in your Ready Stance.",
      },
      {
        time: 897,
        text: "Imagine a ninja is attacking you straight on. We're going to step to the side to evade. Let's try it. Take a quick step to your left, and at the same time, do a strong Right Middle Block across your body.",
      },
      {
        time: 909,
        text: 'Awesome! You moved and blocked as one. Back to Ready. Now the other way. Step to your right, and do a Left Middle Block. Go!',
      },
      {
        time: 919,
        text: "Excellent! This is how you stay safe and create an angle to counter-attack. Let's get a rhythm. Step left, block right. Step right, block left.",
      },
      {
        time: 930,
        text: "One minute in! Your footwork is looking sharp. Remember to keep your hands up in a guard position, even when you're moving.",
      },
      {
        time: 939,
        text: "Let's see five good side-steps and blocks to each side. Keep that quality high.",
      },
      {
        time: 946,
        text: "You're doing great. This is a very important skill for a martial artist.",
      },
      {
        time: 952,
        text: "Let's see another one. Make it your fastest one yet. Quick and sharp!",
      },
      {
        time: 958,
        text: "Excellent! You're looking like a true warrior.",
      },
      {
        time: 963,
        text: "Two minutes in! Now let's try moving backwards. From Ready Stance, an attack is coming! Step back with your right foot into a Right Back Stance and do a Left High Block at the same time.",
      },
      {
        time: 976,
        text: 'Remember, in a Back Stance, your weight is on your back leg. This is a powerful defensive retreat. Good! Back to Ready.',
      },
      {
        time: 986,
        text: 'Now the other side. Step back with your left foot into a Left Back Stance and do a Right High Block. Excellent!',
      },
      {
        time: 995,
        text: "Let's practice that. Step back with a high block, then forward to ready. Keep alternating sides.",
      },
      {
        time: 1003,
        text: 'This is how you create space and defend a high attack at the same time.',
      },
      {
        time: 1010,
        text: "Let's see another one. Make it strong and stable.",
      },
      {
        time: 1016,
        text: "Halfway! Let's combine our movements. I'll call out a direction, and you have to move and block. Ready? SIDE STEP LEFT!",
      },
      {
        time: 1025,
        text: 'Good! Back to Ready. Now... STEP BACK RIGHT!',
      },
      {
        time: 1031,
        text: 'Excellent! Back to Ready. SIDE STEP RIGHT!',
      },
      {
        time: 1037,
        text: 'Awesome reactions! Back to Ready. STEP BACK LEFT!',
      },
      {
        time: 1043,
        text: "You're thinking and moving like a real ninja. Keep it up.",
      },
      {
        time: 1049,
        text: "Let's focus on your balance. Even when you move quickly, you should land in a strong, steady stance.",
      },
      {
        time: 1057,
        text: "Let's see that strong balance. SIDE STEP LEFT! Freeze in that stance. Perfect.",
      },
      {
        time: 1064,
        text: 'And STEP BACK RIGHT! Freeze! Awesome.',
      },
      {
        time: 1070,
        text: "You're doing great. This is a very advanced drill.",
      },
      {
        time: 1076,
        text: 'Prepare for the final minute.',
      },
      {
        time: 1080,
        text: "Last minute! The calls are going to be rapid-fire. Don't think, just react! Ready? SIDE STEP LEFT!",
      },
      {
        time: 1088,
        text: 'STEP BACK RIGHT! SIDE STEP RIGHT! STEP BACK LEFT!',
      },
      {
        time: 1096,
        text: 'Thirty seconds left! Empty the tank. Go, go, go!',
      },
      {
        time: 1102,
        text: 'Faster, sharper, more evasive! Come on!',
      },
      {
        time: 1107,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1113,
        text: 'One more perfect move!',
      },
      {
        time: 1117,
        text: 'Five, four, three, two, one... and TIME! Excellent evasion.',
      },
      {
        time: 1124,
        text: "That was some amazing footwork! Now it's time to build incredible power and become an 'Unmovable Fortress'!",
      },
      {
        time: 1132,
        text: "This exercise is all about generating power from your strong stances. Let's start by getting into our most powerful stance: the Horse Stance. Step out wide, bend your knees, and sink down low. Go!",
      },
      {
        time: 1144,
        text: "Now, from this strong base, I want you to do a powerful Right Middle Block. Remember to twist your hips and your whole body into the block. It's not just your arm!",
      },
      {
        time: 1155,
        text: 'Excellent! Feel that power coming from the ground? Now, a Left Middle Block, with that same hip twist. Go!',
      },
      {
        time: 1164,
        text: "Let's get a rhythm. Right block... left block. Stay low in that Horse Stance. Don't stand up!",
      },
      {
        time: 1173,
        text: "One minute in! Your blocks are looking so strong. Let's try it with our new blocks. From your Horse Stance, do a Right Knife-Hand Block. Remember, that's your sword hand!",
      },
      {
        time: 1184,
        text: 'Good! Now a Left Knife-Hand Block. Keep that hand sharp and strong.',
      },
      {
        time: 1191,
        text: "Now for the shield! A Right Inside Block. Remember, that's the big sweep from the outside in.",
      },
      {
        time: 1199,
        text: "And a Left Inside Block. You're an unbreakable fortress!",
      },
      {
        time: 1205,
        text: "Let's practice that. Knife-Hand, then Inside Block on the right. Then Knife-Hand, then Inside Block on the left. Keep it flowing.",
      },
      {
        time: 1216,
        text: "You're doing great. This is how you build real power in your techniques.",
      },
      {
        time: 1223,
        text: "Two minutes in! Let's make it a combo. From your Horse Stance, do a Right Knife-Hand block, then immediately a Left Inside Block.",
      },
      {
        time: 1233,
        text: 'Awesome! Now the other way. A Left Knife-Hand block, then a Right Inside Block. A super-fast combination.',
      },
      {
        time: 1242,
        text: 'Remember to use your whole body. The power comes from your legs and your hips, not just your arms.',
      },
      {
        time: 1251,
        text: "Let's see that hip twist on every single block. Show me that power.",
      },
      {
        time: 1258,
        text: "Excellent! You're looking like a true martial artist.",
      },
      {
        time: 1264,
        text: "Halfway! Let's hold that Horse Stance. Sink down as low as you can. We're going to build our leg strength for 30 seconds. Hold it!",
      },
      {
        time: 1294,
        text: "Awesome! Now, while holding that low stance, let's go back to our block combos. Right Knife-Hand, Left Inside Block!",
      },
      {
        time: 1303,
        text: 'Good! Now the other way. Left Knife-Hand, Right Inside Block!',
      },
      {
        time: 1310,
        text: "This is a huge challenge for your legs, but you're doing great. Stay low!",
      },
      {
        time: 1317,
        text: "Let's see another combo. Make it sharp and powerful.",
      },
      {
        time: 1323,
        text: "You're building the foundation of a true black belt. Keep it up.",
      },
      {
        time: 1329,
        text: "Let's see one more perfect combo. Make it your best one yet.",
      },
      {
        time: 1335,
        text: 'Awesome. Prepare for the final minute.',
      },
      {
        time: 1339,
        text: "Last minute! I'm going to call out a block. You have to do it with power from your Horse Stance. Ready? RIGHT KNIFE-HAND!",
      },
      {
        time: 1348,
        text: 'Good! LEFT INSIDE BLOCK!',
      },
      {
        time: 1353,
        text: 'Excellent! LEFT KNIFE-HAND!',
      },
      {
        time: 1358,
        text: 'Thirty seconds left! Keep it going! RIGHT INSIDE BLOCK!',
      },
      {
        time: 1364,
        text: 'RIGHT KNIFE-HAND!',
      },
      {
        time: 1369,
        text: 'LEFT INSIDE BLOCK!',
      },
      {
        time: 1374,
        text: "Final fifteen seconds. Give me everything you've got.",
      },
      {
        time: 1380,
        text: 'One more perfect block!',
      },
      {
        time: 1384,
        text: 'Five, four, three, two, one... and TIME! Excellent power.',
      },
      {
        time: 1391,
        text: "That was some amazing strength! No break! For our final and most exciting drill, it's time for the 'Advanced Ninja Shadow Battle'!",
      },
      {
        time: 1400,
        text: "This is your chance to be a hero! Imagine you're a ninja, and you're surrounded by imaginary enemy ninjas. You have to use all the blocks and strikes you've learned to defend yourself. Start in your Ready Stance, eyes open!",
      },
      {
        time: 1413,
        text: "Ready? Two ninjas in front! Give me a fast 'Fist of Fury Combo'! Remember, that's a left jab, a right reverse punch, and a left hook! Go!",
      },
      {
        time: 1424,
        text: "Awesome! Another ninja is rushing you! Give me a 'Triple Kick Challenge'! That's a right front kick, a right roundhouse kick, and a right side kick, all without putting your foot down!",
      },
      {
        time: 1437,
        text: "Incredible balance! Now, a ninja to your left! Step and give them a sharp 'Viper Hand Strike'! That's a spear-hand or a ridge-hand. Be precise!",
      },
      {
        time: 1448,
        text: "Now one to your right! Step into a horse stance and another powerful elbow strike! You're a true defender.",
      },
      {
        time: 1457,
        text: "One minute in! The ninjas are getting faster! Let's see a full 'Cobra Combo'! Remember, that's jab, reverse punch, roundhouse kick, and elbow strike! Go!",
      },
      {
        time: 1469,
        text: "Excellent! Now the other side. A ninja is coming! A 'Cobra Combo' with your left leg kicking! Take them down!",
      },
      {
        time: 1479,
        text: 'Keep moving your feet. A good ninja is always light and bouncy, ready for anything.',
      },
      {
        time: 1487,
        text: "Another ninja! Your choice! Show me a powerful kick. A 'Tornado Kick'? A side kick?",
      },
      {
        time: 1495,
        text: 'Good choice! Very powerful.',
      },
      {
        time: 1500,
        text: "Let's see that 'Fist of Fury Combo' again. Make it a blur! Fast and sharp.",
      },
      {
        time: 1508,
        text: "And a big 'Triple Kick Challenge'! Front kick, roundhouse, side kick! Balance is key.",
      },
      {
        time: 1517,
        text: 'Two minutes in! The ninjas are coming from all sides now! Be ready.',
      },
      {
        time: 1524,
        text: "Ninja on your right! A quick 'Tornado Kick'! Remember to look over your shoulder and spin.",
      },
      {
        time: 1532,
        text: "Ninja on your left! A 'Fist of Fury Combo'!",
      },
      {
        time: 1538,
        text: "Ninja right in front! A big 'Cobra Combo'! Punch, punch, kick, elbow!",
      },
      {
        time: 1546,
        text: "You're doing an amazing job. You're a true karate hero.",
      },
      {
        time: 1552,
        text: 'Keep your hands up in your guard position. Protect your face!',
      },
      {
        time: 1558,
        text: "Let's see another powerful kick. A big side kick! Push them away.",
      },
      {
        time: 1565,
        text: "Halfway! It's freestyle time! For the next minute, you decide how to fight the ninjas. Use any of the moves we've learned. Go!",
      },
      {
        time: 1575,
        text: 'Show me your best punches and kicks. Be creative!',
      },
      {
        time: 1581,
        text: "Maybe a 'Viper Hand Strike' into a 'Tornado Kick'? It's your kata!",
      },
      {
        time: 1588,
        text: 'Keep moving around your space. A good ninja never stands still.',
      },
      {
        time: 1595,
        text: "Let's see a big kiai with your next move! Show me your power!",
      },
      {
        time: 1602,
        text: 'Awesome! The ninjas are scared of you now.',
      },
      {
        time: 1608,
        text: "Keep that energy high. You're doing great.",
      },
      {
        time: 1613,
        text: "Let's see another powerful combo. Make it your own.",
      },
      {
        time: 1619,
        text: "Excellent! You're a true martial artist.",
      },
      {
        time: 1625,
        text: "Last minute! Oh no, the Big Boss Ninja has appeared! He's right in front of you. We need your most powerful moves!",
      },
      {
        time: 1635,
        text: "Give me a 'Fist of Fury Combo'! That's a jab, reverse, and hook! Fast and strong!",
      },
      {
        time: 1642,
        text: "He's still coming! A 'Tornado Kick'! That's the big spinning back kick!",
      },
      {
        time: 1649,
        text: "He's still coming! Give me a full 'Cobra Combo'! That's jab, reverse punch, roundhouse kick, and elbow strike!",
      },
      {
        time: 1659,
        text: "Thirty seconds left! It's the final battle! Go, go, go!",
      },
      {
        time: 1665,
        text: "Faster, stronger! Use everything you've got!",
      },
      {
        time: 1671,
        text: 'Final fifteen seconds. Give me your best combination of the day!',
      },
      {
        time: 1677,
        text: 'One more big combo! You can do it!',
      },
      {
        time: 1682,
        text: 'Ten, nine, eight, seven, six...',
      },
      {
        time: 1688,
        text: 'Five, four, three, two, one... YOU DID IT! The Boss Ninja is defeated!',
      },
      {
        time: 1696,
        text: 'That was an incredible workout! You have learned and practiced so many amazing strikes today. You should be so proud of your hard work and focus.',
      },
      {
        time: 1707,
        text: "Let's do a final cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You have trained like a true karate champion today. Amazing job. Drill complete!",
      },
    ],
    timeTotal: 1712,
    equipment: 'None',
  },

  // ADVANCED BLOCKS (45 minutes)
  {
    id: 2039,
    name: 'Advanced Blocks',
    categories: ['Blocks', 'advanced'],
    sports: ['karate'],
    difficulty: 'advanced',
    skill: 'Blocks',
    points: 30,
    instructions:
      "Ready to become a defensive legend? We're going to learn secret, powerful blocks to build an iron fortress! You'll learn how to move like a ninja, counter-attack like a cobra, and create the ultimate defense. Let's power up!",
    timedInstructions: [
      {
        time: 0,
        text: "Hey, Karate Legend! Ready to build a defense nobody can beat? Let's start with a super-speed warm-up called 'Lightning Reflexes'! Start in a Ready Stance! Feet apart, fists ready!",
      },
      {
        time: 10,
        text: 'This is a high-speed review! On my call, snap into the block! Ready... Right High Block! Make a strong roof over your head!',
      },
      {
        time: 18,
        text: 'Good! Left Low Block! Sweep down low and powerful!',
      },
      {
        time: 24,
        text: 'Excellent! Right Inside Block! Circle your arm in like a shield!',
      },
      {
        time: 30,
        text: 'Awesome! Left Knife-Hand Block! Hand like a sword, a sharp chop!',
      },
      {
        time: 37,
        text: "Let's pick up the pace! Left High Block! ...Right Low Block! ...Left Inside Block! ...Right Knife-Hand Block! You're a blur!",
      },
      {
        time: 49,
        text: 'Now add your feet! Step forward into a Left Front Stance with a Right High Block! Go!',
      },
      {
        time: 56,
        text: 'Good! Now step back into a Right Back Stance with a Left Inside Block! Weight on that back leg!',
      },
      {
        time: 64,
        text: 'Excellent! Step out into a wide Horse Stance with a Right Low Block! Sink low!',
      },
      {
        time: 72,
        text: 'Awesome! Step into a Left Cat Stance with a Left Knife-Hand Block! Be light and sneaky!',
      },
      {
        time: 81,
        text: 'Now a pro combo! Step forward into a Left Front Stance with a High Block... then immediately shift your weight back into a Left Back Stance with an Inside Block!',
      },
      {
        time: 92,
        text: 'Awesome! Other side! Step forward Right Front Stance, High Block... then shift back to a Right Back Stance, Inside Block!',
      },
      {
        time: 103,
        text: "Let's go back to random calls, but move into a stance with each one! Ready? Step forward into a Left Front Stance with a HIGH BLOCK!",
      },
      {
        time: 111,
        text: 'Good! Step back into a Right Back Stance with an INSIDE BLOCK!',
      },
      {
        time: 118,
        text: 'Excellent! Step out into a Horse Stance with a LOW BLOCK!',
      },
      {
        time: 125,
        text: 'Awesome reactions! Step back into a Left Back Stance with a KNIFE-HAND BLOCK!',
      },
      {
        time: 133,
        text: 'Final minute! Lightning round! Move and block as fast as you can! Ready? FORWARD, HIGH BLOCK! Step forward, make that roof!',
      },
      {
        time: 142,
        text: 'BACKWARD, LOW BLOCK! Step back, sweep down low!',
      },
      {
        time: 147,
        text: 'FORWARD, INSIDE BLOCK! Step forward, circle that shield!',
      },
      {
        time: 152,
        text: 'Thirty seconds left! Keep it going! BACKWARD, KNIFE-HAND BLOCK! Step back, hand like a sword!',
      },
      {
        time: 159,
        text: 'FORWARD, LOW BLOCK!',
      },
      {
        time: 163,
        text: 'BACKWARD, MIDDLE BLOCK!',
      },
      {
        time: 167,
        text: 'Final ten seconds! Five, four, three, two, one... TIME! Excellent warm-up!',
      },
      {
        time: 177,
        text: "Amazing start! Now let's learn some new, super-strong blocks to build 'The Iron Fortress'!",
      },
      {
        time: 184,
        text: "First new move: the X-Block! This stops powerful kicks! From Ready, bring both arms up and cross them in front of you, making a big 'X' with your wrists. Make your fists tight! It's an unbreakable shield!",
      },
      {
        time: 197,
        text: 'Awesome! Now try it with the other arm on top! Ready... X-BLOCK! Good! And relax. X-BLOCK! And relax.',
      },
      {
        time: 207,
        text: "Next, a smart move: the Pressing Block! Imagine a punch is coming... use your open right hand to gently press and guide it away from your body. It's not a hard block, it's smooth like water. Try it!",
      },
      {
        time: 220,
        text: "Good! Now with your left hand. Be fluid! Let's practice! Right Pressing Block! ...Left Pressing Block! You're a master of motion!",
      },
      {
        time: 231,
        text: 'Now a tricky one: the Hooking Block! This traps an arm! Bring your right arm up and around in a hook shape, like a pirate! Bend your wrist to make a strong hook. Try it!',
      },
      {
        time: 243,
        text: "Good! Now the other side! A strong Left Hooking Block! Let's practice that! Right Hooking Block! ...and Left Hooking Block! Sharp and tricky!",
      },
      {
        time: 254,
        text: "Let's combine our new blocks! Ready... do a powerful X-Block! Cross those arms! Good. Now, a Right Pressing Block. Guide it away!",
      },
      {
        time: 264,
        text: "Excellent! Now, a Left Hooking Block. Make that hook! You're flowing between all your new shields!",
      },
      {
        time: 272,
        text: "Let's try that again! X-Block! ...Left Pressing Block! ...Right Hooking Block! Awesome!",
      },
      {
        time: 281,
        text: 'Time for a challenge! Hold the X-Block for 10 seconds! Be an iron fortress! Go! ...five, four, three, two, one... Relax!',
      },
      {
        time: 294,
        text: 'Final minute! Lightning round! React as fast as you can! Ready? X-BLOCK! Cross those arms tight!',
      },
      {
        time: 302,
        text: 'RIGHT PRESSING BLOCK! Push it away!',
      },
      {
        time: 306,
        text: 'LEFT HOOKING BLOCK! Trap that arm!',
      },
      {
        time: 310,
        text: 'Thirty seconds left! Keep it going! LEFT PRESSING BLOCK!',
      },
      {
        time: 315,
        text: 'X-BLOCK! With the other arm on top!',
      },
      {
        time: 319,
        text: 'RIGHT HOOKING BLOCK!',
      },
      {
        time: 323,
        text: 'Final ten seconds! Five, four, three, two, one... TIME! Excellent blocks!',
      },
      {
        time: 332,
        text: "Amazing defense! Now let's defend and attack at the same time in 'The Cobra's Counter' drill!",
      },
      {
        time: 340,
        text: 'A master strikes back like a cobra! An enemy punch is coming at your face! Lean your head back just a little to dodge it... then explode back with a one-two punch! A fast left jab and a right punch! Ready... Dodge... COUNTER!',
      },
      {
        time: 354,
        text: "Awesome! Let's try a different counter. Dodge the punch back... and this time, counter with a powerful right roundhouse kick! Pivot your foot and snap that kick! Go!",
      },
      {
        time: 365,
        text: "Excellent! Now, instead of dodging, we'll block! A punch is coming... use a strong rising block to protect your head! Immediately after, counter with a 'Fist of Fury' combo! A jab, a reverse punch, and a hook! Ready... Block... and COUNTER!",
      },
      {
        time: 380,
        text: "You're turning their attack into your opportunity! Let's try a block and a kick. Block the punch... and counter with a fast side kick! Lead with your heel for power! Go!",
      },
      {
        time: 391,
        text: "Now, it's your choice! A high punch is coming! Dodge or block? Show me! ...Good! Now what's your counter-attack? A punch combo? A kick? Unleash it!",
      },
      {
        time: 403,
        text: 'Excellent choice! Another high punch is coming! Defend! ...And counter! A different one this time. Be creative!',
      },
      {
        time: 414,
        text: 'Now use your Viper Hand Strikes as counters! An enemy punch comes... dodge it! ...Now, counter with a sharp spear-hand strike! Fingers straight like a spear!',
      },
      {
        time: 426,
        text: "Awesome! Now, block a punch... and counter with a powerful ridge-hand strike! That's the circular strike with the side of your hand!",
      },
      {
        time: 437,
        text: "Final minute! Freestyle! I'll call out attacks, you counter with ANY move you want! Be a creative cobra! Ready? High punch!",
      },
      {
        time: 446,
        text: 'Good counter! Now a low kick!',
      },
      {
        time: 450,
        text: 'Excellent! Another high punch!',
      },
      {
        time: 454,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! A punch from the side! How do you block and counter?',
      },
      {
        time: 462,
        text: 'Final ten seconds! A super fast punch is coming! React! Five, four, three, two, one... TIME! Excellent counters.',
      },
      {
        time: 473,
        text: "Amazing! Now let's become a true ninja in 'Ninja Evasion & Defense'!",
      },
      {
        time: 480,
        text: 'A great ninja moves their feet! An attack is coming straight on! Take a quick step to your left, and at the same time, do a strong Right Inside Block across your body! You moved and blocked as one!',
      },
      {
        time: 492,
        text: 'Awesome! Back to Ready. Now the other way! Step to your right, and do a Left Inside Block! Go!',
      },
      {
        time: 500,
        text: "Let's get a rhythm! Step left, block right! ...Step right, block left! Stay light on your feet!",
      },
      {
        time: 509,
        text: "Now let's move backwards! An attack is coming! Step back with your right foot into a Right Back Stance and do a Left Knife-Hand Block at the same time! Hand like a sword!",
      },
      {
        time: 520,
        text: 'Good! Back to Ready. Other side! Step back with your left foot into a Left Back Stance and do a Right Knife-Hand Block! Excellent!',
      },
      {
        time: 530,
        text: "Let's combine them! I call it, you do it! Ready? SIDE STEP LEFT with an Inside Block!",
      },
      {
        time: 537,
        text: 'Good! STEP BACK RIGHT with a Knife-Hand Block!',
      },
      {
        time: 543,
        text: 'Excellent! SIDE STEP RIGHT with an Inside Block!',
      },
      {
        time: 549,
        text: 'Awesome! STEP BACK LEFT with a Knife-Hand Block!',
      },
      {
        time: 555,
        text: "Final minute! The calls are rapid-fire! Don't think, just react! Ready? SIDE STEP LEFT!",
      },
      {
        time: 562,
        text: 'STEP BACK RIGHT! SIDE STEP RIGHT! STEP BACK LEFT!',
      },
      {
        time: 569,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! Faster, sharper, more evasive! Come on!',
      },
      {
        time: 578,
        text: 'Final ten seconds! One more perfect move! Five, four, three, two, one... TIME! Excellent evasion.',
      },
      {
        time: 587,
        text: "Amazing footwork! Now, let's combine our most powerful moves in 'Dragon's Flow Combinations'!",
      },
      {
        time: 595,
        text: "We're combining a spinning Tornado Kick with a Viper Hand Strike! Look over your right shoulder, spin, and kick with your heel. As you land, immediately shoot out a sharp left spear-hand strike! Go!",
      },
      {
        time: 609,
        text: "Awesome! Now the other side! A left-leg Tornado Kick... and as you land, a right-hand spear-hand strike! You're a dragon warrior.",
      },
      {
        time: 620,
        text: "New combination! After your right-leg Tornado Kick, you're going to counter with a powerful left-hand ridge-hand strike! That's the circular strike with the side of your hand. Go!",
      },
      {
        time: 632,
        text: 'Excellent! Now the other side! A left-leg Tornado Kick, and a right-hand ridge-hand! Keep that power.',
      },
      {
        time: 642,
        text: 'Focus on the transition! The moment your foot lands from the kick, the hand strike should be exploding forward. No pause! Kick and STRIKE! Awesome speed.',
      },
      {
        time: 655,
        text: 'Your choice now! Tornado Kick into a spear-hand, or a ridge-hand! Surprise me! Remember your kiai on the strike! Let me hear that power!',
      },
      {
        time: 667,
        text: "Final minute! Let's see your most powerful combos! Imagine you're in a movie! Give me a right-leg Tornado Kick into ANY hand strike you want! Be creative!",
      },
      {
        time: 678,
        text: 'Now the left leg! Just as powerful! Just as creative!',
      },
      {
        time: 684,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 693,
        text: 'Final ten seconds! One more perfect combo! Five, four, three, two, one... TIME! Excellent combos.',
      },
      {
        time: 703,
        text: "Amazing power! For our final drill, it's the 'Grandmaster's Challenge'!",
      },
      {
        time: 710,
        text: 'This is it! You have to use all your skills to win against a non-stop barrage of imaginary attackers! Start in Ready Stance. Eyes open, mind sharp!',
      },
      {
        time: 720,
        text: "Ready? Two attackers in front! Give me a fast 'Lightning Chain Combo'! That's a jab, reverse punch, and hook, followed by another combo on the other side! Go!",
      },
      {
        time: 731,
        text: "Awesome! An attacker throws a high punch! Use your 'Cobra's Counter'! Dodge or block, then strike back immediately!",
      },
      {
        time: 741,
        text: "Excellent! A ninja is rushing you from the side! Use your 'Ninja Evasion'! A quick side-step and a strong block at the same time!",
      },
      {
        time: 751,
        text: "The attackers are getting faster! Let's see a 'Dragon's Fury Combo'! A spinning 'Tornado Kick' right into a 'Viper Hand Strike'!",
      },
      {
        time: 762,
        text: "Excellent! Now the other side! A 'Dragon's Fury Combo' with your other leg! Take them down!",
      },
      {
        time: 771,
        text: 'Another attacker! Your choice! Show me a powerful counter. A block and a kick? A dodge and a punch?',
      },
      {
        time: 779,
        text: "It's freestyle time! For the next minute, you decide how to fight! Use any of the moves we've learned! Go! Show me your best blocks, counters, and strikes! Be creative!",
      },
      {
        time: 792,
        text: "Let's see a big kiai with your next move! Show me your power! Awesome! The ninjas are scared of you now.",
      },
      {
        time: 802,
        text: "Final minute! Oh no, the Grandmaster Ninja has appeared! We need your most powerful moves! Give me a 'Lightning Chain Combo'! Fast and strong!",
      },
      {
        time: 813,
        text: "He's still coming! A 'Tornado Kick'! That's the big spinning back kick!",
      },
      {
        time: 819,
        text: "He's still coming! A full 'Dragon's Fury Combo'! Spinning kick into a viper hand strike!",
      },
      {
        time: 828,
        text: "Thirty seconds left! It's the final battle! Go, go, go! Faster, stronger! Use everything you've got!",
      },
      {
        time: 837,
        text: 'Final fifteen seconds! Give me your best combination of the day! One more big combo! You can do it!',
      },
      {
        time: 845,
        text: 'Ten, nine, eight, seven, six... five, four, three, two, one... YOU DID IT! The Grandmaster is defeated!',
      },
      {
        time: 856,
        text: 'That was an incredible workout! You have mastered so many amazing skills today. Be proud of your hard work and focus.',
      },
      {
        time: 865,
        text: "Let's cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You have trained like a true karate champion today. Amazing job. Drill complete!",
      },
    ],
    timeTotal: 873,
    equipment: 'None',
  },

  // COMPLETE INDIVIDUAL KARATE (60 minutes)
  {
    id: 2040,
    name: 'Complete Individual Karate',
    categories: ['Complete', 'advanced'],
    sports: ['karate'],
    difficulty: 'advanced',
    skill: 'Complete',
    points: 30,
    instructions:
      "Ready to become a complete karate master? This is the ultimate challenge! We'll combine everything you've learned—stances, strikes, and blocks—into powerful, lightning-fast combos. Get ready to unleash your full power!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello, Karate Master! Ready for the ultimate challenge? Let's warm up with 'Lightning Chain Combos'! Start in your ready stance! Feet apart, fists up!",
      },
      {
        time: 9,
        text: "First, our 'Fist of Fury' combo! It's three punches! A fast left jab straight out... then a powerful right reverse punch with a twist... and a left hook, swinging your fist around a corner! Let's chain them together! Jab... reverse punch... hook! GO!",
      },
      {
        time: 23,
        text: 'Awesome! Now the other side! Right jab... left reverse punch... right hook! Twist those hips!',
      },
      {
        time: 31,
        text: 'Now chain them! Left combo, then right combo! Non-stop punches! Keep those hands up! Go!',
      },
      {
        time: 40,
        text: "Let's add a kick! After your left-side Fist of Fury combo, add a powerful right roundhouse kick! Pivot your foot and snap that kick! So it's: Jab, reverse, hook, KICK! Let's see it!",
      },
      {
        time: 53,
        text: 'Excellent! Now the other side! Right-side Fist of Fury combo, and finish with a powerful left roundhouse kick! Go!',
      },
      {
        time: 63,
        text: "You're a striking machine! The last punch should flow right into the kick. Punch, punch, punch, KICK! Awesome.",
      },
      {
        time: 73,
        text: "Time for the Triple Kick Challenge! After your left-side Fist of Fury... balance on that left leg and give me three right kicks without putting your foot down! A front kick straight out... a roundhouse kick to the side... and a side kick pushing with your heel! It's a huge balance test! Let's try it!",
      },
      {
        time: 92,
        text: 'You did it! Now the other side! Right-side Fist of Fury combo, followed by the left leg Triple Kick Challenge! Go for it!',
      },
      {
        time: 103,
        text: "This is a true test of your balance! Let's simplify and go for speed. Just the Fist of Fury combo into a single roundhouse kick. But make it lightning fast!",
      },
      {
        time: 115,
        text: 'Left combo and right kick! GO! Right combo and left kick! GO! Faster, faster! The punches should be a blur, the kick a thunderclap!',
      },
      {
        time: 126,
        text: 'Remember your kiai! Let me hear that power on the kick! Final minute! Freestyle combos! Mix any punches and kicks. Create your own lightning chain!',
      },
      {
        time: 138,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 148,
        text: "Final fifteen seconds! Give me everything you've got! One more perfect combo! Five, four, three, two, one... and TIME! Excellent combinations.",
      },
      {
        time: 160,
        text: "Amazing warm-up! Now, let's learn a superstar move: the spinning Tornado Kick!",
      },
      {
        time: 167,
        text: "This kick is powerful, so we'll learn it step-by-step. The secret is the look! Without kicking, just turn your head and body backwards over your right shoulder, like you're spotting a target behind you. Try it! ... Good! Now look over your left shoulder.",
      },
      {
        time: 182,
        text: 'Great! Now add the knee! As you look over your right shoulder, lift your right knee up to your chest. Keep it tucked in tight! Look and lift! Try it!',
      },
      {
        time: 192,
        text: 'Good! Other side! Look over your left shoulder and bring your left knee up!',
      },
      {
        time: 199,
        text: "Now for the TORNADO! After you look and lift your knee, unleash the kick straight behind you, hitting with your heel! Let's try it slowly with the right leg: Look... chamber... KICK! Then bring it back. Awesome!",
      },
      {
        time: 213,
        text: "Now the left leg! Look over your left shoulder, lift your left knee, and kick straight back with your heel! You're a powerhouse!",
      },
      {
        time: 223,
        text: "Let's see five slow and perfect Tornado Kicks with your right leg. Remember: Look, chamber, kick! ...Now five with your left. Use your arms to help you balance!",
      },
      {
        time: 238,
        text: "Your kicks are looking strong! Let's pick up the speed a little bit. A little sharper, a little faster. Let's see a sharp Tornado Kick with your right leg! Good! Now one with your left.",
      },
      {
        time: 251,
        text: "Let's try alternating now. A right kick, then a left kick. Find a good rhythm. Right... left... right... left. Keep those hands up!",
      },
      {
        time: 263,
        text: "Final minute! This is the power round! I want to see your most powerful kicks. Imagine you're a tornado! Ready? Right leg, go!",
      },
      {
        time: 272,
        text: "Awesome power! Now left leg! Just as strong. Don't forget your kiai! A big 'ha!' with every kick!",
      },
      {
        time: 282,
        text: "Thirty seconds left! Let's see how many power kicks you can do. Go, go, go! Faster, stronger! Come on!",
      },
      {
        time: 292,
        text: 'Final ten seconds! Five, four, three, two, one... and TIME! Excellent kicks.',
      },
      {
        time: 302,
        text: "Amazing! Now let's sharpen our hands into weapons in 'Viper Hand Strikes'!",
      },
      {
        time: 309,
        text: 'First, the spear-hand! Squeeze all your fingers together, straight and tight like a spear, and tuck your thumb. From your ready stance, do a fast, stabbing strike forward! Go!',
      },
      {
        time: 321,
        text: "Awesome! Now the left hand! Make that perfect spear-hand and strike! Let's get some reps! Four right, four left! Go!",
      },
      {
        time: 332,
        text: "Next, the ridge-hand! Swing your open hand in a circle like a hook, striking with the side of your hand near your thumb. Let's see that circular ridge-hand strike!",
      },
      {
        time: 344,
        text: "Excellent! Now the left hand! A sharp, circular ridge-hand strike. Let's get some reps! Four right, four left! Go!",
      },
      {
        time: 355,
        text: "Let's combine them! With your right hand: a straight spear-hand strike... bring it back... and a circular ridge-hand strike. It's a one-two combo! Go!",
      },
      {
        time: 367,
        text: 'Awesome! Now the left hand! A sharp spear-hand... and a powerful ridge-hand! Go!',
      },
      {
        time: 376,
        text: "Let's add our stances! Step forward into a Left Front Stance and do a right spear-hand strike! Use your whole body! ...Good! Now, from that same stance, do a left ridge-hand strike. A powerful combo.",
      },
      {
        time: 390,
        text: "Other side! Step into a Right Front Stance and do a left spear-hand! ...And from there, a right ridge-hand. You're a powerhouse.",
      },
      {
        time: 401,
        text: 'Final minute! Freestyle! Mix your spear-hands and ridge-hands. Use any stance you want. Be a creative viper!',
      },
      {
        time: 410,
        text: 'Show me a combo! Maybe a spear-hand, another spear-hand, and then a big ridge-hand!',
      },
      {
        time: 418,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 428,
        text: 'Final ten seconds! One more perfect combo! Five, four, three, two, one... TIME! Excellent strikes.',
      },
      {
        time: 439,
        text: "Amazing speed! Now let's defend and attack at the same time in 'The Cobra's Counter'!",
      },
      {
        time: 446,
        text: 'An enemy punch is coming at your face! Lean your head back just a little to dodge it... then explode back with a one-two punch! A fast left jab and a right punch! Ready... Dodge... COUNTER!',
      },
      {
        time: 459,
        text: "Awesome! Let's try a different counter. Dodge the punch back... and this time, counter with a powerful right roundhouse kick! Pivot your foot and snap that kick! Go!",
      },
      {
        time: 470,
        text: "Excellent! Now, instead of dodging, we'll block! A punch is coming... use a strong rising block to protect your head! Immediately after, counter with a 'Fist of Fury' combo! A jab, a reverse punch, and a hook! Ready... Block... and COUNTER!",
      },
      {
        time: 485,
        text: "You're turning their attack into your opportunity! Let's try a block and a kick. Block the punch... and counter with a fast side kick! Lead with your heel for power! Go!",
      },
      {
        time: 496,
        text: "Now, it's your choice! A high punch is coming! Dodge or block? Show me! ...Good! Now what's your counter-attack? A punch combo? A kick? Unleash it!",
      },
      {
        time: 508,
        text: 'Now use your Viper Hand Strikes as counters! An enemy punch comes... dodge it! ...Now, counter with a sharp spear-hand strike! Fingers straight like a spear!',
      },
      {
        time: 520,
        text: "Awesome! Now, block a punch... and counter with a powerful ridge-hand strike! That's the circular strike with the side of your hand!",
      },
      {
        time: 531,
        text: "Final minute! Freestyle! I'll call out attacks, you counter with ANY move you want! Be a creative cobra! Ready? High punch!",
      },
      {
        time: 540,
        text: 'Good counter! Now a low kick!',
      },
      {
        time: 544,
        text: 'Excellent! Another high punch!',
      },
      {
        time: 548,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! A punch from the side! How do you block and counter?',
      },
      {
        time: 556,
        text: 'Final ten seconds! A super fast punch is coming! React! Five, four, three, two, one... TIME! Excellent counters.',
      },
      {
        time: 567,
        text: "Amazing defense! Now let's unleash the dragon with 'Dragon's Fury Combos'!",
      },
      {
        time: 574,
        text: "We're combining our spinning Tornado Kick with our Viper Hand Strikes! Look over your right shoulder, spin, and kick with your heel. As you land, immediately shoot out a sharp left spear-hand strike! Go!",
      },
      {
        time: 588,
        text: "Awesome! Now the other side! A left-leg Tornado Kick... and as you land, a right-hand spear-hand strike! You're a dragon warrior.",
      },
      {
        time: 599,
        text: 'New combination! After your right-leg Tornado Kick, counter with a powerful left-hand ridge-hand strike! The circular strike with the side of your hand. Go!',
      },
      {
        time: 611,
        text: 'Excellent! Now the other side! A left-leg Tornado Kick, and a right-hand ridge-hand! Keep that power.',
      },
      {
        time: 621,
        text: 'Focus on the transition! The moment your foot lands, the hand strike explodes forward. No pause! Kick and STRIKE! Awesome speed.',
      },
      {
        time: 633,
        text: 'Your choice now! Tornado Kick into a spear-hand, or a ridge-hand! Surprise me! Remember your kiai on the strike! Let me hear that power!',
      },
      {
        time: 645,
        text: "Final minute! Let's see your most powerful combos! Imagine you're in a movie, performing the ultimate finishing move! Give me a right-leg Tornado Kick into ANY hand strike you want! Be creative!",
      },
      {
        time: 658,
        text: 'Now the left leg! Just as powerful! Just as creative!',
      },
      {
        time: 664,
        text: 'Thirty seconds left! Empty the tank! Go, go, go! Faster, stronger, sharper! Come on!',
      },
      {
        time: 673,
        text: 'Final ten seconds! One more perfect combo! Five, four, three, two, one... TIME! Excellent combos.',
      },
      {
        time: 683,
        text: "Amazing power! For our final drill, it's the 'Elite Ninja Shadow Battle'!",
      },
      {
        time: 690,
        text: "This is it! You're surrounded by imaginary enemies! Use everything you've learned! Start in Ready Stance, stay bouncy!",
      },
      {
        time: 699,
        text: "Ready? Two ninjas in front! Give me a fast 'Lightning Chain Combo'! Jab-reverse-hook, then the same on the other side! Go!",
      },
      {
        time: 709,
        text: "Awesome! Another ninja is rushing you! A 'Tornado Kick'! The big spinning back kick!",
      },
      {
        time: 717,
        text: "Incredible! A ninja to your left! Step and give them a sharp 'Viper Hand Strike'! A spear-hand or a ridge-hand!",
      },
      {
        time: 726,
        text: "The ninjas are getting faster! A full 'Dragon's Fury Combo'! A spinning Tornado Kick right into a Viper Hand Strike!",
      },
      {
        time: 736,
        text: "Excellent! Now the other side! A 'Dragon's Fury Combo' with your left leg kicking! Take them down!",
      },
      {
        time: 745,
        text: "An attack is coming! Use your 'Cobra's Counter'! A block or a dodge, and an immediate strike back!",
      },
      {
        time: 754,
        text: "It's freestyle time! For the next minute, you decide how to fight the ninjas! Use any moves we've learned! Go! Show me your best blocks, counters, and strikes! Be creative!",
      },
      {
        time: 768,
        text: "Let's see a big kiai with your next move! Show me your power! Awesome! The ninjas are scared of you now.",
      },
      {
        time: 778,
        text: "Final minute! Oh no, the Big Boss Ninja has appeared! We need your most powerful moves! Give me a 'Lightning Chain Combo'! Fast and strong!",
      },
      {
        time: 789,
        text: "He's still coming! A 'Tornado Kick'! The big spinning back kick!",
      },
      {
        time: 795,
        text: "He's still coming! A full 'Dragon's Fury Combo'! Spinning kick into a viper hand strike!",
      },
      {
        time: 804,
        text: "Thirty seconds left! It's the final battle! Go, go, go! Faster, stronger! Use everything you've got!",
      },
      {
        time: 813,
        text: 'Final fifteen seconds! Give me your best combination of the day! One more big combo! You can do it!',
      },
      {
        time: 821,
        text: 'Ten, nine, eight, seven, six... five, four, three, two, one... YOU DID IT! The Boss Ninja is defeated!',
      },
      {
        time: 832,
        text: 'That was an incredible workout! You have combined all your skills like a true master. Be super proud of your hard work and focus.',
      },
      {
        time: 841,
        text: "Let's cool down. Stand in your Natural Stance. Take a big, deep breath in... and out. You have trained like a true karate champion today. Amazing job. Drill complete!",
      },
    ],
    timeTotal: 849,
    equipment: 'None',
  },
];

export default karateDrills;
